// Extra type for extended signup (non-breaking addition)
type SignUpRequestBody = components['schemas']['SignUpBody'] & {
  recaptchaToken?: string;
  profileImage?: string;
  faceEmbedding?: number[];
};

// Overloaded useSignup for extended body (non-breaking addition)
export function useSignupExtended(): {
  mutate: (variables: { body: SignUpRequestBody }) => void,
  mutateAsync: (variables: { body: SignUpRequestBody }) => Promise<components['schemas']['SignUpResponse']>,
  data: components['schemas']['SignUpResponse'] | undefined,
  error: unknown | null,
  isPending: boolean,
  isSuccess: boolean,
  isError: boolean,
  isIdle: boolean,
  reset: () => void,
  status: 'idle' | 'pending' | 'success' | 'error'
} {
  const result = api.useMutation("post", "/auth/signup");
  return {
    ...result,
    error: result.error ? (result.error) : null
  };
}

// Add useProcessInvites (non-breaking addition)
export async function useProcessInvites(inviteId: string, action: "ACCEPT" | "REJECTED" = "ACCEPT"): Promise<{
  data: null,
  isLoading: boolean,
  error: string | null,
  refetch: () => void
}> {
  let isLoading = true;
  const baseUrl = `${import.meta.env.VITE_BASE_URL}/notifications/invite/${inviteId}`;
  const url =
    action === "REJECTED"
      ? `${baseUrl}?action=REJECTED`
      : baseUrl;

  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      authorization: `Bearer ${localStorage.getItem("firebase-auth-token")}`,
    },
  });
  isLoading = false;

  if (!res.ok) {
    throw new Error(`Failed to update settings: ${res.status}`);
  }

  return {
    data: null,
    isLoading: isLoading,
    error: "",
    refetch: () => { }
  }
}

/*
This file is Exports hooks for OpenAPI endpoints using the api client.
It is most certain that this file is not bugged.
Do not edit this file manually.
*/

import { api } from '../lib/openapi';
import { components, operations } from '../types/schema';
import { useState, useCallback, useEffect, useMemo } from 'react';
import { toast } from 'sonner';

import type { QuestionRenderView, SaveQuestion, SubmitQuizResponse, QuizSubmissionResponse, FlaggedQuestionResponse, UserQuizMetrics, QuizDetails, QuizAnalytics, QuizPerformance, QuizResults, GradingSystemStatus } from '../types/quiz.types';
import { RJSFSchema } from '@rjsf/utils';

import type {
  NewAnomalyData,
  AnomalyData,
  DeleteAnomalyBody
} from '@/types/reportanomaly.types';

import type { ProctoringSettings } from '@/types/video.types';
import type { DetectorSetting } from '@/components/proctoring-detectors';
import { InviteBody, InviteResponse, MessageResponse } from '@/types/invite.types';
import { EntityType, IReport, ReportStatus } from '@/types/flag.types';
import { PendingRegistrationNotification, ApprovedRegistrationNotification, PendingStudentRegistrationNotification, RejectedStudentRegistrationNotification } from '@/types/notification.types';
import { useQueryClient, useQuery, useMutation } from '@tanstack/react-query';
import { useAuthStore } from '@/store/auth-store';
import { VersionWithCourse } from '@/app/pages/student/CourseRegistration';
import { Registration, RegistrationStatus } from '@/app/pages/teacher/CourseRegistrationRequests';
// import { Field } from '@/app/pages/teacher/components/course-registration-modal';
import { IssueSort, IssueStatus } from '@/app/pages/student/FlagResponse';
import { ISubmitFeedbackBody } from '@/components/Item-container';
import { TranscriptResponse } from '@/types/ai.types';
import { VideoOverallAnalytics, VideoUserAnalytics, VideoUserAnalyticsResponse } from '@/app/pages/teacher/teacher-course-page';
import { WatchTimeTrackData } from '@/types/user_activity_event.types';
import { start } from 'repl';

// Add missing ObjectId type
type ObjectId = string;

// Question types - matching backend validators
export interface QuestionBody {
  question: {
    text: string;
    type: 'SELECT_ONE_IN_LOT' | 'SELECT_MANY_IN_LOT' | 'ORDER_THE_LOTS' | 'NUMERIC_ANSWER_TYPE' | 'DESCRIPTIVE';
    isParameterized: boolean;
    parameters?: Array<{
      name: string;
      possibleValues: string[];
      type: 'number' | 'string';
    }>;
    hint?: string;
    timeLimitSeconds: number;
    points?: number;
    priority: 'LOW' | 'MEDIUM' | 'HIGH';
  };
  solution: any; // Union type based on question type
}

export interface QuestionId {
  questionId: string;
}

export interface QuestionResponse {
  _id: string;
  text: string;
  type: 'SELECT_ONE_IN_LOT' | 'SELECT_MANY_IN_LOT' | 'ORDER_THE_LOTS' | 'NUMERIC_ANSWER_TYPE' | 'DESCRIPTIVE';
  isParameterized: boolean;
  parameters?: Array<{
    name: string;
    possibleValues: string[];
    type: 'number' | 'string';
  }>;
  hint?: string;
  timeLimitSeconds: number;
  points: number;
  skipCount?: number;
  // Solution fields based on type
  solutionText?: string;
  decimalPrecision?: number;
  upperLimit?: number;
  lowerLimit?: number;
  value?: number;
  expression?: string;
  ordering?: Array<{
    lotItem: {
      text: string;
      explaination: string;
    };
    order: number;
  }>;
  correctLotItems?: Array<{
    text: string;
    explaination: string;
  }>;
  incorrectLotItems?: Array<{
    text: string;
    explaination: string;
  }>;
  correctLotItem?: {
    text: string;
    explaination: string;
  };
}

export interface FlagQuestionBody {
  reason: string;
  courseId?: string;
  versionId?: string;
}

export interface FlagId {
  flagId: string;
}

export interface ResolveFlagBody {
  status: 'RESOLVED' | 'REJECTED';
}

// Question Bank types - matching backend validators
export interface CreateQuestionBankBody {
  courseId?: string;
  courseVersionId?: string;
  questions?: string[];
  tags?: string[];
  title: string;
  description?: string;
}

export interface CreateQuestionBankResponse {
  questionBankId: string;
}

export interface GetQuestionBankByIdParams {
  questionBankId: string;
}

export interface QuestionBankResponse {
  _id?: string | ObjectId;
  courseId?: string;
  courseVersionId?: string;
  questions?: string[];
  tags?: string[];
  title: string;
  description: string;
}


export interface QuestionBankAndQuestionParams {
  questionBankId: string;
  questionId: string;
}

export interface ReplaceQuestionResponse {
  newQuestionId: string;
}

// Quiz types - matching backend validators
export interface AddQuestionBankBody {
  bankId: string;
  count: number;
  difficulty?: string[];
  tags?: string[];
}

export interface RemoveQuestionBankParams {
  quizId: string;
  questionBankId: string;
}

export interface EditQuestionBankBody {
  bankId: string;
  count: number;
  difficulty?: string[];
  tags?: string[];
  points?: number;
}

export interface GetUserMatricesParams {
  quizId: string;
  userId: string;
}

export interface QuizAttemptParam {
  quizId: string;
  attemptId: string;
}

export interface UpdateQuizSubmissionParam {
  quizId: string;
  submissionId: string;
  score: number;
}

export interface RegradeSubmissionBody {
  totalScore?: number;
  totalMaxScore?: number;
  overallFeedback?: Array<{
    questionId: string | ObjectId;
    status: 'CORRECT' | 'INCORRECT' | 'PARTIAL';
    score: number;
    answerFeedback?: string;
  }>;
  gradingStatus: 'PENDING' | 'PASSED' | 'FAILED';
}

export interface AddFeedbackParams {
  quizId: string;
  submissionId: string;
  questionId: string;
}

export interface AddFeedbackBody {
  feedback: string;
}

export interface GetAllQuestionBanksResponse extends Array<{
  bankId: string;
  count: number;
  difficulty?: string[];
  tags?: string[];
  type?: string;
}> { }


// Attempt types - matching backend validators
export interface CreateAttemptParams {
  quizId: string;
}

export interface SaveAttemptParams {
  quizId: string;
  attemptId: string;
}

export interface SubmitAttemptParams {
  quizId: string;
  attemptId: string;
}

export interface QuestionAnswersBody {
  answers: SaveQuestion[];
}

// Updated response types matching backend
export interface CreateAttemptResponse {
  attemptId: string;
  questionRenderViews: QuestionRenderView[];
  userAttempts: number;
}

export interface SubmitAttemptResponse {
  totalScore?: number;
  totalMaxScore?: number;
  overallFeedback?: Array<{
    questionId: string | ObjectId;
    status: 'CORRECT' | 'INCORRECT' | 'PARTIAL';
    score: number;
    answerFeedback?: string;
  }>;
  gradingStatus: 'PENDING' | 'PASSED' | 'FAILED' | any;
  gradedAt?: Date;
  gradedBy?: string;
}

export interface UserQuizMetricsResponse {
  _id?: string | ObjectId;
  quizId: string | ObjectId;
  userId: string | ObjectId;
  latestAttemptStatus: 'ATTEMPTED' | 'SUBMITTED' | 'SKIPPED';
  latestAttemptId?: string | ObjectId;
  latestSubmissionResultId?: string | ObjectId;
  remainingAttempts: number;
  skipCount: number;
  attempts: Array<{
    attemptId: string | ObjectId;
    submissionResultId?: string | ObjectId;
  }>;
}

export interface QuizAttemptResponse {
  _id?: string | ObjectId;
  quizId: string | ObjectId;
  userId: string | ObjectId;
  questionDetails: Array<{
    questionId: string | ObjectId;
    parameterMap?: Record<string, string | number>;
  }>;
  answers?: Array<{
    questionId: string;
    questionType: 'SELECT_ONE_IN_LOT' | 'SELECT_MANY_IN_LOT' | 'ORDER_THE_LOTS' | 'NUMERIC_ANSWER_TYPE' | 'DESCRIPTIVE';
    answer: any;
  }>;
  createdAt: Date;
  updatedAt: Date;
}

interface IGradingResult {
  totalScore?: number;
  totalMaxScore?: number;
  overallFeedback?: Array<{
    questionId: string | ObjectId;
    status: 'CORRECT' | 'INCORRECT' | 'PARTIAL';
    score: number;
    answerFeedback?: string;
  }>;
  gradingStatus: 'PENDING' | 'PASSED' | 'FAILED' | any;
  gradedAt?: Date;
  gradedBy?: string;
}

export interface QuizSubmissionResponseUpdated {
  _id?: string | ObjectId;
  quizId: string | ObjectId;
  userId: string | ObjectId | { firstName: string, lastName: string, email: string };
  userInfo: string | ObjectId | { firstName: string, lastName: string, email: string };
  attemptId: string | ObjectId;
  submittedAt: Date;
  gradingResult?: IGradingResult;
}

// Anomalies hooks
export interface Anomaly {
  id: string;
  _id?: string;
  studentName: string;
  studentId: string;
  studentEmail: string;
  type: string;
  description?: string;
  courseId: string;
  versionId: string;
  itemId?: string;
  createdAt: string | Date;
  updatedAt?: string | Date;
  date: string;
  status: 'Pending' | 'Investigated' | 'Resolved';
  cohortId?: string;
  cohortId?: string;
}

export interface ExportFeedbackSubmissionsProps {
  courseId: string;
  feedbackId: string;
}


export function useAnomaliesByCourseItem(
  courseId: string | undefined,
  versionId: string | undefined,
  page: number = 1,
  limit: number = 10,
  sortField?: string,
  sortOrder: 'asc' | 'desc' = 'desc',
  search?: string,
  type?: string,
  cohort?: string
): {
  data: Anomaly[];
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
  total: number;
  page: number;
  totalPages: number;
  cohort?: string;
  isRefetching: boolean;
} {
  const result: any = api.useQuery(
    "get",
    "/anomalies/course/{courseId}/version/{versionId}",
    {
      params: {
        path: {
          courseId: courseId || "",
          versionId: versionId || ""
        },
        query: {
          page,
          limit,
          sortField,
          sortOrder,
          ...(search && { search }),
          ...(type && { type }),
          ...(cohort && { cohort })
        }
      }
    },
    {
      enabled: !!courseId && !!versionId,
      retry: 1,
      refetchOnWindowFocus: false
    }
  );


  const data = Array.isArray(result?.data?.data) ? result?.data?.data : [];
  const total = result?.data?.totalDocuments || 0;
  const totalPages = result?.data?.totalPages || 1;

  return {
    data,
    isLoading: result.isLoading,
    error: result.error ? (result.error.message || 'Failed to fetch anomalies') : null,
    refetch: result.refetch,
    total,
    page,
    totalPages,
    isRefetching: result.isRefetching,
  };
}

// Auth hooks

// POST /auth/verify
export function useLogin(): {
  data: components['schemas']['TokenVerificationResponse'] | undefined,
  isLoading: boolean,
  error: string | null,
  refetch: () => void
} {
  const result = api.useQuery("post", "/auth/verify", {});

  return {
    data: result.data,
    isLoading: result.isLoading,
    error: result.error ? (result.error.message || 'Login failed') : null,
    refetch: result.refetch
  };
}

// POST /auth/google
export function useLoginWithGoogle(): {
  mutate: (variables: { body: { lastName: string, firstName: string, email: string } }) => void,
  mutateAsync: (variables: { body: { lastName: string, firstName: string, email: string } }) => Promise<components['schemas']['SignUpResponse']>,
  data: components['schemas']['TokenVerificationResponse'] | undefined,
  error: string | null,
  isPending: boolean,
  isSuccess: boolean,
  isError: boolean,
  isIdle: boolean,
  reset: () => void,
  status: 'idle' | 'pending' | 'success' | 'error'
} {
  const result = api.useMutation("post", "/auth/google");
  return {
    ...result,
    error: result.error ? (result.error.message || 'Google login failed') : null
  };
}

// POST /auth/signup
export function useSignup(): {
  mutate: (variables: { body: components['schemas']['SignUpBody'] }) => void,
  mutateAsync: (variables: { body: components['schemas']['SignUpBody'] }) => Promise<components['schemas']['SignUpResponse']>,
  data: components['schemas']['SignUpResponse'] | undefined,
  error: unknown | null,
  isPending: boolean,
  isSuccess: boolean,
  isError: boolean,
  isIdle: boolean,
  reset: () => void,
  status: 'idle' | 'pending' | 'success' | 'error'
} {
  const result = api.useMutation("post", "/auth/signup");
  return {
    ...result,
    error: result.error ? (result.error) : null
  };
}

// POST /auth/signup/verify
export function useVerifySignUpProvider(): {
  mutate: (variables: { body: components['schemas']['VerifySignUpProviderBody'] }) => void,
  mutateAsync: (variables: { body: components['schemas']['VerifySignUpProviderBody'] }) => Promise<components['schemas']['SignUpResponse']>,
  data: components['schemas']['SignUpResponse'] | undefined,
  error: string | null,
  isPending: boolean,
  isSuccess: boolean,
  isError: boolean,
  isIdle: boolean,
  reset: () => void,
  status: 'idle' | 'pending' | 'success' | 'error'
} {
  const result = api.useMutation("post", "/auth/signup/verify");
  return {
    ...result,
    error: result.error ? (result.error.message || 'Verification failed') : null
  };
}

// PATCH /auth/change-password
export function useChangePassword(): {
  mutate: (variables: { body: components['schemas']['ChangePasswordBody'] }) => void,
  mutateAsync: (variables: { body: components['schemas']['ChangePasswordBody'] }) => Promise<components['schemas']['ChangePasswordResponse']>,
  data: components['schemas']['ChangePasswordResponse'] | undefined,
  error: string | null,
  isPending: boolean,
  isSuccess: boolean,
  isError: boolean,
  isIdle: boolean,
  reset: () => void,
  status: 'idle' | 'pending' | 'success' | 'error'
} {
  const result = api.useMutation("patch", "/auth/change-password");
  return {
    ...result,
    error: result.error ? (result.error.message || 'Password change failed') : null
  };
}

// Course hooks

// POST /courses/
export function useCreateCourse(): {
  mutate: (variables: { body: components['schemas']['CreateCourseBody'] }) => void,
  mutateAsync: (variables: { body: { name: string, description: string, versionName: string, versionDescription: string, cohorts: string[], hpSystem: boolean, baseHp: number | undefined } }) => Promise<components['schemas']['CourseDataResponse']>,
  data: components['schemas']['CourseDataResponse'] | undefined,
  error: string | null,
  isPending: boolean,
  isSuccess: boolean,
  isError: boolean,
  isIdle: boolean,
  reset: () => void,
  status: 'idle' | 'pending' | 'success' | 'error'
} {
  const result = api.useMutation("post", "/courses/");
  return {
    ...result,
    error: result.error ? (result.error.message || 'Course creation failed') : null
  };
}

export async function processInviteApi(inviteId: string, action: "ACCEPT" | "REJECTED" = "ACCEPT", policyAcknowledged: boolean = false

): Promise<{
  data: null,
  isLoading: boolean,
  error: string | null,
  refetch: () => void
}> {
  let isLoading = true;
  const baseUrl = `${import.meta.env.VITE_BASE_URL}/notifications/invite/${inviteId}`;
  const params = new URLSearchParams();
  if (action === "REJECTED") params.set("action", "REJECTED");
  if (policyAcknowledged) params.set("policyAcknowledged", "true");
  const queryString = params.toString();
  const url = queryString ? `${baseUrl}?${queryString}` : baseUrl;

  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      authorization: `Bearer ${localStorage.getItem("firebase-auth-token")}`,
    },
  });
  isLoading = false;

  if (!res.ok) {
    throw new Error(`Failed to update settings: ${res.status}`);
  }


  return {
    data: null,
    isLoading: isLoading,
    error: "",
    refetch: () => { }
  }
}

// GET /courses/{id}
export function useCourseById(id: string, enabled?: boolean): {
  data: components['schemas']['CourseDataResponse'] | undefined,
  isLoading: boolean,
  error: string | null,
  refetch: () => void
} {

  const enabledOptions = enabled !== undefined
    ? { enabled: !!id && enabled }
    : undefined;

  const result = api.useQuery("get", "/courses/{id}", {
    params: { path: { id } }
  }, enabledOptions);

  return {
    data: result.data,
    isLoading: result.isLoading,
    error: result.error ? (result.error.message || 'Failed to fetch course') : null,
    refetch: result.refetch
  };
}
export function useStudentCurrentProgressPath(
  userId?: string,
  courseId?: string,
  versionId?: string,
  enabled?: boolean,
  cohortId?: string
) {
  const result = api.useQuery(
    "get",
    "/users/progress/courses/{courseId}/versions/{versionId}/current-path",
    {
      params: {
        path: { courseId: courseId!, versionId: versionId! },
        query: { userId: userId!, cohortId },
      },
    },
    {
      enabled: Boolean(enabled && userId && courseId && versionId),
    }
  );

  return {
    data: result.data,
    isLoading: result.isLoading,
    error: result.error ? (result.error.message || 'Failed to load current progress') : null,
    refetch: result.refetch
  };
}

// GET /:userId/enrollments/courses/:courseId/versions/:versionId/progress-detail (API 2)
export function useStudentProgressDetail(
  userId?: string | null,
  courseId?: string | null,
  versionId?: string | null,
  enabled?: boolean,
  cohortId?: string | null
) {
  const result = api.useQuery(
    'get',
    `/users/${userId}/enrollments/courses/${courseId}/versions/${versionId}/progress-detail` as any,
    {
      params: {
        path: { userId: userId!, courseId: courseId!, versionId: versionId! },
        query: { cohortId },
      },
    },
    {
      enabled: Boolean(enabled && userId && courseId && versionId),
    }
  );

  return {
    data: result.data as {
      _id: string;
      userId: string;
      firstName: string;
      lastName: string;
      email: string;
      avatar?: string;
      enrollmentDate: string;
      percentCompleted: number;
      completedItemsCount: number;
      // Paired with completedItemsCount: the total that count is "out of",
      // using whichever formula produced completedItemsCount (plain item
      // count normally; feedback-forms-submitted count for Guru-Setu-override
      // courses, where contentCounts.totalItems below would include items --
      // like videos -- that formula doesn't count).
      completedItemsTotal?: number;
      assignedTimeSlots?: any[];
      contentCounts: {
        totalItems: number;
        itemCounts: {
          VIDEO?: number;
          QUIZ?: number;
          BLOG?: number;
          PROJECT?: number;
        };
      };
      totalQuizScore: number;
      totalQuizMaxScore: number;
      watchHours?: number; // total watch hours for this student in the course
    } | undefined,
    isLoading: result.isLoading,
    error: result.error ? (result.error.message || 'Failed to load student progress') : null,
    refetch: result.refetch
  };
}

// GET /:userId/enrollments/courses/:courseId/versions/:versionId/course-structure (API 3)
export function useStudentCourseStructure(
  userId?: string,
  courseId?: string,
  versionId?: string,
  enabled?: boolean,
  cohortId?: string
) {
  const result = api.useQuery(
    'get',
    `/users/${userId}/enrollments/courses/${courseId}/versions/${versionId}/course-structure` as any,
    {
      params: {
        path: { userId: userId!, courseId: courseId!, versionId: versionId! },
        query: { cohortId },
      },
    },
    {
      enabled: Boolean(enabled && userId && courseId && versionId),
    }
  );

  return {
    data: result.data as {
      _id: string;
      userId: string;
      courseStructure: any[];
      totalItems: number;
      itemCounts: any;
    } | undefined,
    isLoading: result.isLoading,
    error: result.error ? (result.error.message || 'Failed to load course structure') : null,
    refetch: result.refetch
  };
}

// PATCH /courses/{id}
export function useUpdateCourse(): {
  mutate: (variables: { params: { path: { id: string } }, body: components['schemas']['UpdateCourseBody'] }) => void,
  mutateAsync: (variables: { params: { path: { id: string } }, body: components['schemas']['UpdateCourseBody'] }) => Promise<components['schemas']['CourseDataResponse']>,
  data: components['schemas']['CourseDataResponse'] | undefined,
  error: string | null,
  isPending: boolean,
  isSuccess: boolean,
  isError: boolean,
  isIdle: boolean,
  reset: () => void,
  status: 'idle' | 'pending' | 'success' | 'error'
} {
  const result = api.useMutation("patch", "/courses/{id}");
  return {
    ...result,
    error: result.error ? (result.error.message || 'Course update failed') : null
  };
}

// DELETE /courses/{id}
export function useDeleteCourse(): {
  mutate: (variables: { params: { path: { id: string } } }) => void,
  mutateAsync: (variables: { params: { path: { id: string } } }) => Promise<void>,
  data: void | undefined,
  error: string | null,
  isPending: boolean,
  isSuccess: boolean,
  isError: boolean,
  isIdle: boolean,
  reset: () => void,
  status: 'idle' | 'pending' | 'success' | 'error'
} {
  const result = api.useMutation("delete", "/courses/{id}");

  return {
    ...result,
    error: result.error ? (result.error || 'Failed to delete course') : null
  };
}


// Course Version hooks

//generate link

export function useGenerateLink(): {
  mutate: (variables: { params: { path: { courseId: string, versionId: string } } }) => void,
  mutateAsync: (variables: { params: { path: { courseId: string, versionId: string } } }) => Promise<{ link: string }>,
  data: { link: string } | undefined,
  error: string | null,
  isPending: boolean,
  isSuccess: boolean,
  isError: boolean,
  isIdle: boolean,
  reset: () => void,
  status: 'idle' | 'pending' | 'success' | 'error'
} {
  const result = api.useMutation("post", "/notifications/invite/courses/{courseId}/versions/{versionId}/bulk");

  return {
    ...result,
    error: result.error ? (result.error.message || 'Failed to generate link') : null
  };
}

// POST /courses/{id}/versions
export function useCreateCourseVersion(): {
  mutate: (variables: { params: { path: { id: string } }, body: components['schemas']['CreateCourseVersionBody'] }) => void,
  mutateAsync: (variables: { params: { path: { id: string } }, body: components['schemas']['CreateCourseVersionBody'] }) => Promise<components['schemas']['CreateCourseVersionResponse']>,
  data: components['schemas']['CreateCourseVersionResponse'] | undefined,
  error: string | null,
  isPending: boolean,
  isSuccess: boolean,
  isError: boolean,
  isIdle: boolean,
  reset: () => void,
  status: 'idle' | 'pending' | 'success' | 'error'
} {
  const result = api.useMutation("post", "/courses/{id}/versions");
  return {
    ...result,
    error: result.error ? (result.error.message || 'Course version creation failed') : null
  };
}

// Get Audit details for a course version:



// GET /courses/versions/{id}
export function useCourseVersionById(
  id: string,
  enabled?: boolean,
  cohortId?: string,
): {
  data: components['schemas']['CourseVersionDataResponse'] | undefined,
  isLoading: boolean,
  error: string | null,
  refetch: () => void
} {

  const enabledOptions = enabled !== undefined
    ? { enabled: !!id && enabled }
    : undefined;

  const result = api.useQuery("get", "/courses/versions/{id}", {
    params: {
      path: { id },
      ...(cohortId ? { query: { cohortId } } : {}),
    }
  }, enabledOptions
  );

  return {
    data: result.data,
    isLoading: result.isLoading,
    error: result.error ? (result.error.message || 'Failed to fetch course version') : null,
    refetch: result.refetch
  };
}

// DELETE /courses/{courseId}/versions/{versionId}
export function useDeleteCourseVersion(): {
  mutate: (variables: { params: { path: { courseId: string, versionId: string } } }) => void,
  mutateAsync: (variables: { params: { path: { courseId: string, versionId: string } } }) => Promise<components['schemas']['DeleteCourseVersionParams']>,
  data: components['schemas']['DeleteCourseVersionParams'] | undefined,
  error: string | null,
  isPending: boolean,
  isSuccess: boolean,
  isError: boolean,
  isIdle: boolean,
  reset: () => void,
  status: 'idle' | 'pending' | 'success' | 'error'
} {
  const result = api.useMutation("delete", "/courses/{courseId}/versions/{versionId}");
  return {
    ...result,
    error: result.error ? (result.error.message || 'Course version deletion failed') : null
  };
}

// PATCH /courses/{courseId}/versions/{versionId}
export function useUpdateCourseVersion(): {
  mutate: (variables: { params: { path: { courseId: string, versionId: string } }, body: components['schemas']['CreateCourseVersionBody'] }) => void,
  mutateAsync: (variables: { params: { path: { courseId: string, versionId: string } }, body: components['schemas']['CreateCourseVersionBody'] }) => Promise<components['schemas']['CourseVersionDataResponse']>,
  data: components['schemas']['CourseVersionDataResponse'] | undefined,
  error: string | null,
  isPending: boolean,
  isSuccess: boolean,
  isError: boolean,
  isIdle: boolean,
  reset: () => void,
  status: 'idle' | 'pending' | 'success' | 'error'
} {
  const result = api.useMutation("patch", "/courses/{courseId}/versions/{versionId}" as any);
  return {
    ...result,
    error: result.error ? (result.error.message || 'Course version update failed') : null
  };
}

export interface CohortResponse {
  id: string
  name: string
  createdAt: Date
  updatedAt: Date
}

export interface CohortsResponse {
  cohorts: CohortResponse[]
  version: string
}


// GET /courses/{courseId}/versions/{versionId}/cohorts
export function useCourseVersionCohorts(
  courseId: string | undefined,
  versionId: string | undefined,
  page: number = 1,
  limit: number = 10,
  search: string = "",
  sortBy: 'name' | 'createdAt' | 'updatedAt' | 'baseHp' | 'safeHp',
  sortOrder: 'asc' | 'desc' = 'desc',
  // enabled: boolean = true,
  // filter: 'STUDENT' | 'OTHER',
  // statusTab: 'ACTIVE' | "INACTIVE",
  // cohort?: string
): {
  data: CohortsResponse | undefined,
  isLoading: boolean,
  error: string | null,
  refetch: () => void,
  isRefetching: boolean
} {

  const result = api.useQuery(
    "get",
    "/courses/{courseId}/versions/{versionId}/cohorts",
    {
      params: {
        path: { courseId, versionId },
        query: { page, limit, search, sortBy, sortOrder },
      },
      // enabled: enabled && !!courseId && !!courseVersionId,
    }
  );

  return {
    data: result.data,
    isLoading: result.isLoading,
    error: result.error ? (result.error.message || 'Failed to fetch course version enrollments') : null,
    refetch: result.refetch,
    isRefetching: result.isRefetching,
  };
}

export function useCreateCohort(): {
  mutate: (variables: { params: { path: { courseId: string, versionId: string } }, body: { newCohortName: string } }) => void,
  mutateAsync: (variables: { params: { path: { courseId: string, versionId: string } }, body: { newCohortName: string } }) => Promise<CohortsResponse>,
  data: CohortsResponse | undefined,
  error: string | null,
  isPending: boolean,
  isSuccess: boolean,
  isError: boolean,
  isIdle: boolean,
  reset: () => void,
  status: 'idle' | 'pending' | 'success' | 'error'
} {
  const result = api.useMutation("post", "/courses/{courseId}/versions/{versionId}/cohorts" as any);
  return {
    mutate: result.mutate,
    mutateAsync: result.mutateAsync,
    data: result.data,
    isPending: result.isPending,
    isSuccess: result.isSuccess,
    isError: result.isError,
    isIdle: result.isIdle,
    reset: result.reset,
    status: result.status,
    error: result.error
      ? result.error.message || "Create cohort failed"
      : null
  };
}

export function useUpdateCohort(): {
  mutate: (variables: { params: { path: { courseId: string, versionId: string, cohortId: string } }, body: { newCohortName?: string, isPublic?: boolean, isActive?: boolean, baseHp?: number, safeHp?: number } }) => void,
  mutateAsync: (variables: { params: { path: { courseId: string, versionId: string, cohortId: string } }, body: { newCohortName?: string, isPublic?: boolean, isActive?: boolean, baseHp?: number, safeHp?: number } }) => Promise<CohortsResponse>,
  data: CohortsResponse | undefined,
  error: string | null,
  isPending: boolean,
  isSuccess: boolean,
  isError: boolean,
  isIdle: boolean,
  reset: () => void,
  status: 'idle' | 'pending' | 'success' | 'error'
} {
  const result = api.useMutation("patch", "/courses/{courseId}/versions/{versionId}/cohorts/{cohortId}" as any);
  return {
    mutate: result.mutate,
    mutateAsync: result.mutateAsync,
    data: result.data,
    isPending: result.isPending,
    isSuccess: result.isSuccess,
    isError: result.isError,
    isIdle: result.isIdle,
    reset: result.reset,
    status: result.status,
    error: result.error
      ? result.error.message || "Update cohort failed"
      : null
  };
}


export interface DeleteCohortResponse {
  message: string
  requiresConfirmation?: boolean
  pendingInviteCount?: number
}

export function useDeleteCohort(): {
  mutate: (variables: { params: { path: { courseId: string, versionId: string, cohortId: string }, query?: { confirmCancelInvites?: boolean } } }) => void,
  mutateAsync: (variables: { params: { path: { courseId: string, versionId: string, cohortId: string }, query?: { confirmCancelInvites?: boolean } } }) => Promise<DeleteCohortResponse>,
  data: DeleteCohortResponse | undefined,
  error: string | null,
  isPending: boolean,
  isSuccess: boolean,
  isError: boolean,
  isIdle: boolean,
  reset: () => void,
  status: 'idle' | 'pending' | 'success' | 'error'
} {
  const result = api.useMutation("delete", "/courses/{courseId}/versions/{versionId}/cohorts/{cohortId}" as any);
  return {
    mutate: result.mutate,
    mutateAsync: result.mutateAsync,
    data: result.data,
    isPending: result.isPending,
    isSuccess: result.isSuccess,
    isError: result.isError,
    isIdle: result.isIdle,
    reset: result.reset,
    status: result.status,
    error: result.error
      ? result.error.message || "Delete cohort failed"
      : null
  };
}

export function useMoveToCohort(): {
  mutate: (variables: {
    params: { path: { courseId: string; versionId: string } };
    body: { enrollmentIds: string[]; targetCohortId: string };
  }) => void;
  mutateAsync: (variables: {
    params: { path: { courseId: string; versionId: string } };
    body: { enrollmentIds: string[]; targetCohortId: string };
  }) => Promise<any>;
  data: any;
  error: string | null;
  isPending: boolean;
  isSuccess: boolean;
  isError: boolean;
  isIdle: boolean;
  reset: () => void;
  status: "idle" | "pending" | "success" | "error";
} {
  const result = api.useMutation(
    "post",
    "/courses/{courseId}/versions/{versionId}/move-to-cohort" as any
  );

  return {
    mutate: result.mutate,
    mutateAsync: result.mutateAsync,
    data: result.data,
    isPending: result.isPending,
    isSuccess: result.isSuccess,
    isError: result.isError,
    isIdle: result.isIdle,
    reset: result.reset,
    status: result.status,
    error: result.error
      ? result.error.message || "Move to cohort failed"
      : null,
  };
}

export function useCourseVersionArchive(): {
  mutate: (variables: { params: { path: { versionId: string } }, body: components['schemas']['UpdateCourseVersionStatusBody'] }) => void,
  mutateAsync: (variables: { params: { path: { courseId: string, versionId: string } }, body: components['schemas']['UpdateCourseVersionStatusBody'] }) => Promise<components['schemas']['CourseVersionDataResponse']>,
  data: components['schemas']['CourseVersionDataResponse'] | undefined,
  error: string | null,
  isPending: boolean,
  isSuccess: boolean,
  isError: boolean,
  isIdle: boolean,
  reset: () => void,
  status: 'idle' | 'pending' | 'success' | 'error'
} {
  const result = api.useMutation("patch", "/courses/versions/{versionId}/archive" as any);
  return {
    ...result,
    error: result.error
      ? result.error.message || "Course version status update failed"
      : null,
  };
}

// Module hooks

// POST /courses/versions/{versionId}/modules
export function useCreateModule(): {
  mutate: (variables: { params: { path: { versionId: string } }, body: components['schemas']['CreateModuleBody'] }) => void,
  mutateAsync: (variables: { params: { path: { versionId: string } }, body: components['schemas']['CreateModuleBody'] }) => Promise<components['schemas']['ModuleDataResponse']>,
  data: components['schemas']['ModuleDataResponse'] | undefined,
  error: string | null,
  isPending: boolean,
  isSuccess: boolean,
  isError: boolean,
  isIdle: boolean,
  reset: () => void,
  status: 'idle' | 'pending' | 'success' | 'error'
} {
  const result = api.useMutation("post", "/courses/versions/{versionId}/modules");
  return {
    ...result,
    error: result.error ? (result.error.message || 'Module creation failed') : null
  };
}

// PUT /courses/versions/{versionId}/modules/{moduleId}
export function useUpdateModule(): {
  mutate: (variables: { params: { path: { versionId: string, moduleId: string } }, body: components['schemas']['UpdateModuleBody'] }) => void,
  mutateAsync: (variables: { params: { path: { versionId: string, moduleId: string } }, body: components['schemas']['UpdateModuleBody'] }) => Promise<components['schemas']['ModuleDataResponse']>,
  data: components['schemas']['ModuleDataResponse'] | undefined,
  error: string | null,
  isPending: boolean,
  isSuccess: boolean,
  isError: boolean,
  isIdle: boolean,
  reset: () => void,
  status: 'idle' | 'pending' | 'success' | 'error'
} {
  const result = api.useMutation("put", "/courses/versions/{versionId}/modules/{moduleId}");
  return {
    ...result,
    error: result.error ? (result.error.message || 'Module update failed') : null
  };
}

// DELETE /courses/versions/{versionId}/modules/{moduleId}
export function useDeleteModule(): {
  mutate: (variables: { params: { path: { versionId: string, moduleId: string } } }) => void,
  mutateAsync: (variables: { params: { path: { versionId: string, moduleId: string } } }) => Promise<components['schemas']['ModuleDeletedResponse']>,
  data: components['schemas']['ModuleDeletedResponse'] | undefined,
  error: string | null,
  isPending: boolean,
  isSuccess: boolean,
  isError: boolean,
  isIdle: boolean,
  reset: () => void,
  status: 'idle' | 'pending' | 'success' | 'error'
} {
  const result = api.useMutation("delete", "/courses/versions/{versionId}/modules/{moduleId}");
  return {
    ...result,
    error: result.error ? (result.error.message || 'Module deletion failed') : null
  };
}

// PUT /courses/versions/{versionId}/modules/{moduleId}/move
export function useMoveModule(): {
  mutate: (variables: { params: { path: { versionId: string, moduleId: string } }, body: components['schemas']['MoveModuleBody'] }) => void,
  mutateAsync: (variables: { params: { path: { versionId: string, moduleId: string } }, body: components['schemas']['MoveModuleBody'] }) => Promise<components['schemas']['ModuleDataResponse']>,
  data: components['schemas']['ModuleDataResponse'] | undefined,
  error: string | null,
  isPending: boolean,
  isSuccess: boolean,
  isError: boolean,
  isIdle: boolean,
  reset: () => void,
  status: 'idle' | 'pending' | 'success' | 'error'
} {
  const result = api.useMutation("put", "/courses/versions/{versionId}/modules/{moduleId}/move");
  console.log(result,"API RESPONSE");
  console.log(result.error, result?.error?.message)
  return {
    ...result,
    error: result.error ? (result.error.message || 'Module move failed') : null
  };
}

// Section hooks

// POST /courses/versions/{versionId}/modules/{moduleId}/sections
export function useCreateSection(): {
  mutate: (variables: { params: { path: { versionId: string, moduleId: string } }, body: components['schemas']['CreateSectionBody'] }) => void,
  mutateAsync: (variables: { params: { path: { versionId: string, moduleId: string } }, body: components['schemas']['CreateSectionBody'] }) => Promise<components['schemas']['SectionDataResponse']>,
  data: components['schemas']['SectionDataResponse'] | undefined,
  error: string | null,
  isPending: boolean,
  isSuccess: boolean,
  isError: boolean,
  isIdle: boolean,
  reset: () => void,
  status: 'idle' | 'pending' | 'success' | 'error'
} {
  const result = api.useMutation("post", "/courses/versions/{versionId}/modules/{moduleId}/sections");
  return {
    ...result,
    error: result.error ? (result.error.message || 'Section creation failed') : null
  };
}

// PUT /courses/versions/{versionId}/modules/{moduleId}/sections/{sectionId}
export function useUpdateSection(): {
  mutate: (variables: { params: { path: { versionId: string, moduleId: string, sectionId: string } }, body: components['schemas']['UpdateSectionBody'] }) => void,
  mutateAsync: (variables: { params: { path: { versionId: string, moduleId: string, sectionId: string } }, body: components['schemas']['UpdateSectionBody'] }) => Promise<components['schemas']['SectionDataResponse']>,
  data: components['schemas']['SectionDataResponse'] | undefined,
  error: string | null,
  isPending: boolean,
  isSuccess: boolean,
  isError: boolean,
  isIdle: boolean,
  reset: () => void,
  status: 'idle' | 'pending' | 'success' | 'error'
} {
  const result = api.useMutation("put", "/courses/versions/{versionId}/modules/{moduleId}/sections/{sectionId}");
  return {
    ...result,
    error: result.error ? (result.error.message || 'Section update failed') : null
  };
}

// DELETE /courses/versions/{versionId}/modules/{moduleId}/sections/{sectionId}
export function useDeleteSection(): {
  mutate: (variables: { params: { path: { versionId: string, moduleId: string, sectionId: string } } }) => void,
  mutateAsync: (variables: { params: { path: { versionId: string, moduleId: string, sectionId: string } } }) => Promise<components['schemas']['SectionDeletedResponse']>,
  data: components['schemas']['SectionDeletedResponse'] | undefined,
  error: string | null,
  isPending: boolean,
  isSuccess: boolean,
  isError: boolean,
  isIdle: boolean,
  reset: () => void,
  status: 'idle' | 'pending' | 'success' | 'error'
} {
  const result = api.useMutation("delete", "/courses/versions/{versionId}/modules/{moduleId}/sections/{sectionId}");
  return {
    ...result,
    error: result.error ? (result.error.message || 'Section deletion failed') : null
  };
}

// PUT /courses/versions/{versionId}/modules/{moduleId}/sections/{sectionId}/move
export function useMoveSection(): {
  mutate: (variables: { params: { path: { versionId: string, moduleId: string, sectionId: string } }, body: components['schemas']['MoveSectionBody'] }) => void,
  mutateAsync: (variables: { params: { path: { versionId: string, moduleId: string, sectionId: string } }, body: components['schemas']['MoveSectionBody'] }) => Promise<components['schemas']['SectionDataResponse']>,
  data: components['schemas']['SectionDataResponse'] | undefined,
  error: string | null,
  isPending: boolean,
  isSuccess: boolean,
  isError: boolean,
  isIdle: boolean,
  reset: () => void,
  status: 'idle' | 'pending' | 'success' | 'error'
} {
  const result = api.useMutation("put", "/courses/versions/{versionId}/modules/{moduleId}/sections/{sectionId}/move");
  return {
    ...result,
    error: result.error ? (result.error.message || 'Section move failed') : null
  };
}

// Item hooks


export function useCourseVersionAuditDetails(
  courseId: string,
  versionId: string,
  page: number,
  limit: number,
  startDate?: string,
  endDate?: string
): {
  data: any;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
} {
  const result = api.useQuery(
    "get",
    "/audit-trails/course/{courseId}/version/{versionId}" as any,
    {
      params: {
        path: { courseId, versionId },
        query: { page, limit, startDate, endDate },
      },
    },
    {
      enabled: !!courseId && !!versionId,
      keepPreviousData: true, // 🔥 Important for pagination UX
    }
  );

  return {
    data: result.data,
    isLoading: result.isLoading,
    error: result.error
      ? result.error.message || "Failed to fetch audit details"
      : null,
    refetch: result.refetch,
  };
}


// GET /courses/versions/{versionId}/modules/{moduleId}/sections/{sectionId}/items
export function useItemsBySectionId(versionId: string, moduleId: string, sectionId: string, cohortId?: string): {
  data: components['schemas']['ItemDataResponse'] | undefined,
  isLoading: boolean,
  error: string | null,
  errorName: string | null,
  refetch: () => void
} {
  const isEnabled: boolean | null =
    versionId !== "SKIP" &&
      moduleId !== "SKIP" &&
      sectionId !== "SKIP" &&
      !!versionId &&
      !!moduleId &&
      !!sectionId
      ? true
      : null;

  const result = api.useQuery("get", "/courses/versions/{versionId}/modules/{moduleId}/sections/{sectionId}/items", {
    params: {
      path: { versionId, moduleId, sectionId },
      ...(cohortId ? { query: { cohortId } } : {}),
    }
  }, { enabled: isEnabled ?? false });

  return {
    data: result.data,
    isLoading: result.isLoading,
    error: result.error ? (result.error.message || 'Failed to fetch items') : null,
    errorName: result.error ? ((result.error as any).name || null) : null,
    refetch: result.refetch
  };
}

// POST /courses/versions/{versionId}/modules/{moduleId}/sections/{sectionId}/items
export function useCreateItem(): {
  mutate: (variables: { params: { path: { versionId: string, moduleId: string, sectionId: string } }, body: any }) => void,
  mutateAsync: (variables: { params: { path: { versionId: string, moduleId: string, sectionId: string } }, body: any }) => Promise<components['schemas']['ItemDataResponse']>,
  data: components['schemas']['ItemDataResponse'] | undefined,
  error: string | null,
  isPending: boolean,
  isSuccess: boolean,
  isError: boolean,
  isIdle: boolean,
  reset: () => void,
  status: 'idle' | 'pending' | 'success' | 'error'
} {
  const result = api.useMutation("post", "/courses/versions/{versionId}/modules/{moduleId}/sections/{sectionId}/items");
  return {
    ...result,
    error: result.error ? (result.error.message || 'Item creation failed') : null
  };
}

export function userParseCSVtoItems(): {
  mutate: (variables: { params: { path: { courseId: string, versionId: string, moduleId: string, sectionId: string } }, body: any }) => void,
  mutateAsync: (variables: { params: { path: { courseId: string, versionId: string, moduleId: string, sectionId: string } }, body: any }) => Promise<{
    success: boolean;
    message: string;
    createdItems: any[];
  }>,
  data: {
    success: boolean;
    message: string;
    createdItems: any[];
  } | undefined,
  error: string | null,
  isPending: boolean,
  isSuccess: boolean,
  isError: boolean,
  isIdle: boolean,
  reset: () => void,
  status: 'idle' | 'pending' | 'success' | 'error'
} {
  const result = api.useMutation("post", "/courses/{courseId}/versions/{versionId}/module/{moduleId}/section/{sectionId}/items/csv");
  return {
    ...result,
    error: result.error ? (result.error.message || 'Item creation failed') : null
  };
}

// GET /courses/versions/{versionId}/modules/{moduleId}/sections/{sectionId}/items/{itemId}
export function useItemById(
  courseId: string,
  versionId: string,
  itemId: string,
  moduleId: string,
  sectionId: string,
  cohortId?: string
): {
  data: components['schemas']['ItemDataResponse'] | undefined;
  isLoading: boolean;
  error: string | null;
  errorName: string | null;
  refetch: () => void;
} {
  const result = api.useQuery(
    "get",
    "/courses/{courseId}/versions/{versionId}/modules/{moduleId}/sections/{sectionId}/item/{itemId}",
    {
      params: {
        path: { courseId, versionId, itemId, moduleId, sectionId },
        ...(cohortId ? { query: { cohortId } } : {}),
      },
    },
    {
      enabled: !!courseId && !!versionId && !!itemId,
    }
  );

  return {
    data: result.data,
    isLoading: result.isLoading,
    error: result.error ? (result.error.message || 'Item fetch failed') : null,
    errorName: result.error ? (result.error.name || null) : null,
    refetch: result.refetch,
  };
}



export function useOverallVideoAnalytics(
  courseId: string,
  versionId: string,
  itemId: string
): {
  data: VideoOverallAnalytics | null;
  isLoading: boolean;
  error: string | null;
  errorName: string | null;
  refetch: () => void;
} {
  const result = api.useQuery(
    "get",
    "/courses/{courseId}/versions/{versionId}/item/{itemId}/analytics",
    {
      params: { path: { courseId, versionId, itemId } },
    },
    {
      enabled: !!courseId && !!versionId && !!itemId,
    }
  );

  return {
    data: result.data,
    isLoading: result.isLoading,
    error: result.error ? result.error.message || "Video analytics fetch failed" : null,
    errorName: result.error ? result.error.name || null : null,
    refetch: result.refetch,
  };
}

export function useVideoUserAnalytics(
  courseId: string,
  versionId: string,
  itemId: string,
  query?: {
    search?: string;
    page?: number;
    limit?: number;
    sortBy?: 'name' | 'views' | 'watchHours';
    sortOrder?: 'asc' | 'desc';
  }
): {
  data: VideoUserAnalyticsResponse | null;
  isLoading: boolean;
  error: string | null;
  errorName: string | null;
  refetch: () => void;
} {
  const page = query?.page ?? 1;
  const limit = query?.limit ?? 20;
  const search = query?.search;
  const sortBy = query?.sortBy;
  const sortOrder = query?.sortOrder;

  const result = api.useQuery(
    "get",
    "/courses/{courseId}/versions/{versionId}/item/{itemId}/analytics/users",
    {
      params: {
        path: { courseId, versionId, itemId },
        query: {
          ...(search ? { search } : {}),
          ...(sortBy ? { sortBy } : {}),
          ...(sortOrder ? { sortOrder } : {}),
          page,
          limit,
        },
      },
    },
    {
      enabled: !!courseId && !!versionId && !!itemId,
      keepPreviousData: true,
    }
  );

  return {
    data: result.data ?? null,
    isLoading: result.isLoading,
    error: result.error ? result.error.message || "Video user analytics fetch failed" : null,
    errorName: result.error ? result.error.name || null : null,
    refetch: result.refetch,
  };
}


export function useUpdateCourseItem(): {
  mutate: (variables: { params: { path: { courseId: string, versionId: string, itemId: string } }, body: components['schemas']['UpdateItemBody'] }) => void,
  mutateAsync: (variables: { params: { path: { courseId: string, versionId: string, itemId: string } }, body: components['schemas']['UpdateItemBody'] }) => Promise<components['schemas']['ItemDataResponse']>,
  data: components['schemas']['ItemDataResponse'] | undefined,
  error: string | null,
  isPending: boolean,
  isSuccess: boolean,
  isError: boolean,
  isIdle: boolean,
  reset: () => void,
  status: 'idle' | 'pending' | 'success' | 'error'
} {
  const result = api.useMutation("put", "/courses/{courseId}/versions/{versionId}/items/{itemId}");
  return {
    ...result,
    error: result.error ? (result.error.message || 'Item update failed') : null
  };
}

// PUT /courses/versions/{versionId}/modules/{moduleId}/sections/{sectionId}/items/{itemId}
export function useUpdateItem(): {
  mutate: (variables: { params: { path: { versionId: string, moduleId: string, sectionId: string, itemId: string } }, body: components['schemas']['UpdateItemBody'] }) => void,
  mutateAsync: (variables: { params: { path: { versionId: string, moduleId: string, sectionId: string, itemId: string } }, body: components['schemas']['UpdateItemBody'] }) => Promise<components['schemas']['ItemDataResponse']>,
  data: components['schemas']['ItemDataResponse'] | undefined,
  error: string | null,
  isPending: boolean,
  isSuccess: boolean,
  isError: boolean,
  isIdle: boolean,
  reset: () => void,
  status: 'idle' | 'pending' | 'success' | 'error'
} {
  const result = api.useMutation("put", "/courses/versions/{versionId}/modules/{moduleId}/sections/{sectionId}/items/{itemId}");
  return {
    ...result,
    error: result.error ? (result.error.message || 'Item update failed') : null
  };
}

// DELETE /courses/itemGroups/{itemsGroupId}/items/{itemId}
export function useDeleteItem(): {
  mutate: (variables: { params: { path: { courseId: string, itemsGroupId: string, itemId: string } } }) => void,
  mutateAsync: (variables: { params: { path: { courseId: string, itemsGroupId: string, itemId: string } } }) => Promise<components['schemas']['DeletedItemResponse']>,
  data: components['schemas']['DeletedItemResponse'] | undefined,
  error: string | null,
  isPending: boolean,
  isSuccess: boolean,
  isError: boolean,
  isIdle: boolean,
  reset: () => void,
  status: 'idle' | 'pending' | 'success' | 'error'
} {
  const result = api.useMutation("delete", "/courses/{courseId}/itemGroups/{itemsGroupId}/items/{itemId}");
  return {
    ...result,
    error: result.error ? (result.error.message || 'Item deletion failed') : null
  };
}

// PUT /courses/versions/{versionId}/modules/{moduleId}/sections/{sectionId}/items/{itemId}/move
export function useMoveItem(): {
  mutate: (variables: { params: { path: { versionId: string, moduleId: string, sectionId: string, itemId: string } }, body: components['schemas']['MoveItemBody'] }) => void,
  mutateAsync: (variables: { params: { path: { versionId: string, moduleId: string, sectionId: string, itemId: string } }, body: components['schemas']['MoveItemBody'] }) => Promise<components['schemas']['ItemDataResponse']>,
  data: components['schemas']['ItemDataResponse'] | undefined,
  error: string | null,
  isPending: boolean,
  isSuccess: boolean,
  isError: boolean,
  isIdle: boolean,
  reset: () => void,
  status: 'idle' | 'pending' | 'success' | 'error'
} {
  const result = api.useMutation("put", "/courses/versions/{versionId}/modules/{moduleId}/sections/{sectionId}/items/{itemId}/move");
  return {
    ...result,
    error: result.error ? (result.error.message || 'Item move failed') : null
  };
}

// Enrollment hooks

// POST /users/enrollments/courses/{courseId}/versions/{courseVersionId}
export function useEnrollUser(): {
  mutate: (variables: { params: { path: { courseId: string, courseVersionId: string } } }) => void,
  mutateAsync: (variables: { params: { path: { courseId: string, courseVersionId: string } } }) => Promise<components['schemas']['EnrollUserResponseData']>,
  data: components['schemas']['EnrollUserResponseData'] | undefined,
  error: string | null,
  isPending: boolean,
  isSuccess: boolean,
  isError: boolean,
  isIdle: boolean,
  reset: () => void,
  status: 'idle' | 'pending' | 'success' | 'error'
} {
  const result = api.useMutation("post", "/users/enrollments/courses/{courseId}/versions/{courseVersionId}");
  return {
    ...result,
    error: result.error ? (result.error.message || 'User enrollment failed') : null
  };
}

// POST /users/{userId}/enrollments/courses/{courseId}/versions/{courseVersionId}/unenroll
export function useUnenrollUser(): {
  mutate: (variables: { params: { path: { userId: string, courseId: string, courseVersionId: string } } }, body: { cohortId?: string }) => void,
  mutateAsync: (variables: { params: { path: { userId: string, courseId: string, courseVersionId: string } } }, body: { cohortId?: string }) => Promise<components['schemas']['EnrollUserResponseData']>,
  data: components['schemas']['EnrollUserResponseData'] | undefined,
  error: string | null,
  isPending: boolean,
  isSuccess: boolean,
  isError: boolean,
  isIdle: boolean,
  reset: () => void,
  status: 'idle' | 'pending' | 'success' | 'error'
} {
  const result = api.useMutation("post", "/users/{userId}/enrollments/courses/{courseId}/versions/{courseVersionId}/unenroll");
  return {
    ...result,
    error: result.error ? (result.error.message || 'User unenrollment failed') : null
  };
}

// GET /users/enrollments
export function useUserEnrollments(page?: number, limit?: number, enabled: boolean = true, search?: string, role = "STUDENT", tab: 'active' | 'archived' = "active"): {
  data: components['schemas']['EnrollmentResponse'] | undefined,
  isLoading: boolean,
  error: string | null,
  refetch: () => void,
  isRefetching: boolean
} {
  const result = api.useQuery("get", "/users/enrollments", {
    params: {
      query: { page, limit, search, role, tab }
    },
    enabled: enabled
  });

  return {
    data: result.data,
    isLoading: result.isLoading,
    error: result.error ? (result.error.message || 'Failed to fetch user enrollments') : null,
    refetch: result.refetch,
    isRefetching: result.isRefetching,
  };
}

// GET /users/enrollments/courses/{courseId}/versions/{versionId}/statistics
export interface CourseEnrollmentStats {
  totalEnrollments: number;
  completedCount: number;
  averageProgressPercent: number;
  averageWatchHoursPerUser?: number;
  // Null until the statistics job has computed watch hours for this course
  // version; the dashboard shows it as not measured rather than as zero.
  watchHoursComputedAt?: string | null;
}

export function useCourseEnrollmentsStats(
  courseId: string | undefined,
  versionId: string | undefined,
  enabled: boolean = true
): {
  data: CourseEnrollmentStats | undefined,
  isLoading: boolean,
  error: string | null,
  refetch: () => void
} {
  const result = api.useQuery(
    "get",
    "/users/enrollments/courses/{courseId}/versions/{versionId}/statistics",
    {
      params: {
        path: { courseId, versionId }
      }
    },
    {
      enabled: enabled && !!courseId && !!versionId,
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
    }
  );

  return {
    data: result.data,
    isLoading: result.isLoading,
    error: result.error ? (result.error.message || 'Failed to fetch course anomalies stats') : null,
    refetch: result.refetch
  };
}

// GET /users/enrollments/courses/{courseId}/versions/{versionId}/export/quiz-scores
export function useCourseQuizScores(
  courseId: string | undefined,
  versionId: string | undefined,
  enabled: boolean = true,
  statusTab: 'ACTIVE' | 'INACTIVE' = 'ACTIVE',
  cohortId?: string | null
): {
  data: any | undefined,
  isLoading: boolean,
  error: string | null,
  refetch: () => void
} {
  const result = api.useQuery(
    'get',
    '/users/enrollments/courses/{courseId}/versions/{versionId}/export/quiz-scores',
    {
      params: {
        path: { courseId, versionId },
        query: { statusTab, ...(cohortId && { cohortId }) }
      }
    },
    {
      enabled: enabled ? !!courseId && !!versionId : false,
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
    }
  );

  return {
    data: result.data,
    isLoading: result.isLoading,
    error: result.error?.message || null,
    refetch: result.refetch,
  };
}

// GET /enrollments/courses/{courseId}/versions/{courseVersionId}
export function useCourseVersionEnrollments(
  courseId: string | undefined,
  courseVersionId: string | undefined,
  page: number = 1,
  limit: number = 10,
  search: string = "",
  sortBy: 'name' | 'enrollmentDate' | 'progress' | 'unenrolledAt' = 'enrollmentDate',
  sortOrder: 'asc' | 'desc' = 'desc',
  enabled: boolean = true,
  filter: 'STUDENT' | 'OTHER',
  statusTab: 'ACTIVE' | "INACTIVE",
  cohort?: string
): {
  data: components['schemas']['CourseVersionEnrollmentResponse'] | undefined,
  isLoading: boolean,
  error: string | null,
  refetch: () => void,
  isRefetching: boolean
} {
  const result = api.useQuery(
    "get",
    "/users/enrollments/courses/{courseId}/versions/{courseVersionId}",
    {
      params: {
        path: { courseId, courseVersionId },
        query: { page, limit, search, sortBy, sortOrder, filter, statusTab, cohort },
      },
      enabled: enabled && !!courseId && !!courseVersionId,
    }
  );

  return {
    data: result.data,
    isLoading: result.isLoading,
    error: result.error ? (result.error.message || 'Failed to fetch course version enrollments') : null,
    refetch: result.refetch,
    isRefetching: result.isRefetching,
  };
}


// Progress hooks

// GET /users/progress/courses/{courseId}/versions/{courseVersionId}/
export function useUserProgress(courseId: string, courseVersionId: string, cohortId?: string): {
  data: components['schemas']['ProgressDataResponse'] | undefined,
  isLoading: boolean,
  error: string | null,
  refetch: () => void
} {
  const result = api.useQuery("get", "/users/progress/courses/{courseId}/versions/{courseVersionId}", {
    params: { path: { courseId, courseVersionId }, query: { cohortId } }
  }, { enabled: !!courseId && !!courseVersionId }
  );

  return {
    data: result.data,
    isLoading: result.isLoading,
    error: result.error ? (result.error.message || 'Failed to fetch user progress') : null,
    refetch: result.refetch
  };
}

// GET /users/progress/courses/{courseId}/versions/{courseVersionId}/percentage
export function useUserProgressPercentage(courseId: string, courseVersionId: string): {
  data: {
    completed: boolean;
    percentCompleted: number;
    totalItems: number;
    completedItems: number;
  } | undefined,
  isLoading: boolean,
  error: string | null,
  refetch: () => void
} {
  const result = api.useQuery("get", "/users/progress/courses/{courseId}/versions/{courseVersionId}/percentage", {
    params: { path: { courseId, courseVersionId } }
  }, { enabled: !!courseId && !!courseVersionId }
  );


  return {
    data: result.data,
    isLoading: result.isLoading,
    error: result.error ? (result.error.message || 'Failed to fetch user progress percentage') : null,
    refetch: result.refetch
  };
}

// Add this hook to your hooks file
export function useUserProgressPercentageByUserId(
  userId: string,
  courseId: string,
  courseVersionId: string
): {
  data: {
    completed: boolean;
    percentCompleted: number;
    totalItems: number;
    completedItems: number;
  } | undefined,
  isLoading: boolean,
  error: string | null,
  refetch: () => void
} {
  const result = api.useQuery(
    "get",
    "get",
    "/users/{userId}/progress/courses/{courseId}/versions/{courseVersionId}/percentage",
    {
      params: {
        path: {
          userId,
          courseId,
          courseVersionId
        }
      }
    },
    {
      enabled: !!(userId && courseId && courseVersionId)
    }
  );

  return {
    data: result.data,
    isLoading: result.isLoading,
    error: result.error ? (result.error.message || 'Failed to fetch user progress percentage') : null,
    refetch: result.refetch
  };
}

// POST /users/progress/courses/{courseId}/versions/{courseVersionId}/start
export function useStartItem(): {
  mutate: (variables: { params: { path: { courseId: string, courseVersionId: string } }, body: components['schemas']['StartItemBody'] }) => void,
  mutateAsync: (variables: { params: { path: { courseId: string, courseVersionId: string } }, body: components['schemas']['StartItemBody'] }) => Promise<components['schemas']['StartItemResponse']>,
  data: components['schemas']['StartItemResponse'] | undefined,
  error: string | null,
  isPending: boolean,
  isSuccess: boolean,
  isError: boolean,
  isIdle: boolean,
  reset: () => void,
  status: 'idle' | 'pending' | 'success' | 'error'
} {
  const result = api.useMutation("post", "/users/progress/courses/{courseId}/versions/{courseVersionId}/start");
  return {
    ...result,
    error: result.error ? (result.error.message || 'Failed to start item') : null
  };
}

// POST /users/progress/courses/{courseId}/versions/{courseVersionId}/stop
export function useStopItem() {
  const queryClient = useQueryClient();

  const result = api.useMutation(
    "post",
    "/users/progress/courses/{courseId}/versions/{courseVersionId}/stop",
    {
      retry: 5,
      retryDelay: (attempt) => 1000 * attempt,

      onSuccess: () => {
        // Items list for the completed section — matches useItemsBySectionId's
        // real cache key (openapi-fetch stores the literal path-template
        // string as the query key, not an interpolated one).
        queryClient.invalidateQueries({
          predicate: (query) =>
            query.queryKey[0] === "get" &&
            query.queryKey[1] ===
            `/courses/versions/{versionId}/modules/{moduleId}/sections/{sectionId}/items`,
        });
        // The progress bookmark useUserProgress populates -- this is what
        // isItemLocked() in course-page.tsx reads. useUserProgress passes
        // this path as a literal (unlike useModuleProgress, which
        // interpolates real ids into its path and so needs a different
        // match), so without this the lock check kept seeing the
        // pre-completion bookmark for up to the 5-minute staleTime, showing
        // the next item as locked even though the server had already
        // unlocked it.
        queryClient.invalidateQueries({
          predicate: (query) =>
            query.queryKey[0] === "get" &&
            query.queryKey[1] ===
            "/users/progress/courses/{courseId}/versions/{courseVersionId}",
        });
      },
    }
  );

  return {
    ...result,
    error: result.error
      ? result.error.message || 'Failed to stop item'
      : null,
  };
}

export function useUpsertWatchTime() {
  const result = api.useMutation(
    "post",
    "/users/watchtime/upsert" as any,
  );
  return {
    ...result,
    error: result.error
      ? result.error.message || 'Failed to upsert watch time'
      : null,
  };
}

export function useSkipOptionalItem(): {
  mutate: (variables: { params: { path: { itemId: String }, query: { cohortId?: string } } }) => void,
  mutateAsync: (variables: { params: { path: { itemId: String }, query: { cohortId?: string } } }) => Promise<unknown>,
  data: unknown | undefined,
  error: string | null,
  isPending: boolean,
  isSuccess: boolean,
  isError: boolean,
  isIdle: boolean,
} {
  const result = api.useMutation("post", "/users/items/{itemId}/skip") as any;
  const rawError = result.error as { message?: string; response?: { data?: { message?: string } } } | null;
  return {
    ...result,
    // error: result.error ? (result.error.message || 'Failed to skip item') : null
    error: rawError
      ? rawError.message
      ?? rawError.response?.data?.message
      ?? "Failed to skip item"
      : null,
  }
}

export function useUpdateItemOptional(): {
  mutate: (variables: { params: { path: { versionId: ObjectId, itemId: ObjectId } } }) => void,
  mutateAsync: (variables: {
    params: { path: { versionId: ObjectId, itemId: ObjectId } },
    body: { isOptional: boolean }
  }) => Promise<unknown>,
  data: unknown | undefined,
  error: string | null,
  isPending: boolean,
  isSuccess: boolean,
  isError: boolean,
  isIdle: boolean,
} {
  const result = api.useMutation("put", "/courses/versions/{versionId}/items/{itemId}/optional");
  return {
    ...result,
    error: result.error ? (result.error.message || 'Failed to skip item') : null
  }
}

// PUT /courses/versions/{versionId}/items/{itemId}/proctoring
// Overrides this item's proctoring status, taking precedence over its
// module's and the course's universal proctoring setting.
export function useUpdateItemProctoring(): {
  mutate: (variables: { params: { path: { versionId: ObjectId, itemId: ObjectId } } }) => void,
  mutateAsync: (variables: {
    params: { path: { versionId: ObjectId, itemId: ObjectId } },
    body: { detectors: DetectorSetting[] | null }
  }) => Promise<unknown>,
  data: unknown | undefined,
  error: string | null,
  isPending: boolean,
  isSuccess: boolean,
  isError: boolean,
  isIdle: boolean,
} {
  const queryClient = useQueryClient();
  const result = api.useMutation("put", "/courses/versions/{versionId}/items/{itemId}/proctoring", {
    onSuccess: () => {
      // Without this, useItemById keeps serving its pre-update cached
      // resolved detector list -- the override saved correctly, the UI
      // just never knew to ask again.
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === "get" &&
          query.queryKey[1] ===
          "/courses/{courseId}/versions/{versionId}/modules/{moduleId}/sections/{sectionId}/item/{itemId}",
      });
    },
  });
  return {
    ...result,
    error: result.error ? (result.error.message || 'Failed to update item proctoring') : null
  }
}

// PUT /courses/versions/{versionId}/modules/{moduleId}/proctoring
// Overrides this module's proctoring status, taking precedence over the
// course's universal setting for every item in the module without its own
// item-level override.
export function useUpdateModuleProctoring(): {
  mutate: (variables: { params: { path: { versionId: ObjectId, moduleId: ObjectId } } }) => void,
  mutateAsync: (variables: {
    params: { path: { versionId: ObjectId, moduleId: ObjectId } },
    body: { detectors: DetectorSetting[] | null }
  }) => Promise<unknown>,
  data: unknown | undefined,
  error: string | null,
  isPending: boolean,
  isSuccess: boolean,
  isError: boolean,
  isIdle: boolean,
} {
  const queryClient = useQueryClient();
  const result = api.useMutation("put", "/courses/versions/{versionId}/modules/{moduleId}/proctoring", {
    onSuccess: () => {
      // A module override changes the resolved detectors for every item in
      // it that doesn't have its own item-level override -- invalidate every
      // cached item, not just one, so none of them keep serving stale data.
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === "get" &&
          query.queryKey[1] ===
          "/courses/{courseId}/versions/{versionId}/modules/{moduleId}/sections/{sectionId}/item/{itemId}",
      });
    },
  });
  return {
    ...result,
    error: result.error ? (result.error.message || 'Failed to update module proctoring') : null
  }
}


// PATCH /users/{userid}/progress/courses/{courseId}/versions/{courseVersionId}/reset
export function useResetProgress(): {
  mutate: (variables: { params: { path: { userId: string, courseId: string, courseVersionId: string } }, body: components['schemas']['ResetCourseProgressBody'] }) => void,
  mutateAsync: (variables: { params: { path: { userId: string, courseId: string, courseVersionId: string } }, body: components['schemas']['ResetCourseProgressBody'] }) => Promise<unknown>,
  data: unknown | undefined,
  error: string | null,
  isPending: boolean,
  isSuccess: boolean,
  isError: boolean,
  isIdle: boolean,
  reset: () => void,
  status: 'idle' | 'pending' | 'success' | 'error'
} {
  const result = api.useMutation("patch", "/users/{userId}/progress/courses/{courseId}/versions/{courseVersionId}/reset");
  return {
    ...result,
    error: result.error ? (result.error.message || 'Failed to reset progress') : null
  };
}

export function useRecalculateProgress(): {
  mutate: (variables: { params: { query: { courseId?: string, userId?: string, versionId?: string } } }) => void,
  mutateAsync: (variables: { params: { query: { courseId?: string, userId?: string, versionId?: string } } }) => Promise<unknown>,
  data: unknown | undefined,
  error: string | null,
  isPending: boolean,
  isSuccess: boolean,
  isError: boolean,
  isIdle: boolean,
  reset: () => void,
  status: 'idle' | 'pending' | 'success' | 'error'
} {
  const result = api.useMutation("patch", "/users/enrollments/bulk-update-watchtime-progress-completeCounts");
  return {
    ...result,
    error: result.error ? (result.error.message || 'Failed to reset progress') : null
  };
}

export function useProctoringSettings(courseId: string, versionId: string): {
  data: ProctoringSettings | undefined,
  isLoading: boolean,
  error: string | null,
  refetch: () => void
} {
  const result = api.useQuery("get", "/setting/course-setting/{courseId}/{versionId}", {
    params: { path: { courseId, versionId } }
  },
    { enabled: !!courseId && !!versionId }
  );

  return {
    data: result.data,
    isLoading: result.isLoading,
    error: result.error ? (result.error.message || 'Failed to fetch user by Firebase UID') : null,
    refetch: result.refetch
  };
}

export function useEditProctoringSettings() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const editSettings = async (
    courseId: string,
    versionId: string,
    detectors: { name: string; enabled: boolean }[],
    isNew: boolean,
    linearProgressionEnabled: boolean,
    seekForwardEnabled: boolean,
    isPublic: boolean,
    hpSystem: boolean,
    baseHp: number,
    randomizeItems: boolean,
    crowdsourcedQuestionSubmissionEnabled: boolean = false,
    caseStudiesEnabled: boolean = false,
    caseStudyStrictUnlockEnabled: boolean = true,
    caseStudyWeakStreakThreshold: number = 3,
  ) => {
    setLoading(true);
    setError(null);


    try {
      const method = 'PUT';
      const url = `${import.meta.env.VITE_BASE_URL}/setting/course-setting/${courseId}/${versionId}/proctoring`;

      const body =
      {
        detectors: detectors.map((d) => ({
          detectorName: d.name,
          settings: { enabled: d.enabled },
        })),
        linearProgressionEnabled,
        seekForwardEnabled,
        isPublic,
        hpSystem,
        baseHp,
        randomizeItems,
        crowdsourcedQuestionSubmissionEnabled,
        caseStudiesEnabled,
        caseStudyStrictUnlockEnabled,
        caseStudyWeakStreakThreshold,
      };

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', 'authorization': `Bearer ${localStorage.getItem('firebase-auth-token')}` },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        throw new Error(`Failed to update settings: ${res.status}`);
      }

      return await res.json();
    } catch (err: any) {
      setError(err.message || 'Unknown error');
      throw new Error(err);
    } finally {
      setLoading(false);
    }
  };

  return { editSettings, loading, error };
}

// PUT /setting/course-setting/{courseId}/{versionId}/follow-up-invite
// Configures the exclusive follow-up course a student is invited to when they
// complete this (source) course version.
export function useUpdateFollowUpInvite() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateFollowUpInvite = async (
    courseId: string,
    versionId: string,
    followUpInvite: {
      enabled: boolean;
      courseId?: string;
      courseVersionId?: string;
      cohortId?: string;
      role?: string;
    },
  ) => {
    setLoading(true);
    setError(null);

    try {
      const url = `${import.meta.env.VITE_BASE_URL}/setting/course-setting/${courseId}/${versionId}/follow-up-invite`;

      const res = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          authorization: `Bearer ${localStorage.getItem('firebase-auth-token')}`,
        },
        body: JSON.stringify(followUpInvite),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        // Surface the backend's actionable message (e.g. "please select a cohort").
        throw new Error(data?.message || `Failed to update follow-up invite: ${res.status}`);
      }

      return data;
    } catch (err: any) {
      setError(err.message || 'Unknown error');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { updateFollowUpInvite, loading, error };
}

// PATCH /users/{userId}/enrollments/courses/{courseId}/versions/{versionId}/cohorts
// Replaces the cohorts an instructor is confined to on a course version. An
// empty list clears the assignment, returning them to course-wide access.
// Raw fetch rather than the generated client because this endpoint is not in
// src/types/schema.ts yet — regenerate and switch to api.useMutation once it is.
export function useAssignInstructorCohorts() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const assignCohorts = async (
    userId: string,
    courseId: string,
    versionId: string,
    cohortIds: string[],
  ) => {
    setLoading(true);
    setError(null);

    try {
      const url = `${import.meta.env.VITE_BASE_URL}/users/${userId}/enrollments/courses/${courseId}/versions/${versionId}/cohorts`;

      const res = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          authorization: `Bearer ${localStorage.getItem('firebase-auth-token')}`,
        },
        body: JSON.stringify({ cohortIds }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data?.message || `Failed to assign cohorts: ${res.status}`);
      }

      return data as { cohortIds: string[] };
    } catch (err: any) {
      setError(err.message || 'Unknown error');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { assignCohorts, loading, error };
}

// POST /setting/course-setting/{courseId}/{versionId}/follow-up-invite/backfill
// Re-sends the configured follow-up invite to every student who already
// completed this (source) course version but isn't yet enrolled in the target
// course — e.g. students who finished before the follow-up invite was set up.
export function useBackfillFollowUpInvites() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const backfillFollowUpInvites = async (
    courseId: string,
    versionId: string,
  ): Promise<{
    completed: number;
    alreadyEnrolled: number;
    alreadyInvited: number;
    missingEmail: number;
    invited: number;
  }> => {
    setLoading(true);
    setError(null);

    try {
      const url = `${import.meta.env.VITE_BASE_URL}/setting/course-setting/${courseId}/${versionId}/follow-up-invite/backfill`;

      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          authorization: `Bearer ${localStorage.getItem('firebase-auth-token')}`,
        },
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data?.message || `Failed to backfill follow-up invites: ${res.status}`);
      }

      return data;
    } catch (err: any) {
      setError(err.message || 'Unknown error');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { backfillFollowUpInvites, loading, error };
}

// Accept (or reject) a course invite from within the app. Hits the local
// backend accept endpoint (the backend-provided acceptUrl points at APP_URL,
// which may be a different/staging host, so we build it from our own base).
export function useAcceptInvite() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const acceptInvite = async (
    inviteId: string,
    action: 'ACCEPT' | 'REJECTED' = 'ACCEPT',
  ) => {
    setLoading(true);
    setError(null);
    try {
      const url = `${import.meta.env.VITE_BASE_URL}/notifications/invite/${inviteId}?action=${action}`;
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          authorization: `Bearer ${localStorage.getItem('firebase-auth-token')}`,
        },
      });
      if (!res.ok) {
        throw new Error(`Failed to process invite: ${res.status}`);
      }
      return true;
    } catch (err: any) {
      setError(err.message || 'Unknown error');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { acceptInvite, loading, error };
}

export function useSubmitStudentQuestion(): {
  submitQuestion: (
    courseId: string,
    courseVersionId: string,
    segmentId: string,
    payload: import('@/types/student-question.types').StudentQuestionSubmissionPayload,
  ) => Promise<import('@/types/student-question.types').StudentQuestionSubmissionResult>;
  loading: boolean;
  error: string | null;
} {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submitQuestion = async (
    courseId: string,
    courseVersionId: string,
    segmentId: string,
    payload: import('@/types/student-question.types').StudentQuestionSubmissionPayload,
  ): Promise<import('@/types/student-question.types').StudentQuestionSubmissionResult> => {
    setLoading(true);
    setError(null);

    try {
      const url = `${import.meta.env.VITE_BASE_URL}/student-questions/courses/${courseId}/versions/${courseVersionId}/segments/${segmentId}`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          authorization: `Bearer ${localStorage.getItem('firebase-auth-token')}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        let serverMessage = '';
        try {
          const errBody = await response.json();
          serverMessage = errBody?.message || errBody?.error || '';
        } catch {
          /* response had no JSON body */
        }
        throw new Error(serverMessage || `Failed to submit question: ${response.status}`);
      }

      return await response.json();
    } catch (err: any) {
      const message = err?.message || 'Failed to submit student question';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { submitQuestion, loading, error };
}

export function useListStudentQuestions(): {
  listForCourseVersion: (
    courseId: string,
    courseVersionId: string,
    status?: import('@/types/student-question.types').StudentQuestionStatusFilter,
    limit?: number,
    gateState?: import('@/types/student-question.types').StudentQuestionGateStateFilter,
  ) => Promise<import('@/types/student-question.types').StudentQuestionListResponse>;
  listForSegment: (
    courseId: string,
    courseVersionId: string,
    segmentId: string,
    limit?: number,
  ) => Promise<import('@/types/student-question.types').StudentQuestionListResponse>;
  getSegmentDetails: (
    courseId: string,
    courseVersionId: string,
    segmentId: string,
  ) => Promise<import('@/types/student-question.types').SegmentDetails>;
  loading: boolean;
  error: string | null;
} {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Memoized so their identity is stable across renders. Callers put these in
  // useEffect/useCallback deps; unstable references caused an infinite fetch
  // loop on the teacher review + segment panel. (Setters are stable; all inputs
  // arrive as arguments.)
  const request = useCallback(async (url: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          authorization: `Bearer ${localStorage.getItem('firebase-auth-token')}`,
        },
      });
      if (!response.ok) {
        let serverMessage = '';
        try {
          const errBody = await response.json();
          serverMessage = errBody?.message || errBody?.error || '';
        } catch {
          /* response had no JSON body */
        }
        throw new Error(serverMessage || `Failed to load student questions: ${response.status}`);
      }
      return await response.json();
    } catch (err: any) {
      const message = err?.message || 'Failed to load student questions';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const listForCourseVersion = useCallback(async (
    courseId: string,
    courseVersionId: string,
    status: import('@/types/student-question.types').StudentQuestionStatusFilter = 'ALL',
    limit = 100,
    gateState?: import('@/types/student-question.types').StudentQuestionGateStateFilter,
  ) => {
    const params = new URLSearchParams();
    if (status && status !== 'ALL') params.set('status', status);
    if (gateState && gateState !== 'ALL') params.set('gateState', gateState);
    params.set('limit', String(limit));
    const url = `${import.meta.env.VITE_BASE_URL}/student-questions/courses/${courseId}/versions/${courseVersionId}?${params.toString()}`;
    return await request(url);
  }, [request]);

  const listForSegment = useCallback(async (
    courseId: string,
    courseVersionId: string,
    segmentId: string,
    limit = 100,
  ) => {
    const params = new URLSearchParams();
    params.set('limit', String(limit));
    const url = `${import.meta.env.VITE_BASE_URL}/student-questions/courses/${courseId}/versions/${courseVersionId}/segments/${segmentId}?${params.toString()}`;
    return await request(url);
  }, [request]);

  const getSegmentDetails = useCallback(async (
    courseId: string,
    courseVersionId: string,
    segmentId: string,
  ) => {
    const url = `${import.meta.env.VITE_BASE_URL}/student-questions/courses/${courseId}/versions/${courseVersionId}/segments/${segmentId}/details`;
    return await request(url);
  }, [request]);

  return { listForCourseVersion, listForSegment, getSegmentDetails, loading, error };
}

export function useListMyStudentQuestions(): {
  listMine: (
    status?: import('@/types/student-question.types').StudentQuestionStatusFilter,
    limit?: number,
  ) => Promise<import('@/types/student-question.types').StudentQuestionListResponse>;
  loading: boolean;
  error: string | null;
} {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Memoized so its identity is stable across renders — callers put this in
  // effect deps, and an unstable reference caused an infinite fetch loop on
  // the My Submissions page. (Setters are stable; status/limit come from args.)
  const listMine = useCallback(async (
    status: import('@/types/student-question.types').StudentQuestionStatusFilter = 'ALL',
    limit = 100,
  ) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (status && status !== 'ALL') params.set('status', status);
      params.set('limit', String(limit));
      const url = `${import.meta.env.VITE_BASE_URL}/student-questions/me?${params.toString()}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          authorization: `Bearer ${localStorage.getItem('firebase-auth-token')}`,
        },
      });
      if (!response.ok) {
        let serverMessage = '';
        try {
          const errBody = await response.json();
          serverMessage = errBody?.message || errBody?.error || '';
        } catch {
          /* response had no JSON body */
        }
        throw new Error(serverMessage || `Failed to load my submissions: ${response.status}`);
      }
      return await response.json();
    } catch (err: any) {
      const message = err?.message || 'Failed to load my submissions';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { listMine, loading, error };
}

export function useUpdateStudentQuestionStatus(): {
  updateStatus: (
    courseId: string,
    courseVersionId: string,
    segmentId: string,
    questionId: string,
    status: import('@/types/student-question.types').StudentQuestionStatus,
    reason?: string,
  ) => Promise<void>;
  loading: boolean;
  error: string | null;
} {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateStatus = async (
    courseId: string,
    courseVersionId: string,
    segmentId: string,
    questionId: string,
    status: import('@/types/student-question.types').StudentQuestionStatus,
    reason?: string,
  ): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const url = `${import.meta.env.VITE_BASE_URL}/student-questions/courses/${courseId}/versions/${courseVersionId}/segments/${segmentId}/questions/${questionId}/status`;
      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          authorization: `Bearer ${localStorage.getItem('firebase-auth-token')}`,
        },
        body: JSON.stringify({ status, reason }),
      });
      if (!response.ok) {
        let serverMessage = '';
        try {
          const errBody = await response.json();
          serverMessage = errBody?.message || errBody?.error || '';
        } catch {
          /* response had no JSON body */
        }
        throw new Error(serverMessage || `Failed to update status: ${response.status}`);
      }
    } catch (err: any) {
      const message = err?.message || 'Failed to update student question status';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { updateStatus, loading, error };
}

export function useUpdateStudentQuestionContent(): {
  updateContent: (
    courseId: string,
    courseVersionId: string,
    segmentId: string,
    questionId: string,
    payload: import('@/types/student-question.types').UpdateStudentQuestionPayload,
  ) => Promise<void>;
  loading: boolean;
  error: string | null;
} {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateContent = async (
    courseId: string,
    courseVersionId: string,
    segmentId: string,
    questionId: string,
    payload: import('@/types/student-question.types').UpdateStudentQuestionPayload,
  ): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const url = `${import.meta.env.VITE_BASE_URL}/student-questions/courses/${courseId}/versions/${courseVersionId}/segments/${segmentId}/questions/${questionId}`;
      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          authorization: `Bearer ${localStorage.getItem('firebase-auth-token')}`,
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        let serverMessage = '';
        try {
          const errBody = await response.json();
          serverMessage = errBody?.message || errBody?.error || '';
        } catch {
          /* response had no JSON body */
        }
        throw new Error(serverMessage || `Failed to update question: ${response.status}`);
      }
    } catch (err: any) {
      const message = err?.message || 'Failed to update student question';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { updateContent, loading, error };
}

export function usePublicCourses(
  page: number,
  limit: number,
  enabled: boolean,
  search: string = ''
): {
  data: { courses: any[]; currentPage: number; totalPages: number; totalDocuments: number } | undefined,
  isLoading: boolean,
  error: string | null,
  refetch: () => void,
  isRefetching: boolean
} {
  const result = api.useQuery(
    "get",
    "/courses/public",
    {
      params: {
        query: { page, limit, search }
      }
    },
    {
      enabled,
      staleTime: 5 * 60 * 1000, // 5 minutes
    }
  );

  return {
    data: result.data,
    isLoading: result.isLoading,
    error: result.error ? (result.error.message || 'Failed to fetch public courses') : null,
    refetch: result.refetch,
    isRefetching: result.isRefetching,
  };
}

export function useInviteUsers(): {
  mutate: (variables: { params: { path: { courseId: string, courseVersionId: string } }, body: InviteBody }) => void,
  mutateAsync: (variables: { params: { path: { courseId: string, courseVersionId: string } }, body: InviteBody }) => Promise<InviteResponse>,
  data: InviteResponse | undefined,
  error: string | null,
  isPending: boolean,
  isSuccess: boolean,
  isError: boolean,
  isIdle: boolean,
  reset: () => void,
  status: 'idle' | 'pending' | 'success' | 'error'
} {
  const result = api.useMutation("post", "/notifications/invite/courses/{courseId}/versions/{courseVersionId}");
  return {
    ...result,
    error: result.error ? (result.error.message || 'Failed to invite users') : null
  };
}

export function useCourseInvites(courseId: string, courseVersionId: string, enabled: boolean = true, search: string = "", currentPage: number = 1, limit: number = 10, inviteStatus: string, sort?: string, startDate?: string, endDate?: string): {
  data: InviteResponse | undefined,
  isLoading: boolean,
  error: string | null,
  refetch: () => void,
  isRefetching: boolean
} {
  const queryParams: any = { search, currentPage, limit };
  if (sort) queryParams.sort = sort;
  if (inviteStatus) queryParams.inviteStatus = inviteStatus;
  if (startDate) queryParams.startDate = startDate;
  if (endDate) queryParams.endDate = endDate;

  const result = api.useQuery("get", "/notifications/invite/courses/{courseId}/versions/{courseVersionId}", {
    params: { path: { courseId, courseVersionId }, query: queryParams },
    enabled: enabled
  });

  return {
    data: result.data,
    isLoading: result.isLoading,
    error: result.error ? (result.error.message || 'Failed to fetch course invites') : null,
    refetch: result.refetch,
    isRefetching: result.isRefetching,
  };
}

export function useResendInvite(): {
  mutate: (variables: { params: { path: { inviteId: string } } }) => void,
  mutateAsync: (variables: { params: { path: { inviteId: string } } }) => Promise<MessageResponse>,
  data: MessageResponse | undefined,
  error: string | null,
  isPending: boolean,
  isSuccess: boolean,
  isError: boolean,
  isIdle: boolean,
  reset: () => void,
  status: 'idle' | 'pending' | 'success' | 'error'
} {
  const result = api.useMutation("post", "/notifications/invite/resend/{inviteId}");
  return {
    ...result,
    error: result.error ? (result.error.message || 'Failed to resend invite') : null
  };
}

export function useCancelInvite(): {
  mutate: (variables: { params: { path: { inviteId: string } } }) => void,
  mutateAsync: (variables: { params: { path: { inviteId: string } } }) => Promise<MessageResponse>,
  data: MessageResponse | undefined,
  error: string | null,
  isPending: boolean,
  isSuccess: boolean,
  isError: boolean,
  isIdle: boolean,
  reset: () => void,
  status: 'idle' | 'pending' | 'success' | 'error'
} {
  const result = api.useMutation("post", "/notifications/invite/cancel/{inviteId}");
  return {
    ...result,
    error: result.error ? (result.error.message || 'Failed to cancel invite') : null
  };
}

// GET /users/{id}/watchTime/item/itemId

export function useWatchTimeByItemId(userId: string, courseId: string, courseVersionId: string, itemId: string, type: string, cohortId?: string): {
  data: undefined,
  isLoading: boolean,
  error: string | null,
  refetch: () => void
} {
  const result = api.useQuery("get", "/users/{id}/watchTime/course/{courseId}/version/{courseVersionId}/item/{itemId}/type/{type}", {
    params: { path: { id: userId, courseId: courseId, courseVersionId: courseVersionId, itemId: itemId, type: type }, query: { cohortId } }
  }, { enabled: !!userId && !!itemId && !!type },);

  return {
    data: result.data,
    isLoading: result.isLoading,
    error: result.error ? (result.error.message || 'Failed to fetch watch time') : null,
    refetch: result.refetch
  };
}

export function useEditUser(): {
  mutate: (variables: { body: { firstName?: string; lastName?: string; avatar?: string; gender?: string; country?: string; state?: string; city?: string } }) => void,
  mutateAsync: (variables: { body: { firstName?: string; lastName?: string; avatar?: string; gender?: string; country?: string; state?: string; city?: string } }) => Promise<void>,
  data: void | undefined,
  error: string | null,
  isPending: boolean,
  isSuccess: boolean,
  isError: boolean,
  isIdle: boolean,
  reset: () => void,
  status: 'idle' | 'pending' | 'success' | 'error'
} {
  const result = api.useMutation("patch", "/users/edit");

  return {
    ...result,
    error: result.error ? (result.error.message || 'Failed to edit user') : null
  };
}

export function useWatchtimeTotal(): {
  data: number | undefined,
  isLoading: boolean,
  error: string | null,
  refetch: () => void
} {
  const result = api.useQuery("get", "/users/watchTime/total");

  return {
    data: result.data,
    isLoading: result.isLoading,
    error: result.error ? (result.error.message || 'Failed to fetch total watch time') : null,
    refetch: result.refetch
  };
}

// GenAI hook;
// GET  /:id/tasks/:type/status
export function useGenAIResponse():{
  data: any;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
} {
  const result = api.useQuery("get", "/{id}/tasks/{type}/status" as any, {
    params: { path: {id, type} }
  })
  return {
    data: result.data,
    isLoading: result.isLoading,
    error: result.error ? (result.error.message || 'Failed to fetch GenAI response') : null,
    refetch: result.refetch
  }
}




// --- AnomalyController Hooks ---

// POST /anomalies/record/image
export function useReportAnomalyImage(): {
  mutate: (variables: { body: NewAnomalyData; file: File }) => void,
  mutateAsync: (variables: { body: NewAnomalyData; file: File }) => Promise<AnomalyData>,
  data: AnomalyData | undefined,
  error: string | null,
  isPending: boolean,
  isSuccess: boolean,
  isError: boolean,
  isIdle: boolean,
  reset: () => void,
  status: 'idle' | 'pending' | 'success' | 'error'
} {
  // This assumes api.useMutation supports multipart/form-data
  const result = api.useMutation("post", "/anomalies/record/image", { isMultipart: true });
  return {
    ...result,
    error: result.error ? (result.error.message || 'Failed to record anomaly image') : null
  };
}

// POST /anomalies/record/audio
export function useReportAnomalyAudio(): {
  mutate: (variables: { body: NewAnomalyData; file: File }) => void,
  mutateAsync: (variables: { body: NewAnomalyData; file: File }) => Promise<AnomalyData>,
  data: AnomalyData | undefined,
  error: string | null,
  isPending: boolean,
  isSuccess: boolean,
  isError: boolean,
  isIdle: boolean,
  reset: () => void,
  status: 'idle' | 'pending' | 'success' | 'error'
} {
  // This assumes api.useMutation supports multipart/form-data
  const result = api.useMutation("post", "/anomalies/record/audio", { isMultipart: true });
  return {
    ...result,
    error: result.error ? (result.error.message || 'Failed to record anomaly audio') : null
  };
}

// GET /anomalies/course/:courseId/version/:versionId/user/:userId
export function useUserAnomalies(courseId: string, versionId: string, userId: string): {
  data: AnomalyData[] | undefined,
  isLoading: boolean,
  error: string | null,
  refetch: () => void
} {
  const result = api.useQuery(
    "get",
    "/anomalies/course/{courseId}/version/{versionId}/user/{userId}",
    { params: { path: { courseId, versionId, userId } } },
    { enabled: !!courseId && !!versionId && !!userId }
  );
  return {
    data: result.data,
    isLoading: result.isLoading,
    error: result.error ? (result.error.message || 'Failed to fetch user anomalies') : null,
    refetch: result.refetch
  };
}

// GET /anomalies/course/:courseId/version/:versionId
export function useCourseAnomalies(courseId: string, versionId: string): {
  data: AnomalyData[] | undefined,
  isLoading: boolean,
  error: string | null,
  refetch: () => void
} {
  const result = api.useQuery(
    "get",
    "/anomalies/course/{courseId}/version/{versionId}",
    { params: { path: { courseId, versionId } } },
    { enabled: !!courseId && !!versionId }
  );
  return {
    data: result.data,
    isLoading: result.isLoading,
    error: result.error ? (result.error.message || 'Failed to fetch course anomalies') : null,
    refetch: result.refetch
  };
}

// DELETE /anomalies/:id
export function useDeleteAnomaly(): {
  mutate: (variables: { params: { path: { id: string } }, body: DeleteAnomalyBody }) => void,
  mutateAsync: (variables: { params: { path: { id: string } }, body: DeleteAnomalyBody }) => Promise<void>,
  data: void | undefined,
  error: string | null,
  isPending: boolean,
  isSuccess: boolean,
  isError: boolean,
  isIdle: boolean,
  reset: () => void,
  status: 'idle' | 'pending' | 'success' | 'error'
} {
  const result = api.useMutation("delete", "/anomalies/{id}");
  return {
    ...result,
    error: result.error ? (result.error.message || 'Failed to delete anomaly') : null
  };
}

export interface QuestionId {
  questionId: string;
}

export interface QuestionResponse {
  _id: string;
  type: string;
  priority: string;
  details: any;
  skipCount?: number;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  attemptedByUsersCount: number;
  attemptCount: number;
}

export interface FlagQuestionBody {
  reason: string;
  courseId: string;
  versionId: string;
}

export interface FlagId {
  flagId: string;
}

export interface ResolveFlagBody {
  status: 'resolved' | 'rejected';
}

// Question Bank types
export interface CreateQuestionBankBody {
  courseId?: string;
  courseVersionId?: string;
  questions?: string[];
  tags?: string[];
  title: string;
  description?: string;
}

export interface CreateQuestionBankResponse {
  questionBankId: string;
}

export interface GetQuestionBankByIdParams {
  questionBankId: string;
}

export interface QuestionBankResponse {
  _id?: string | ObjectId;
  courseId?: string;
  courseVersionId?: string;
  questions?: string[];
  tags?: string[];
  title: string;
  description: string;
}


export interface QuestionBankAndQuestionParams {
  questionBankId: string;
  questionId: string;
}

export interface ReplaceQuestionResponse {
  newQuestionId: string;
}

// Quiz types - matching backend validators
export interface AddQuestionBankBody {
  bankId: string;
  count: number;
  difficulty?: string[];
  tags?: string[];
}

export interface RemoveQuestionBankParams {
  quizId: string;
  questionBankId: string;
}

export interface EditQuestionBankBody {
  questionBankId: string;
  questionsCount?: number;
}

export interface GetUserMatricesParams {
  quizId: string;
  userId: string;
}

export interface QuizAttemptParam {
  quizId: string;
  attemptId: string;
}

export interface UpdateQuizSubmissionParam {
  quizId: string;
  submissionId: string;
  score: number;
}

export interface RegradeSubmissionBody {
  gradingResult: any;
}

export interface AddFeedbackParams {
  quizId: string;
  submissionId: string;
  questionId: string;
}

export interface AddFeedbackBody {
  feedback: string;
}

export interface GetAllQuestionBanksResponse extends Array<{
  questionBankId: string;
  questionsCount?: number;
  title?: string;
  description?: string;
  points?: number;
}> { }

// Attempt types
export interface CreateAttemptParams {
  quizId: string;
}

export interface SaveAttemptParams {
  quizId: string;
  attemptId: string;
}

export interface SubmitAttemptParams {
  quizId: string;
  attemptId: string;
}

export interface QuestionAnswersBody {
  answers: SaveQuestion[];
}

// --- Question Controller Hooks ---
// Quiz hooks
export function useAttemptQuiz(): {
  mutate: (variables: { params: { path: { quizId: string } }, body: { cohortId?: string } }) => void,
  mutateAsync: (variables: { params: { path: { quizId: string } }, body: { cohortId?: string } }) => Promise<CreateAttemptResponse>,
  data: CreateAttemptResponse | undefined,
  error: string | null,
  isPending: boolean,
  isSuccess: boolean,
  isError: boolean,
  isIdle: boolean,
  reset: () => void,
  status: 'idle' | 'pending' | 'success' | 'error'
} {
  const result = api.useMutation("post", "/quizzes/{quizId}/attempt")
  return {
    mutate: result.mutate,
    mutateAsync: result.mutateAsync,
    data: result.data,
    isPending: result.isPending,
    isSuccess: result.isSuccess,
    isError: result.isError,
    isIdle: result.isIdle,
    reset: result.reset,
    status: result.status,
    error: result.error ? (result.error.message || 'Failed to attempt quiz') : null
  };
}

export function useFlagQuestion(): {
  mutate: (variables: { params: { path: { questionId: string } }, body: components['schemas']['FlagQuestionBody'] }) => void,
  mutateAsync: (variables: { params: { path: { questionId: string } }, body: components['schemas']['FlagQuestionBody'] }) => Promise<void>,
  data: void | undefined,
  error: string | null,
  isPending: boolean,
  isSuccess: boolean,
  isError: boolean,
  isIdle: boolean,
  reset: () => void,
  status: 'idle' | 'pending' | 'success' | 'error'
} {
  const result = api.useMutation("post", "/quizzes/questions/{questionId}/flag");
  return {
    ...result,
    error: result.error ? (result.error.message || 'Failed to flag question') : null
  };
}


export interface SaveQuizResponse {
  result: 'CORRECT' | 'INCORRECT' | 'PARTIALLY_CORRECT';
  explanation?: string;
}

export function useSaveQuiz(): {
  mutate: (variables: { params: { path: { quizId: string, attemptId: string } }, body: { answers: SaveQuestion[], cohortId?: string } }) => void,
  mutateAsync: (variables: { params: { path: { quizId: string, attemptId: string } }, body: { answers: SaveQuestion[], cohortId?: string } }) => Promise<SaveQuizResponse>,
  data: SaveQuizResponse | undefined,
  error: string | null,
  isPending: boolean,
  isSuccess: boolean,
  isError: boolean,
  isIdle: boolean,
  reset: () => void,
  status: 'idle' | 'pending' | 'success' | 'error'
} {
  const result = api.useMutation("post", "/quizzes/{quizId}/attempt/{attemptId}/save");
  return {
    mutate: result.mutate,
    mutateAsync: result.mutateAsync,
    data: result.data,
    isPending: result.isPending,
    isSuccess: result.isSuccess,
    isError: result.isError,
    isIdle: result.isIdle,
    reset: result.reset,
    status: result.status,
    error: result.error ? (result.error.message || 'Failed to save quiz') : null
  };
}

export function useSubmitQuiz(): {
  mutate: (variables: { params: { path: { quizId: string, attemptId: string } }, body: { answers: SaveQuestion[], isSkipped?: boolean, courseId: string | undefined, courseVersionId: string | null | undefined, watchItemId?: string, cohortId?: string } }) => void,
  mutateAsync: (variables: { params: { path: { quizId: string, attemptId: string } }, body: { answers: SaveQuestion[], isSkipped?: boolean, courseId: string | undefined, courseVersionId: string | null | undefined, watchItemId?: string, cohortId?: string } }) => Promise<SubmitAttemptResponse>,
  data: SubmitAttemptResponse | undefined,
  error: string | null,
  isPending: boolean,
  isSuccess: boolean,
  isError: boolean,
  isIdle: boolean,
  reset: () => void,
  status: 'idle' | 'pending' | 'success' | 'error'
} {
  const result = api.useMutation("post", "/quizzes/{quizId}/attempt/{attemptId}/submit");
  return {
    mutate: result.mutate,
    mutateAsync: result.mutateAsync,
    data: result.data,
    isPending: result.isPending,
    isSuccess: result.isSuccess,
    isError: result.isError,
    isIdle: result.isIdle,
    reset: result.reset,
    status: result.status,
    error: result.error ? (result.error.message || 'Failed to submit quiz') : null
  };
}

interface IAttemptDetails {
  attemptId: string | ObjectId;
  submissionResultId?: string | ObjectId;
}

// GET /quizzes/{quizId}/user/{userId}
export function useUserQuizMetrics(
  quizId: string,
  userId: string,
  cohortId?: string
): {
  data: UserQuizMetricsResponse | undefined;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
} {
  const result = api.useQuery(
    "get",
    "/quizzes/quiz/{quizId}/user/{userId}",
    {
      params: { path: { quizId, userId }, query: { cohortId } },
    },
    {
      enabled: !!quizId && !!userId,
    }
  );

  return {
    data: result.data,
    isLoading: result.isLoading,
    error: result.error ? (result.error.message || 'Quiz metric fetch failed') : null,
    refetch: result.refetch,
  };
}


// GET /quiz/{quizId}/submissions/{submissionId}
export function useQuizSubmission(quizId: string, submissionId: string): {
  data: QuizSubmissionResponseUpdated | undefined,
  isLoading: boolean,
  error: string | null,
  refetch: () => void
} {
  const result = api.useQuery("get", "/quizzes/quiz/{quizId}/submissions/{submissionId}", {
    params: { path: { quizId, submissionId } }
  }, { enabled: !!quizId && !!submissionId }
  );

  return {
    data: result.data,
    isLoading: result.isLoading,
    error: result.error ? (result.error.message ? result.error.message : "Cannot fetch Quiz submission details.") : null,
    refetch: result.refetch
  };
}


// GET /quizzes/questions/{questionId}
export function useQuestionById(questionId: string): {
  data: QuestionResponse | undefined,
  isLoading: boolean,
  error: string | null,
  refetch: () => void
} {
  const result = api.useQuery("get", "/quizzes/questions/{questionId}", {
    params: { path: { questionId } }
  }, { enabled: !!questionId });

  return {
    data: result.data,
    isLoading: result.isLoading,
    error: result.error ? (result.error.message || 'Failed to fetch question') : null,
    refetch: result.refetch
  };
}

// POST /quizzes/questions
export function useCreateQuestion(): {
  mutate: (variables: { body: QuestionBody }) => void,
  mutateAsync: (variables: { body: QuestionBody }) => Promise<{ questionId: string }>,
  data: { questionId: string } | undefined,
  error: string | null,
  isPending: boolean,
  isSuccess: boolean,
  isError: boolean,
  isIdle: boolean,
  reset: () => void,
  status: 'idle' | 'pending' | 'success' | 'error'
} {
  const result = api.useMutation("post", "/quizzes/questions");
  return {
    ...result,
    error: result.error ? (result.error.message || 'Question creation failed') : null
  };
}

// PUT /quizzes/questions/{questionId}
export function useUpdateQuestion(): {
  mutate: (variables: { params: { path: { questionId: string } }, body: QuestionBody }) => void,
  mutateAsync: (variables: { params: { path: { questionId: string } }, body: QuestionBody }) => Promise<QuestionResponse>,
  data: QuestionResponse | undefined,
  error: string | null,
  isPending: boolean,
  isSuccess: boolean,
  isError: boolean,
  isIdle: boolean,
  reset: () => void,
  status: 'idle' | 'pending' | 'success' | 'error'
} {
  const result = api.useMutation("put", "/quizzes/questions/{questionId}");
  return {
    ...result,
    error: result.error ? (result.error.message || 'Question update failed') : null
  };
}

// DELETE /quizzes/questions/{questionId}
export function useDeleteQuestion(): {
  mutate: (variables: { params: { path: { questionId: string } } }) => void,
  mutateAsync: (variables: { params: { path: { questionId: string } } }) => Promise<void>,
  data: void | undefined,
  error: string | null,
  isPending: boolean,
  isSuccess: boolean,
  isError: boolean,
  isIdle: boolean,
  reset: () => void,
  status: 'idle' | 'pending' | 'success' | 'error'
} {
  const result = api.useMutation("delete", "/quizzes/questions/{questionId}");
  return {
    ...result,
    error: result.error ? (result.error.message || 'Question deletion failed') : null
  };
}

// POST /quizzes/questions/flags/{flagId}/resolve
export function useResolveFlaggedQuestion(): {
  mutate: (variables: { params: { path: { flagId: string } }, body: ResolveFlagBody }) => void,
  mutateAsync: (variables: { params: { path: { flagId: string } }, body: ResolveFlagBody }) => Promise<void>,
  data: void | undefined,
  error: string | null,
  isPending: boolean,
  isSuccess: boolean,
  isError: boolean,
  isIdle: boolean,
  reset: () => void,
  status: 'idle' | 'pending' | 'success' | 'error'
} {
  const result = api.useMutation("post", "/quizzes/questions/flags/{flagId}/resolve");
  return {
    ...result,
    error: result.error ? (result.error.message || 'Failed to resolve flagged question') : null
  };
}

// --- Question Bank Controller Hooks ---

// POST /quizzes/question-bank
export function useCreateQuestionBank(): {
  mutate: (variables: { body: CreateQuestionBankBody }) => void,
  mutateAsync: (variables: { body: CreateQuestionBankBody }) => Promise<CreateQuestionBankResponse>,
  data: CreateQuestionBankResponse | undefined,
  error: string | null,
  isPending: boolean,
  isSuccess: boolean,
  isError: boolean,
  isIdle: boolean,
  reset: () => void,
  status: 'idle' | 'pending' | 'success' | 'error'
} {
  const result = api.useMutation("post", "/quizzes/question-bank");
  return {
    ...result,
    error: result.error ? (result.error.message || 'Question bank creation failed') : null
  };
}

// GET /quizzes/question-bank/{questionBankId}
export function useQuestionBankById(questionBankId: string): {
  data: QuestionBankResponse | undefined,
  isLoading: boolean,
  error: string | null,
  refetch: () => void
} {
  // A bank's question list changes outside this screen — approving a student
  // submission promotes a question into it. The global 5-minute staleTime made
  // those additions invisible until the cache expired, so this query opts out
  // and always revalidates on mount.
  const result = api.useQuery("get", "/quizzes/question-bank/{questionBankId}", {
    params: { path: { questionBankId } }
  }, {
    enabled: !!questionBankId && questionBankId !== '',
    staleTime: 0,
    refetchOnMount: 'always',
  });

  return {
    data: result.data,
    isLoading: result.isLoading,
    error: result.error ? (result.error.message || 'Failed to fetch question bank') : null,
    refetch: result.refetch
  };
}

// PATCH /quizzes/question-bank/{questionBankId}/questions/{questionId}/add
export function useAddQuestionToBank(): {
  mutate: (variables: { params: { path: { questionBankId: string, questionId: string } } }) => void,
  mutateAsync: (variables: { params: { path: { questionBankId: string, questionId: string } } }) => Promise<QuestionBankResponse>,
  data: QuestionBankResponse | undefined,
  error: string | null,
  isPending: boolean,
  isSuccess: boolean,
  isError: boolean,
  isIdle: boolean,
  reset: () => void,
  status: 'idle' | 'pending' | 'success' | 'error'
} {
  const result = api.useMutation("patch", "/quizzes/question-bank/{questionBankId}/questions/{questionId}/add");
  return {
    ...result,
    error: result.error ? (result.error.message || 'Failed to add question to bank') : null
  };
}

// PATCH /quizzes/question-bank/{questionBankId}/questions/{questionId}/remove
export function useRemoveQuestionFromBank(): {
  mutate: (variables: { params: { path: { questionBankId: string, questionId: string } } }) => void,
  mutateAsync: (variables: { params: { path: { questionBankId: string, questionId: string } } }) => Promise<QuestionBankResponse>,
  data: QuestionBankResponse | undefined,
  error: string | null,
  isPending: boolean,
  isSuccess: boolean,
  isError: boolean,
  isIdle: boolean,
  reset: () => void,
  status: 'idle' | 'pending' | 'success' | 'error'
} {
  const result = api.useMutation("patch", "/quizzes/question-bank/{questionBankId}/questions/{questionId}/remove");
  return {
    ...result,
    error: result.error ? (result.error.message || 'Failed to remove question from bank') : null
  };
}

// PATCH /quizzes/question-bank/{questionBankId}/questions/{questionId}/replace-duplicate
export function useReplaceQuestionWithDuplicate(): {
  mutate: (variables: { params: { path: { questionBankId: string, questionId: string } } }) => void,
  mutateAsync: (variables: { params: { path: { questionBankId: string, questionId: string } } }) => Promise<ReplaceQuestionResponse>,
  data: ReplaceQuestionResponse | undefined,
  error: string | null,
  isPending: boolean,
  isSuccess: boolean,
  isError: boolean,
  isIdle: boolean,
  reset: () => void,
  status: 'idle' | 'pending' | 'success' | 'error'
} {
  const result = api.useMutation("patch", "/quizzes/question-bank/{questionBankId}/questions/{questionId}/replace-duplicate");
  return {
    ...result,
    error: result.error ? (result.error.message || 'Failed to replace question with duplicate') : null
  };
}

// --- Quiz Controller Hooks ---

// POST /quizzes/quiz/{quizId}/bank
export function useAddQuestionBankToQuiz(): {
  mutate: (variables: { params: { path: { quizId: string } }, body: AddQuestionBankBody }) => void,
  mutateAsync: (variables: { params: { path: { quizId: string } }, body: AddQuestionBankBody }) => Promise<void>,
  data: void | undefined,
  error: string | null,
  isPending: boolean,
  isSuccess: boolean,
  isError: boolean,
  isIdle: boolean,
  reset: () => void,
  status: 'idle' | 'pending' | 'success' | 'error'
} {
  const result = api.useMutation("post", "/quizzes/quiz/{quizId}/bank");
  return {
    ...result,
    error: result.error ? (result.error.message || 'Failed to add question bank to quiz') : null
  };
}

export function useGetProcotoringSettings(): {
  getSettings: (courseId: string, courseVersionId: string) => Promise<any>;
  settingLoading: boolean;
  settingError: string | null;
} {
  const [settingLoading, setSettingLoading] = useState(false);
  const [settingError, setSettingError] = useState<string | null>(null);

  const getSettings = async (
    courseId: string,
    courseVersionId: string
  ): Promise<any> => {
    setSettingLoading(true);
    setSettingError(null);

    try {
      const method = 'GET';
      const url = `${import.meta.env.VITE_BASE_URL}/setting/course-setting/${courseId}/${courseVersionId}`;

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', 'authorization': `Bearer ${localStorage.getItem('firebase-auth-token')}` },
      });

      if (!res.ok) {
        throw new Error(`Failed to update settings: ${res.status}`);
      }

      const data = await res.json();
      return data;
    } catch (err: any) {
      setSettingError(err.message || 'Unknown error');
    } finally {
      setSettingLoading(false);
    }
  };

  return { getSettings, settingLoading, settingError };
}

export function useInvites(): {
  getInvites: () => Promise<any>;
  loading: boolean;
  error: string | null;
} {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getInvites = async (): Promise<any> => {
    setLoading(true);
    setError(null);

    try {
      const method = 'GET';
      const url = `${import.meta.env.VITE_BASE_URL}/notifications/invite/`;

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', 'authorization': `Bearer ${localStorage.getItem('firebase-auth-token')}` },
      });


      if (!res.ok) {
        throw new Error(`Failed to update settings: ${res.status}`);
      }

      return await res.json();
    } catch (err: any) {
      setError(err.message || 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  return { getInvites, loading, error };
}

// DELETE /quizzes/quiz/{quizId}/bank/{questionBankId}
export function useRemoveQuestionBankFromQuiz(): {
  mutate: (variables: { params: { path: { quizId: string, questionBankId: string } } }) => void,
  mutateAsync: (variables: { params: { path: { quizId: string, questionBankId: string } } }) => Promise<void>,
  data: void | undefined,
  error: string | null,
  isPending: boolean,
  isSuccess: boolean,
  isError: boolean,
  isIdle: boolean,
  reset: () => void,
  status: 'idle' | 'pending' | 'success' | 'error'
} {
  const result = api.useMutation("delete", "/quizzes/quiz/{quizId}/bank/{questionBankId}");
  return {
    ...result,
    error: result.error ? (result.error.message || 'Failed to remove question bank from quiz') : null
  };
}

// PATCH /quizzes/quiz/{quizId}/bank
export function useEditQuestionBankInQuiz(): {
  mutate: (variables: { params: { path: { quizId: string } }, body: EditQuestionBankBody }) => void,
  mutateAsync: (variables: { params: { path: { quizId: string } }, body: EditQuestionBankBody }) => Promise<void>,
  data: void | undefined,
  error: string | null,
  isPending: boolean,
  isSuccess: boolean,
  isError: boolean,
  isIdle: boolean,
  reset: () => void,
  status: 'idle' | 'pending' | 'success' | 'error'
} {
  const result = api.useMutation("patch", "/quizzes/quiz/{quizId}/bank");
  return {
    ...result,
    error: result.error ? (result.error.message || 'Failed to edit question bank configuration') : null
  };
}

// GET /quizzes/quiz/{quizId}/bank
export function useGetAllQuestionBanksForQuiz(quizId: string): {
  data: GetAllQuestionBanksResponse | undefined,
  isLoading: boolean,
  error: string | null,
  refetch: () => void
} {
  // Same reasoning as useQuestionBankById: bank membership and question counts
  // change from the student-question review screen, so never serve this stale.
  const result = api.useQuery("get", "/quizzes/quiz/{quizId}/bank", {
    params: { path: { quizId } }
  }, {
    enabled: !!quizId,
    staleTime: 0,
    refetchOnMount: 'always',
  });

  return {
    data: result.data,
    isLoading: result.isLoading,
    error: result.error ? (result.error.message || 'Failed to fetch question banks for quiz') : null,
    refetch: result.refetch
  };
}

// POST /quizzes/quiz/{quizId}/submission/{submissionId}/score/{score}
export function useUpdateQuizSubmissionScore(): {
  mutate: (variables: { params: { path: { quizId: string, submissionId: string, score: number } } }) => void,
  mutateAsync: (variables: { params: { path: { quizId: string, submissionId: string, score: number } } }) => Promise<void>,
  data: void | undefined,
  error: string | null,
  isPending: boolean,
  isSuccess: boolean,
  isError: boolean,
  isIdle: boolean,
  reset: () => void,
  status: 'idle' | 'pending' | 'success' | 'error'
} {
  const result = api.useMutation("post", "/quizzes/quiz/{quizId}/submission/{submissionId}/score/{score}");
  return {
    ...result,
    error: result.error ? (result.error.message || 'Failed to update submission score') : null
  };
}

// POST /quizzes/quiz/{quizId}/submission/{submissionId}/regrade
export function useRegradeQuizSubmission(): {
  mutate: (variables: { params: { path: { quizId: string, submissionId: string } }, body: RegradeSubmissionBody }) => void,
  mutateAsync: (variables: { params: { path: { quizId: string, submissionId: string } }, body: RegradeSubmissionBody }) => Promise<void>,
  data: void | undefined,
  error: string | null,
  isPending: boolean,
  isSuccess: boolean,
  isError: boolean,
  isIdle: boolean,
  reset: () => void,
  status: 'idle' | 'pending' | 'success' | 'error'
} {
  const result = api.useMutation("post", "/quizzes/quiz/{quizId}/submission/{submissionId}/regrade");
  return {
    ...result,
    error: result.error ? (result.error.message || 'Failed to regrade submission') : null
  };
}

// POST /quizzes/quiz/{quizId}/submission/{submissionId}/question/{questionId}/feedback
export function useAddFeedbackToQuizQuestion(): {
  mutate: (variables: { params: { path: { quizId: string, submissionId: string, questionId: string } }, body: AddFeedbackBody }) => void,
  mutateAsync: (variables: { params: { path: { quizId: string, submissionId: string, questionId: string } }, body: AddFeedbackBody }) => Promise<void>,
  data: void | undefined,
  error: string | null,
  isPending: boolean,
  isSuccess: boolean,
  isError: boolean,
  isIdle: boolean,
  reset: () => void,
  status: 'idle' | 'pending' | 'success' | 'error'
} {
  const result = api.useMutation("post", "/quizzes/quiz/{quizId}/submission/{submissionId}/question/{questionId}/feedback");
  return {
    ...result,
    error: result.error ? (result.error.message || 'Failed to add feedback to question') : null
  };
}

// POST /quizzes/quiz/{quizId}/user/{userId}/reset-attempts
export function useResetUserQuizAttempts(): {
  mutate: (variables: { params: { path: { quizId: string, userId: string } } }) => void,
  mutateAsync: (variables: { params: { path: { quizId: string, userId: string } } }) => Promise<void>,
  data: void | undefined,
  error: string | null,
  isPending: boolean,
  isSuccess: boolean,
  isError: boolean,
  isIdle: boolean,
  reset: () => void,
  status: 'idle' | 'pending' | 'success' | 'error'
} {
  const result = api.useMutation("post", "/quizzes/quiz/{quizId}/user/{userId}/reset-attempts");
  return {
    ...result,
    error: result.error ? (result.error.message || 'Failed to reset user quiz attempts') : null
  };
}

// --- Attempt Controller Hooks ---

// GET /quizzes/{quizId}/attempt/{attemptId}
export function useGetAttemptDetails(quizId: string, attemptId: string): {
  data: QuizAttemptResponse | undefined,
  isLoading: boolean,
  error: string | null,
  refetch: () => void
} {
  const result = api.useQuery("get", "/quizzes/quiz/{quizId}/attempt/{attemptId}", {
    params: { path: { quizId, attemptId } }
  }, { enabled: !!quizId && !!attemptId });

  return {
    data: result.data,
    isLoading: result.isLoading,
    error: result.error ? (result.error.message || 'Failed to fetch attempt details') : null,
    refetch: result.refetch
  };
}

// GET /quizzes/{quizId}/analytics
export function useQuizAnalytics(quizId: string): {
  data: {
    totalAttempts: number;
    submissions: number;
    passRate: number;
    averageScore: number;
    averagePercentage: number;
  } | undefined,
  isLoading: boolean,
  error: string | null,
  refetch: () => void
} {
  const result = api.useQuery("get", "/quizzes/quiz/{quizId}/analytics", {
    params: { path: { quizId } }
  }, { enabled: !!quizId });

  return {
    data: result.data,
    isLoading: result.isLoading,
    error: result.error ? (result.error.message || 'Failed to fetch quiz analytics') : null,
    refetch: result.refetch
  };
}

// GET /quizzes/{quizId}/details
export function useQuizDetails(quizId: string): {
  data: {
    _id?: string | ObjectId;
    name: string;
    description: string;
    type: 'QUIZ';
    details?: {
      questionBankRefs: Array<{
        bankId: string;
        count: number;
        difficulty?: string[];
        tags?: string[];
        type?: string;
      }>;
      passThreshold: number;
      maxAttempts: number;
      quizType: 'DEADLINE' | 'NO_DEADLINE';
      releaseTime: Date;
      questionVisibility: number;
      deadline?: Date;
      approximateTimeToComplete: string;
      allowPartialGrading: boolean;
      allowHint: boolean;
      showCorrectAnswersAfterSubmission: boolean;
      showExplanationAfterSubmission: boolean;
      showScoreAfterSubmission: boolean;
    };
  } | undefined,
  isLoading: boolean,
  error: string | null,
  refetch: () => void
} {
  const result = api.useQuery("get", "/quizzes/quiz/{quizId}/details", {
    params: { path: { quizId } }
  }, { enabled: !!quizId });

  return {
    data: result.data,
    isLoading: result.isLoading,
    error: result.error ? (result.error.message || 'Failed to fetch quiz details') : null,
    refetch: result.refetch
  };
}

// GET /quizzes/{quizId}/performance
export function useQuizPerformance(quizId: string): {
  data: Array<{
    questionId: string;
    correctRate: number;
    averageScore: number;
  }> | undefined,
  isLoading: boolean,
  error: string | null,
  refetch: () => void
} {
  const result = api.useQuery("get", "/quizzes/quiz/{quizId}/performance", {
    params: { path: { quizId } }
  }, { enabled: !!quizId });

  return {
    data: result.data,
    isLoading: result.isLoading,
    error: result.error ? (result.error.message || 'Failed to fetch quiz performance') : null,
    refetch: result.refetch
  };
}

// Get /quizzes/{quizId}/results
export function useQuizResults(quizId: string): {
  data: Array<{
    studentId: string | ObjectId;
    attemptId: string | ObjectId;
    score: number;
    status: 'PENDING' | 'PASSED' | 'FAILED';
  }> | undefined,
  isLoading: boolean,
  error: string | null,
  refetch: () => void
} {
  const result = api.useQuery("get", "/quizzes/quiz/{quizId}/results", {
    params: { path: { quizId } }
  }, { enabled: !!quizId });

  return {
    data: result.data,
    isLoading: result.isLoading,
    error: result.error ? (result.error.message || 'Failed to fetch quiz results') : null,
    refetch: result.refetch
  };
}

export function useQuizSubmissions(quizId: string, gradeStatus: GradingSystemStatus, search: string, sort: string, currentPage: number, limit: number, selectedTab: string): {
  data: { totalCount: number, totalPages: number, currentPage: number, data: QuizSubmissionResponseUpdated[] } | undefined,
  isLoading: boolean,
  error: string | null,
  refetch: () => void
} {
  const isPaginatedResult = selectedTab == "submissions";
  const result = api.useQuery("get", "/quizzes/quiz/{quizId}/submissions", {
    params: {
      path: { quizId }, query: isPaginatedResult
        ? {
          ...(gradeStatus && gradeStatus !== "All" ? { gradeStatus } : {}),
          ...(search ? { search } : {}),
          ...(sort && sort !== "All" ? { sort } : {}),
          currentPage,
          limit,
        }
        : undefined,
    }
  }, { enabled: !!quizId });
  // const result = api.useQuery("get", "/quizzes/quiz/{quizId}/submissions", {
  //   params: { path: { quizId }, query: { ...(gradeStatus && gradeStatus=="All" ? {}:{gradeStatus}), search, ...(sort && sort=="All" ? {}:{sort}), currentPage, limit   } }
  // }, { enabled: !!quizId });

  return {
    data: result.data,
    isLoading: result.isLoading,
    error: result.error ? (result.error.message || 'Failed to fetch quiz submissions') : null,
    refetch: result.refetch
  };
}

export function useSubmitFlag(): {
  mutate: (variables: { body: { courseId: string, versionId: string, entityId: string, entityType: EntityType, reason: string, questionId?: string, cohortId?: string } }) => void,
  mutateAsync: (variables: { body: { courseId: string, versionId: string, entityId: string, entityType: EntityType, reason: string, questionId?: string, cohortId?: string } }) => Promise<void>,
  error: string | null,
  isPending: boolean,
  isSuccess: boolean,
  isError: boolean,
  isIdle: boolean,
  reset: () => void,
  status: 'idle' | 'pending' | 'success' | 'error'
} {
  const result = api.useMutation("post", "/reports");
  return {
    ...result,
    error: result.error ? (result?.error?.message || 'Failed to submit flag') : null
  };
}

export function useGetReports(courseId: string, versionId: string, limit = 10, currentPage = 1, status?: string, entityType?: string, sortBy?: string,
  sortOrder?: 'asc' | 'desc'): {
    data: IReport[],
    isLoading: boolean,
    error: string | null,
    refetch: () => void,
    isRefetching: boolean
  } {

  const result = api.useQuery(
    "get",
    "/reports/{courseId}/{versionId}",
    {
      params: {
        path: {
          courseId,
          versionId
        },
        query: {
          ...(entityType && entityType !== "ALL" ? { entityType } : {}),
          ...(status && status !== "ALL" ? { status } : {}),
          limit,
          currentPage,
          sortBy,
          sortOrder
        }
      },
    },
  );
  return {
    ...result,
    error: result.error ? result.error.message || 'Failed to get flags' : null,
  };
}

export function useGetReportDetails(reportId?: string): {
  data: IReport | undefined,
  isLoading: boolean,
  error: string | null,
  refetch: () => void
} {
  const result = api.useQuery(
    "get",
    "/reports/{reportId}",
    {
      params: {
        path: {
          reportId
        }
      }
    },
    {
      enabled: !!reportId,
    }
  );
  return {
    ...result,
    error: result.error ? result.error.message || 'Failed to get flag details' : null,
  };
}

export function useUpdateReportStatus(): {
  mutate: (variables: { params: { path: { reportId: string } }, body: { status: ReportStatus, comment: string } }) => void,
  mutateAsync: (variables: { params: { path: { reportId: string } }, body: { status: ReportStatus, comment: string } }) => Promise<void>,
  error: string | null,
  isPending: boolean,
  isSuccess: boolean,
  isError: boolean,
  isIdle: boolean,
  reset: () => void,
  status: 'idle' | 'pending' | 'success' | 'error'
} {

  const result = api.useMutation("patch", "/reports/{reportId}");
  return {
    ...result,
    error: result.error ? (result?.error?.message || 'Failed to update status') : null
  };
}

// Project submission types
export interface SubmitProjectBody {
  projectId: string;
  courseId: string;
  versionId: string;
  moduleId: string;
  sectionId: string;
  watchItemId: string;
  submissionURL: string;
  comment?: string;
  cohortId?: string;
}

export interface ProjectSubmissionResponse {
  message: string;
}

// POST /project/
export function useSubmitProject(): {
  mutate: (variables: { body: SubmitProjectBody }) => void,
  mutateAsync: (variables: { body: SubmitProjectBody }) => Promise<ProjectSubmissionResponse>,
  data: ProjectSubmissionResponse | undefined,
  error: string | null,
  isPending: boolean,
  isSuccess: boolean,
  isError: boolean,
  isIdle: boolean,
  reset: () => void,
  status: 'idle' | 'pending' | 'success' | 'error'
} {
  const result = api.useMutation("post", "/project/" as any);
  return {
    ...result,
    error: result.error ? (result.error.message || 'Failed to submit project') : null
  };
}

// Custom hook to fetch project submissions for a course version
export interface ProjectSubmissionUserInfo {
  firstName?: string;
  lastName?: string;
  email?: string;
  submissionURL: string;
}

export interface ProjectSubmissionsResponse {
  course: { name: string };
  courseVersion: { name: string };
  userInfo: ProjectSubmissionUserInfo[];
}

export function useProjectSubmissions(courseId: string, versionId: string, cohortId?: string): {
  data: ProjectSubmissionsResponse | undefined,
  isLoading: boolean,
  error: string | null,
  refetch: () => void
} {
  const result = api.useQuery(
    "get",
    "/project/course/{courseId}/version/{versionId}/submissions",
    {
      params: {
        path: { courseId, versionId },
        ...(cohortId && { query: { cohortId } })
      }
    },
    { enabled: !!courseId && !!versionId }
  );
  return {
    data: result.data,
    isLoading: result.isLoading,
    error: result.error ? (result.error.message || 'Failed to fetch project submissions') : null,
    refetch: result.refetch
  };
}

// POST (copy course version)
export function useCopyCourseVersion(): {
  mutate: (variables: { params: { path: { courseId: string; courseVersionId: string } } }) => void,
  mutateAsync: (variables: { params: { path: { courseId: string; courseVersionId: string } } }) => Promise<{ message: string }>,
  data: { message: string } | undefined,
  error: string | null,
  isPending: boolean,
  isSuccess: boolean,
  isError: boolean,
  isIdle: boolean,
  reset: () => void,
  status: "idle" | "pending" | "success" | "error";
} {
  const result = api.useMutation(
    "post",
    "/courses/{courseId}/version/{courseVersionId}/copy" as any
  );

  return {
    ...result,
    error: result.error ? (result.error.message || "Failed to copy course version") : null,
  };
}


// Course registration

export const useGetCourseRegistration = (
  versionId: string,
): {
  data: VersionWithCourse;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
} => {
  const result = api.useQuery(
    "get",
    "/course/registration/version/{versionId}" as any,
    {
      params: {
        path: { versionId },
      },
    },
    {
      enabled: !!versionId,
    }
  );

  return {
    data: result.data,
    isLoading: result.isLoading,
    error: result.error
      ? result.error.message || "Failed to fetch project submissions"
      : null,
    refetch: result.refetch,
  };
};

type RegistrationBody = {
  name: string;
  email: string;
  mobile: string;
  gender: string;
  city: string;
  state: string;
  category: string;
  university: string;
};

export const useSubmitCourseRegistration: () => {
  mutate: (variables: {
    params: { path: { versionId: string } };
    body: Record<string, any>;
  }) => void;
  mutateAsync: (variables: {
    params: { path: { versionId: string } };
    body: Record<string, any>;
  }) => Promise<{ message: string }>;
  data: { message: string } | undefined;
  error: string | null;
  isPending: boolean;
  isSuccess: boolean;
  isError: boolean;
  isIdle: boolean;
  reset: () => void;
  status: 'idle' | 'pending' | 'success' | 'error';
} = () => {
  const result = api.useMutation(
    'post',
    '/course/registration/version/{versionId}' as any
  );

  return {
    ...result,
    error: result.error
      ? result.error.message || 'Failed to submit course registration'
      : null,
  };
};


export interface RegistrationRequestQuery {
  status?: RegistrationStatus;
  sort?: 'older' | 'latest';
  search?: string;
  limit?: number;
  page?: number;
}



export const useGetCourseRegistrationRequests = (
  versionId: string,
  query: RegistrationRequestQuery = {},
  enabled: boolean = true,
) => {
  const result = api.useQuery(
    "get",
    "/course/registration/requests/version/{versionId}" as any,
    {
      params: {
        path: { versionId },
        query,
      },
    },
    {
      enabled: !!versionId && enabled,
      retry: 1,
      refetchOnWindowFocus: false,
    }
  );

  return {
    data: (result.data as any) ?? {
      totalDocuments: 0,
      totalPages: 0,
      currentPage: 1,
      registrations: [],
    },
    isLoading: result.isLoading,
    error: result.error
      ? result.error.message || "Failed to fetch course registration requests"
      : null,
    refetch: result.refetch,
  };
};



export const useUpdateRegistrationStatus = (): {
  mutate: (registrationId: string, status: RegistrationStatus, cohort?: string) => void;
  mutateAsync: (registrationId: string, status: RegistrationStatus, cohort?: string) => Promise<{
    message: string;
    registrationId: string;
  }>;
  data: { message: string; registrationId: string } | undefined;
  error: string | null;
  isPending: boolean;
  isSuccess: boolean;
  isError: boolean;
  isIdle: boolean;
  reset: () => void;
  status: 'idle' | 'pending' | 'success' | 'error';
} => {
  const result = api.useMutation('patch', '/course/registration/status/{registrationId}' as any);

  return {
    mutate: (registrationId, status, cohort) =>
      result.mutate({
        params: { path: { registrationId } },
        body: { status, cohort },
      }),

    mutateAsync: (registrationId, status, cohort) =>
      result.mutateAsync({
        params: { path: { registrationId } },
        body: { status, cohort },
      }),

    data: result.data as { message: string; registrationId: string } | undefined,
    error: result.error
      ? result.error.message || 'Failed to update registration status'
      : null,
    isPending: result.isPending,
    isSuccess: result.isSuccess,
    isError: result.isError,
    isIdle: result.isIdle,
    reset: result.reset,
    status: result.status,
  };
};

export const useBulkUpdateRegistrationStatus = (): {
  mutate: (selected: string[]) => void;
  mutateAsync: (selected: string[]) => Promise<{
    message: string;
    updatedCount?: number;
  }>;
  data: { message: string; updatedCount?: number } | undefined;
  error: string | null;
  isPending: boolean;
  isSuccess: boolean;
  isError: boolean;
  isIdle: boolean;
  reset: () => void;
  status: 'idle' | 'pending' | 'success' | 'error';
} => {
  const result = api.useMutation('patch', '/course/registration/status/update/bulk' as any);

  return {
    mutate: (selected) =>
      result.mutate({
        body: { selected },
      }),

    mutateAsync: (selected) =>
      result.mutateAsync({
        body: { selected },
      }),

    data: result.data as { message: string; updatedCount?: number } | undefined,
    error: result.error
      ? result.error.message || 'Failed to update registration status'
      : null,
    isPending: result.isPending,
    isSuccess: result.isSuccess,
    isError: result.isError,
    isIdle: result.isIdle,
    reset: result.reset,
    status: result.status,
  };
};

export const useUpdateRegistrationFields = (): {
  mutate: (versionId: string, fields: { label: string; type: "text" | "textarea" | "email" | "tel" | "date" | "number" | "url" | "select"; required: boolean; options: string[] }[]) => void;
  mutateAsync: (versionId: string, fields: { label: string; type: "text" | "textarea" | "email" | "tel" | "date" | "number" | "url" | "select"; required: boolean; options: string[] }[]) => Promise<{ message: string }>;
  data: { message: string } | undefined;
  error: string | null;
  isPending: boolean;
  isSuccess: boolean;
  isError: boolean;
  isIdle: boolean;
  reset: () => void;
  status: 'idle' | 'pending' | 'success' | 'error';
} => {
  const result = api.useMutation('put', '/course/registration/settings/version/{versionId}' as any);

  return {
    mutate: (versionId, fields) =>
      result.mutate({
        params: { path: { versionId } },
        body: fields,
      }),

    mutateAsync: (versionId, fields) =>
      result.mutateAsync({
        params: { path: { versionId } },
        body: fields,
      }),

    data: result.data as { message: string } | undefined,
    error: result.error
      ? result.error.message || 'Failed to update registration fields'
      : null,
    isPending: result.isPending,
    isSuccess: result.isSuccess,
    isError: result.isError,
    isIdle: result.isIdle,
    reset: result.reset,
    status: result.status,
  };
};


// export const useGetRegistrationFields = (
//   versionId: string,
// ): {
//   data: Omit<Field, 'id' | 'isDefault' >[];
//   isLoading: boolean;
//   error: string | null;
//   refetch: () => void;
// } => {
//   const result = api.useQuery(
//     'get',
//     '/course/registration/settings/version/{versionId}' as any,
//     {
//       params: {
//         path: { versionId },
//       },
//     },
//     {
//       enabled: !!versionId,
//       retry: 1,
//       refetchOnWindowFocus: false,
//     }
//   );

//   return {
//     data: (result.data as Omit<Field, 'id' | 'isDefault'>[]) || [],
//     isLoading: result.isLoading,
//     error: result.error
//       ? result.error.message || 'Failed to fetch registration fields'
//       : null,
//     refetch: result.refetch,
//   };
// };


//New hook for the Form creation 
// New hook for updating registration fields
export const useCreateRegistrationFields = (versionId: string): {
  mutate: (fields: any) => void;
  mutateAsync: (fields: any) => Promise<{ message: string }>;
  data: { message: string } | undefined;
  error: string | null;
  isPending: boolean;
  isSuccess: boolean;
  isError: boolean;
  isIdle: boolean;
  reset: () => void;
  status: 'idle' | 'pending' | 'success' | 'error';
} => {
  // Assuming the endpoint path includes versionId; adjust if needed based on your API structure
  const result = api.useMutation('put', `/course/registration/build-form/version/${versionId}` as any);

  return {
    mutate: (fields) =>
      result.mutate({
        body: fields,
      }),

    mutateAsync: (fields) =>
      result.mutateAsync({
        body: fields,
      }),

    data: result.data as { message: string } | undefined,
    error: result.error
      ? result.error.message || 'Failed to update registration fields'
      : null,
    isPending: result.isPending,
    isSuccess: result.isSuccess,
    isError: result.isError,
    isIdle: result.isIdle,
    reset: result.reset,
    status: result.status,
  };
};


export const useGetRegistrationFields = (
  versionId: string,
): {
  data: { jsonSchema: RJSFSchema; uiSchema: Record<string, any> } | undefined;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
} => {
  const result = api.useQuery(
    'get',
    '/course/registration/build-form/version/{versionId}' as any,
    {
      params: {
        path: { versionId },
      },
    },
    {
      enabled: !!versionId,
      retry: 1,
      refetchOnWindowFocus: false,
    }
  );

  return {
    data: result.data as { jsonSchema: RJSFSchema; uiSchema: Record<string, any> } | undefined,
    isLoading: result.isLoading,
    error: result.error
      ? result.error.message || 'Failed to fetch registration fields'
      : null,
    refetch: result.refetch,
  };
};


// Get registration status (isActive)
export const useGetRegistrationStatus = (
  versionId: string,
): {
  data: { jsonSchema: RJSFSchema; uiSchema: Record<string, any>; isActive: boolean } | undefined;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
} => {
  const result = api.useQuery(
    'get',
    '/course/registration/build-form/version/{versionId}' as any,
    {
      params: {
        path: { versionId },
      },
    },
    {
      enabled: !!versionId,
      retry: 1,
      refetchOnWindowFocus: false,
    }
  );

  return {
    data: result.data as { jsonSchema: RJSFSchema; uiSchema: Record<string, any>; isActive: boolean } | undefined,
    isLoading: result.isLoading,
    error: result.error
      ? result.error.message || 'Failed to fetch registration status'
      : null,
    refetch: result.refetch,
  };
};

// Toggle registration status (isActive)
export const useToggleRegistrationStatus = (versionId: string): {
  mutate: (params: { isActive: boolean }) => void;
  mutateAsync: (params: { isActive: boolean }) => Promise<any>;
  data: any | undefined;
  error: string | null;
  isPending: boolean;
  isSuccess: boolean;
  isError: boolean;
  isIdle: boolean;
  reset: () => void;
  status: 'idle' | 'pending' | 'success' | 'error';
} => {
  const result = api.useMutation('patch', `/course/registration/registration/version/${versionId}/toggle` as any);

  return {
    mutate: (params: { isActive: boolean }) =>
      result.mutate({
        body: params,
      }),

    mutateAsync: (params: { isActive: boolean }) =>
      result.mutateAsync({
        body: params,
      }),

    data: result.data,
    error: result.error
      ? result.error.message || 'Failed to toggle registration status'
      : null,
    isPending: result.isPending,
    isSuccess: result.isSuccess,
    isError: result.isError,
    isIdle: result.isIdle,
    reset: result.reset,
    status: result.status,
  };
};

export const useUpdateAutoApprovalsettings = (
  versionId: string,
): {
  mutate: (params: { registrationsAutoApproved?: boolean; autoapproval_emails?: string[], cohortId?: string }) => void;
  mutateAsync: (params: { registrationsAutoApproved?: boolean; autoapproval_emails?: string[], cohortId?: string }) => Promise<any>;
  data: any;
  error: string | null;
  isPending: boolean;
  isSuccess: boolean;
  isError: boolean;
  isIdle: boolean;
  reset: () => void;
  status: 'idle' | 'pending' | 'success' | 'error';
} => {
  const result = api.useMutation('put', '/course/registration/auto-approval/version/{versionId}' as any);

  return {
    mutate: (params: { registrationsAutoApproved?: boolean; autoapproval_emails?: string[], cohortId?: string }) =>
      result.mutate({
        params: {
          path: { versionId },
        },
        body: params,
      }),

    mutateAsync: (params: { registrationsAutoApproved?: boolean; autoapproval_emails?: string[], cohortId?: string }) =>
      result.mutateAsync({
        params: {
          path: { versionId },
        },
        body: params,
      }),

    data: result.data,
    error: result.error
      ? result.error.message || 'Failed to update auto-approval settings'
      : null,
    isPending: result.isPending,
    isSuccess: result.isSuccess,
    isError: result.isError,
    isIdle: result.isIdle,
    reset: result.reset,
    status: result.status,
  };
};

export const useAutoApprovalSettings = (
  versionId: string,
): {
  data: { registrationsAutoApproved?: boolean; autoapproval_emails?: string[] } | undefined;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
} => {
  const result = api.useQuery(
    "get",
    "/course/registration/build-form/version/{versionId}" as any,
    {
      params: {
        path: { versionId },
      },
    },
    {
      enabled: !!versionId,
    }
  );

  return {
    settings: result.data as { registrationsAutoApproved?: boolean; autoapproval_emails?: string[] } | undefined,
    isLoading: result.isLoading,
    error: result.error ? result.error.message || "Failed to fetch auto-approval settings" : null,
    refetch: result.refetch,
  };
};


export const useGetDynamicFields = (
  versionId: string,
): {
  data: { jsonSchema: RJSFSchema; uiSchema: Record<string, any> } | undefined;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
} => {
  const result = api.useQuery(
    "get",
    "/course/registration/form/version/{versionId}" as any,
    {
      params: {
        path: { versionId },
      },
    },
    {
      enabled: !!versionId,
    }
  );
  return {
    data: result.data as { jsonSchema: RJSFSchema; uiSchema: Record<string, any> } | undefined,
    isLoading: result.isLoading,
    error: result.error ? result.error.message || "Failed to fetch settings schema" : null,
    refetch: result.refetch,
  };
};



export type IssueStatus =
  | "ALL"
  | "REPORTED"
  | "IN_REVIEW"
  | "RESOLVED"
  | "DISCARDED"
  | "CLOSED";

export type IssueSort = "ALL" | "VIDEO" | "QUIZ" | "ARTICLE" | "QUESTION";

export type EntityType = "VIDEO" | "QUIZ" | "ARTICLE" | "QUESTION";

export interface IssueQueryParams {
  page?: number;
  limit?: number;
  status?: string;
  search?: string;
  sort?: string; // Add this line
}

export interface IssueStatusHistory {
  status: IssueStatus;
  comment: string;
  createdAt: string;
  createdBy?: string;
}

export interface IssueReport {
  _id: string;
  courseId: string;
  versionId: string;
  entityId: string;
  entityType: EntityType;
  reason: string;
  reportedBy: string;
  status: IssueStatusHistory[]; // ✅ fixed: array of objects
  createdAt: string;
  updatedAt: string;
  cohortId?: string;
  cohortId?: string;
}

interface Params {
  status: IssueStatus;
  search: string;
  sort: IssueSort;
  page: number;
  limit: number;
}

export interface IssueReportsResponse {
  issues: IssueReport[];
  totalDocuments: number;
  totalPages: number;
}

export const useGetCourseIssueReports = (
  versionId: string,
  params: Params
): {
  data: IssueReportsResponse;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
  isRefetching: boolean;
} => {
  const result = api.useQuery(
    "get",
    `/reports/student/issues/flag` as any,
    {
      params: {
        query: params,
      },
    },
    {
      enabled: !!versionId,
    }
  );

  return {
    data: result.data as IssueReportsResponse,
    isLoading: result.isLoading,
    error: result.error
      ? result.error.message || "Failed to fetch issue reports"
      : null,
    refetch: result.refetch,
    isRefetching: result.isRefetching,
  };
};



export const useUpdateStudentInterest = () => {
  const result = api.useMutation(
    "patch",
    `/reports/student/issues/interest` as any // replace with your real endpoint
  );

  return {
    mutate: (data: { issueId: string; interest: "yes" | "no" }) =>
      result.mutate({ body: data }),
    mutateAsync: (data: { issueId: string; interest: "yes" | "no" }) =>
      result.mutateAsync({ body: data }),
    data: result.data as { message: string } | undefined,
    error: result.error ? result.error.message || "Failed to submit comment" : null,
    isPending: result.isPending,
    isSuccess: result.isSuccess,
    isError: result.isError,
    isIdle: result.isIdle,
    reset: result.reset,
    status: result.status,
  };
};

export const useCreateFeedbackFormFields = (feedbackId: string): {
  mutate: (fields: any) => void;
  mutateAsync: (fields: any) => Promise<{ message: string }>;
  data: { message: string } | undefined;
  error: string | null;
  isPending: boolean;
  isSuccess: boolean;
  isError: boolean;
  isIdle: boolean;
  reset: () => void;
  status: 'idle' | 'pending' | 'success' | 'error';
} => {

  const result = api.useMutation('put', `/course/registration/build-form/version/${feedbackId}` as any);
  return {
    mutate: (fields) =>
      result.mutate({
        body: fields,
      }),

    mutateAsync: (fields) =>
      result.mutateAsync({
        body: fields,
      }),

    data: result.data as { message: string } | undefined,
    error: result.error
      ? result.error.message || 'Failed to update registration fields'
      : null,
    isPending: result.isPending,
    isSuccess: result.isSuccess,
    isError: result.isError,
    isIdle: result.isIdle,
    reset: result.reset,
    status: result.status,
  };
};

export const useGetFeedbackFormFields = (
  versionId: string,
): {
  data: { jsonSchema: RJSFSchema; uiSchema: Record<string, any> } | undefined;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
} => {
  const result = api.useQuery(
    'get',
    '/course/registration/build-form/version/{versionId}' as any,
    {
      params: {
        path: { versionId },
      },
    },
    {
      enabled: !!versionId,
      retry: 1,
      refetchOnWindowFocus: false,
    }
  );

  return {
    data: result.data as { jsonSchema: RJSFSchema; uiSchema: Record<string, any> } | undefined,
    isLoading: result.isLoading,
    error: result.error
      ? result.error.message || 'Failed to fetch registration fields'
      : null,
    refetch: result.refetch,
  };
};


export const useSubmitFeedback = (itemId: string): {
  mutate: (body: ISubmitFeedbackBody) => void;
  mutateAsync: (body: ISubmitFeedbackBody) => Promise<{ message: string }>;
  data: { message: string } | undefined;
  error: string | null;
  isPending: boolean;
  isSuccess: boolean;
  isError: boolean;
  isIdle: boolean;
  reset: () => void;
  status: 'idle' | 'pending' | 'success' | 'error';
} => {

  const result = api.useMutation('post', `/quizzes/${itemId}/feedback/submit` as any);

  return {
    mutate: (body) =>
      result.mutate({
        body: body,
      }),

    mutateAsync: (body) =>
      result.mutateAsync({
        body: body,
      }),

    data: result.data as { message: string } | undefined,
    error: result.error
      ? result.error.message || 'Failed to submit feedback'
      : null,
    isPending: result.isPending,
    isSuccess: result.isSuccess,
    isError: result.isError,
    isIdle: result.isIdle,
    reset: result.reset,
    status: result.status,
  };
};




let navigationQueue = Promise.resolve();

export function enqueueNavigation(fn: () => Promise<void>) {
  navigationQueue = navigationQueue.then(fn).catch(err => {
    console.error("Navigation error:", err);
  });
  return navigationQueue;
}


interface Submission {
  _id: string;
  userInfo: { firstName: string; lastName: string; email?: string };
  submittedAt: string;
  // Add fields like responses, status if available
}

interface SubmissionsData {
  data: Submission[];
  totalPages: number;
  totalCount: number;
}

interface UseFeedbackSubmissionsProps {
  feedbackId: string;
  courseId: string;
  searchQuery?: string;
  statusFilter?: string; // e.g., 'All', 'Submitted'
  page?: number;
  limit?: number;
}

export const useFeedbackSubmissions = ({
  feedbackId,
  courseId,
  searchQuery = '',
  page = 1,
  limit = 10
}: UseFeedbackSubmissionsProps): {
  data: SubmissionsData | undefined;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
} => {
  const result = api.useQuery(
    'get',
    `/courses/${courseId}/item/${feedbackId}/feedback/submissions` as any,
    {
      params: {
        query: {
          page: page.toString(),
          limit: limit.toString(),
          search: searchQuery,
        }
      }
    },
    {
      enabled: !!feedbackId && !!courseId,
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
    }
  );

  return {
    data: result.data as SubmissionsData | undefined,
    isLoading: result.isLoading,
    error: result.error
      ? result.error.message || 'Failed to fetch feedback submissions'
      : null,
    refetch: result.refetch,
  };
};

export const exportQuizSubmissions = async (quizId: string) => {
  const authToken = localStorage.getItem('firebase-auth-token');

  const response = await fetch(`${import.meta.env.VITE_BASE_URL}/quizzes/${quizId}/attempts/export`, {
    method: 'GET',
    headers: {
      'Authorization': authToken ? `Bearer ${authToken}` : '',
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`Failed to export quiz submissions: ${response.statusText}`);
  }

  const csvData = await response.text();

  const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `quiz_${quizId}_submissions.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
}

// Downloads every quiz question in a course version (options, explanations,
// correct answer) as a CSV for instructor review.
export const exportCourseQuestionBank = async (courseId: string, versionId: string) => {
  const authToken = localStorage.getItem('firebase-auth-token');

  const response = await fetch(
    `${import.meta.env.VITE_BASE_URL}/quizzes/question-bank/export/courses/${courseId}/versions/${versionId}`,
    {
      method: 'GET',
      headers: {
        'Authorization': authToken ? `Bearer ${authToken}` : '',
      },
      credentials: 'include',
    },
  );

  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new Error(body?.message || `Failed to export question bank: ${response.statusText}`);
  }

  // blob() rather than text(): text() would strip the UTF-8 BOM Excel needs.
  const blob = await response.blob();
  const disposition = response.headers.get('Content-Disposition') ?? '';
  const fileName =
    disposition.match(/filename="([^"]+)"/)?.[1] ?? `question_bank_${versionId}.csv`;

  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
}

export function useModuleProgress(
  courseId: string,
  versionId: string,
  cohortId?: string
): {
  data: {
    moduleId: string;
    moduleName: string;
    totalItems: number;
    completedItems: number;
  }[] | undefined;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
} {
  const result = api.useQuery(
    'get',
    `/users/progress/courses/${courseId}/versions/${versionId}/modules` as any,
    {
      params: {
        path: { courseId, versionId },
        query: { cohortId }
      }
    },
    {
      enabled: !!courseId && !!versionId
    }
  );

  return {
    data: result.data,
    isLoading: result.isLoading,
    error: result.error ? (result.error.message || "Failed to fetch module progress") : null,
    refetch: result.refetch
  };
}

// Hook for instructors to get module progress for a specific user
export function useUserModuleProgress(
  userId: string,
  courseId: string,
  versionId: string,
  cohortId?: string
): {
  data: {
    modules: {
      moduleId: string;
      moduleName: string;
      totalItems: number;
      completedItems: number;
    }[];
  } | undefined;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
} {
  const result = api.useQuery(
    'get',
    `/users/${userId}/enrollments/courses/${courseId}/versions/${versionId}/modules/progress` as any,
    {
      params: {
        path: { userId, courseId, versionId },
        query: { cohortId }
      }
    },
    {
      enabled: !!userId && !!courseId && !!versionId
    }
  );

  return {
    data: result.data as any,
    isLoading: result.isLoading,
    error: result.error ? (result.error.message || "Failed to fetch module progress") : null,
    refetch: result.refetch
  };
}


export const useHideModule = (): {
  mutate: (variables: { params: { path: { versionId: string, moduleId: string } }, body: { hide: boolean } }) => void,
  mutateAsync: (variables: { params: { path: { versionId: string, moduleId: string } }, body: { hide: boolean } }) => Promise<void>,
  error: string | null,
  isPending: boolean,
  isSuccess: boolean,
  isError: boolean,
  isIdle: boolean,
  reset: () => void,
  status: 'idle' | 'pending' | 'success' | 'error'
} => {
  const result = api.useMutation('put', '/courses/versions/{versionId}/modules/{moduleId}/toggle-visibility');
  return {
    ...result,
    error: result.error ? (result?.error?.message || 'Failed to hide/unhide module') : null
  }
}
// Leaderboard hook
export interface LeaderboardEntry {
  userId: string;
  userName: string;
  completionPercentage: number;
  completedAt: Date | null;
  enrollmentDate?: Date | null;
  weeklyItems?: number;
  weeklyMinutes?: number;
  daysToComplete?: number | null;
  league?: 'finishers' | 'active';
  rank: number;
  completedCount?: number;
  score?: number;
}

export interface LeaderboardResponse {
  finishers: { data: LeaderboardEntry[]; total: number };
  active: {
    data: LeaderboardEntry[];
    total: number;
    totalPages: number;
    currentPage: number;
  };
  myStats: LeaderboardEntry | null;
}

export const useLeaderboard = (
  courseId: string,
  versionId: string,
  page: number,
  limit: number = 10,
  enabled: boolean = true,
  cohortId?: string
) => {
  const authToken = localStorage.getItem('firebase-auth-token');

  const result = useQuery({
    queryKey: ['leaderboard', courseId, versionId, page, limit, cohortId],
    queryFn: async () => {
      const response = await fetch(
        `${import.meta.env.VITE_BASE_URL}/users/progress/courses/${courseId}/versions/${versionId}/leaderboard?page=${page}&limit=${limit}${cohortId ? `&cohortId=${cohortId}` : ''}`,
        {
          method: 'GET',
          headers: {
            Authorization: authToken ? `Bearer ${authToken}` : '',
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        },
      );

      if (!response.ok) {
        throw new Error('Failed to fetch leaderboard');
      }

      // Shape is normalized below — accept either the new or legacy backend.
      return response.json() as Promise<any>;
    },
    enabled: enabled && !!courseId && !!versionId,
  });

  const raw: any = result.data;
  // New backend returns { finishers, active, myStats }; legacy returns { data, myStats }.
  const isNewShape =
    !!raw && (raw.finishers !== undefined || raw.active !== undefined);

  let finishers: LeaderboardEntry[];
  let active: LeaderboardEntry[];
  let finishersTotal: number;
  let activeTotal: number;
  let currentPage: number;
  let totalPages: number;
  let combined: LeaderboardEntry[];
  let totalDocuments: number;

  if (isNewShape) {
    finishers = raw.finishers?.data ?? [];
    active = raw.active?.data ?? [];
    finishersTotal = raw.finishers?.total ?? finishers.length;
    activeTotal = raw.active?.total ?? active.length;
    currentPage = raw.active?.currentPage ?? page;
    totalPages = raw.active?.totalPages ?? 0;
    // Flat list for compact previews: finishers first (page 1 only), then active.
    combined = (currentPage === 1 ? [...finishers, ...active] : active).map(
      (entry, index) => ({ ...entry, rank: index + 1 }),
    );
    totalDocuments = finishersTotal + activeTotal;
  } else {
    // Legacy backend (pre-two-league deploy): derive the leagues from the flat
    // `data` array so the UI still renders. New metrics (daysToComplete /
    // weeklyItems) are absent here and fall back to placeholders until deploy.
    const data: LeaderboardEntry[] = raw?.data ?? [];
    finishers = data
      .filter((e) => Math.round(e.completionPercentage) >= 100)
      .map((e, i) => ({ ...e, league: 'finishers' as const, rank: i + 1 }));
    active = data
      .filter((e) => Math.round(e.completionPercentage) < 100)
      .map((e, i) => ({ ...e, league: 'active' as const, rank: i + 1 }));
    finishersTotal = finishers.length;
    activeTotal = active.length;
    currentPage = raw?.currentPage ?? page;
    totalPages = raw?.totalPages ?? 0;
    combined = data; // already ranked/sorted by the legacy backend
    totalDocuments = raw?.totalDocuments ?? data.length;
  }

  // Tag myStats with a league if the backend didn't (legacy shape).
  let myStats: LeaderboardEntry | null = raw?.myStats ?? null;
  if (myStats && !myStats.league) {
    myStats = {
      ...myStats,
      league:
        Math.round(myStats.completionPercentage) >= 100 ? 'finishers' : 'active',
    };
  }

  return {
    finishers,
    finishersTotal,
    active,
    activeTotal,
    totalPages,
    currentPage,
    myStats,
    // Backward-compatible aliases for compact previews
    leaderboard: combined,
    totalDocuments,
    isLoading: result.isLoading,
    isFetching: result.isFetching,
    error: result.isError
      ? result.error?.message || 'Failed to fetch leaderboard'
      : null,
    refetch: result.refetch,
  };
};

// export const useLeaderboard = (courseId: string, versionId: string, enabled: boolean = true, page: number,
//   limit: number = 10,) => {
//   const authToken = localStorage.getItem('firebase-auth-token');

//   const result = useQuery({
//     queryKey: ['leaderboard', courseId, versionId, page, limit],
//     queryFn: async () => {
//       const response = await fetch(
//         `${import.meta.env.VITE_BASE_URL}/users/progress/courses/${courseId}/versions/${versionId}/leaderboard?page=${page}&limit=${limit}`,
//         {
//           method: 'GET',
//           headers: {
//             'Authorization': authToken ? `Bearer ${authToken}` : '',
//             'Content-Type': 'application/json',
//           },
//           credentials: 'include',
//         }
//       );

//       if (!response.ok) {
//         throw new Error(`Failed to fetch leaderboard: ${response.statusText}`);
//       }

//       // const data = await response.json();
//       // return data as LeaderboardEntry[];
//        return response.json() as Promise<{
//         data: LeaderboardEntry[];
//         totalDocuments: number;
//         totalPages: number;
//         currentPage: number;
//       }>;
//     },
//     enabled: enabled && !!courseId && !!versionId,
//   });

//   return {
//     data: result.data,
//     isLoading: result.isLoading,
//     error: result.isError ? result.error.message || 'Failed to fetch leaderboard' : null,
//     refetch: result.refetch,
//   };
// };

export const useHideSection = (): {
  mutate: (variables: { params: { path: { versionId: string, moduleId: string, sectionId: string } }, body: { hide: boolean } }) => void,
  mutateAsync: (variables: { params: { path: { versionId: string, moduleId: string, sectionId: string } }, body: { hide: boolean } }) => Promise<void>,
  error: string | null,
  isPending: boolean,
  isSuccess: boolean,
  isError: boolean,
  isIdle: boolean,
  reset: () => void,
  status: 'idle' | 'pending' | 'success' | 'error'
} => {
  const result = api.useMutation('put', '/courses/versions/{versionId}/modules/{moduleId}/sections/{sectionId}/toggle-visibility');
  return {
    ...result,
    error: result.error ? (result?.error?.message || 'Failed to hide/unhide section') : null
  }
}

export const useHideItem = (): {
  mutate: (variables: { params: { path: {courseId: string, versionId: string, itemId: string } }, body: { hide: boolean } }) => void,
  mutateAsync: (variables: { params: { path: { courseId: string, versionId: string, itemId: string } }, body: { hide: boolean } }) => Promise<void>,
  error: string | null,
  isPending: boolean,
  isSuccess: boolean,
  isError: boolean,
  isIdle: boolean,
  reset: () => void,
  status: 'idle' | 'pending' | 'success' | 'error'
} => {

  const result = api.useMutation('put', '/courses/{courseId}/versions/{versionId}/items/{itemId}/toggle-visibility');
  return {
    ...result,
    error: result.error ? (result?.error?.message || 'Failed to hide/unhide item') : null
  }

}

export interface GenerateAIQuestionsBody {
  text?: string;
  courseId?: string;
  versionId?: string;
  difficulty?: 'beginner' | 'intermediate' | 'advanced';
  focusAreas?: string;
  avoidTopics?: string;
}

export const useGenerateAIQuestions = (): {
  mutate: (variables: { body: GenerateAIQuestionsBody }) => void;
  mutateAsync: (variables: { body: GenerateAIQuestionsBody }) => Promise<{ success: boolean; response: TranscriptResponse[] }>;
  data: { success: boolean; response: TranscriptResponse[] } | undefined;
  error: string | null;
  isPending: boolean;
  isSuccess: boolean;
  isError: boolean;
  isIdle: boolean;
  reset: () => void;
  status: 'idle' | 'pending' | 'success' | 'error';
} => {
  const result = api.useMutation('post', '/quizzes/questions/generate-csv-res');

  return {
    ...result,
    error: result.error ? (result.error.message || 'Failed to generate AI questions') : null,
  };
}




export function useRecalculateStudentProgress(): {
  mutate: (variables: {
    body: {
      courseId: string;
      courseVersionId: string;
      userId?: string;
      cohortId?: string;
    };
  }) => void;
  mutateAsync: (variables: {
    body: {
      courseId: string;
      courseVersionId: string;
      userId?: string;
      cohortId?: string;
    };
  }) => Promise<string>;
  data: string | undefined;
  error: string | null;
  isPending: boolean;
  isSuccess: boolean;
  isError: boolean;
  isIdle: boolean;
  reset: () => void;
  status: 'idle' | 'pending' | 'success' | 'error';
  cohort?: string;
} {
  const result = api.useMutation(
    'post',
    '/users/progress/recalculate',
  );

  return {
    mutate: result.mutate,
    mutateAsync: result.mutateAsync,
    data: result.data,
    isPending: result.isPending,
    isSuccess: result.isSuccess,
    isError: result.isError,
    isIdle: result.isIdle,
    reset: result.reset,
    status: result.status,
    error: result.error
      ? result.error.message || 'Failed to recalculate progress'
      : null,
  };
}

// Hook to export feedback submissions as CSV

export const useExportFeedbackSubmissions = ({ courseId, feedbackId }: ExportFeedbackSubmissionsProps) => {
  const [isExporting, setIsExporting] = useState(false);

  const exportCSV = async () => {
    try {
      setIsExporting(true);
      const baseUrl = import.meta.env.VITE_BASE_URL;
      const response = await fetch(`${baseUrl}/courses/${courseId}/item/${feedbackId}/feedback/submissions/export`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('firebase-auth-token')}`
        }
      });

      if (!response.ok) throw new Error('Failed to export submissions');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `feedback_submissions_${feedbackId}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Feedback submissions exported successfully');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export feedback submissions');
    } finally {
      setIsExporting(false);
    }
  };

  return { exportCSV, isExporting };
};

const EMPTY_ARRAY: any[] = [];

// GET /course/registration/pending
export function useGetPendingRegistrations(instructorId: string): {
  data: PendingRegistrationNotification[];
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
} {
  const result = api.useQuery("get", "/course/registration/pending", {
    params: {
      query: { instructorId }
    }
  }, {
    enabled: !!instructorId,
    refetchOnWindowFocus: false
  });

  return {
    data: Array.isArray(result?.data) ? result?.data : (Array.isArray((result?.data as any)?.data) ? (result?.data as any)?.data : (Array.isArray((result?.data as any)?.registrations) ? (result?.data as any)?.registrations : EMPTY_ARRAY)),
    isLoading: result.isLoading,
    error: result.error ? (result.error.message || 'Failed to fetch pending registrations') : null,
    refetch: result.refetch
  };
}

// GET /course/registration/notifications/unread
export function useGetUnreadApprovedRegistrations(studentId: string): {
  data: ApprovedRegistrationNotification[];
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
} {
  const result = api.useQuery("get", "/course/registration/notifications/unread", {
    params: {
      query: { studentId }
    }
  }, {
    enabled: !!studentId,
    refetchOnWindowFocus: false
  });

  return {
    data: Array.isArray(result?.data) ? result?.data : (Array.isArray((result?.data as any)?.data) ? (result?.data as any)?.data : (Array.isArray((result?.data as any)?.registrations) ? (result?.data as any)?.registrations : EMPTY_ARRAY)),
    isLoading: result.isLoading,
    error: result.error ? (result.error.message || 'Failed to fetch unread notifications') : null,
    refetch: result.refetch
  };
}

// GET /course/registration/pending/student
export function useGetPendingStudentRegistrations(studentId: string): {
  data: PendingStudentRegistrationNotification[];
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
} {
  const result = api.useQuery("get", "/course/registration/pending/student" as any, {
    params: {
      query: { studentId }
    }
  }, {
    enabled: !!studentId,
    refetchOnWindowFocus: false,
    refetchInterval: 30000
  });

  return {
    data: Array.isArray(result?.data) ? result?.data as PendingStudentRegistrationNotification[] : (Array.isArray((result?.data as any)?.data) ? (result?.data as any)?.data : (Array.isArray((result?.data as any)?.registrations) ? (result?.data as any)?.registrations : EMPTY_ARRAY)),
    isLoading: result.isLoading,
    error: result.error ? (result.error.message || 'Failed to fetch pending student registrations') : null,
    refetch: result.refetch
  };
}

// GET /course/registration/rejected/student
export function useGetRejectedStudentRegistrations(studentId: string): {
  data: RejectedStudentRegistrationNotification[];
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
} {
  const result = api.useQuery("get", "/course/registration/rejected/student" as any, {
    params: {
      query: { studentId }
    }
  }, {
    enabled: !!studentId,
    refetchOnWindowFocus: false,
    refetchInterval: 30000
  });

  return {
    data: Array.isArray(result?.data) ? result?.data as RejectedStudentRegistrationNotification[] : (Array.isArray((result?.data as any)?.data) ? (result?.data as any)?.data : (Array.isArray((result?.data as any)?.registrations) ? (result?.data as any)?.registrations : EMPTY_ARRAY)),
    isLoading: result.isLoading,
    error: result.error ? (result.error.message || 'Failed to fetch rejected student registrations') : null,
    refetch: result.refetch
  };
}

// PATCH /course/registration/notifications/{registrationId}/read
export function useMarkNotificationAsRead(): {
  mutate: (variables: { params: { path: { registrationId: string } } }) => void,
  mutateAsync: (variables: { params: { path: { registrationId: string } } }) => Promise<{ message: string; success: boolean }>,
  data: { message: string; success: boolean } | undefined,
  error: string | null,
  isPending: boolean,
  isSuccess: boolean,
  isError: boolean,
  isIdle: boolean,
  reset: () => void,
  status: 'idle' | 'pending' | 'success' | 'error'
} {
  const result = api.useMutation("patch", "/course/registration/notifications/{registrationId}/read");

  return {
    ...result,
    error: result.error ? (result.error.message || 'Failed to mark notification as read') : null
  };
}


// Bulk Unenroll Users Hook
export const useBulkUnenrollUsers = () => {
  return api.useMutation('post', '/users/enrollments/courses/{courseId}/versions/{versionId}/bulk-unenroll');
};

export const useChangeEnrollmentStatus = () => {
  return api.useMutation('patch', '/users/{userId}/enrollments/courses/{courseId}/versions/{versionId}/status');
};

export const useBulkChangeEnrollmentStatus = () => {
  return api.useMutation('patch', '/users/enrollments/courses/{courseId}/versions/{versionId}/bulk-status');
};

// Time Slots hooks

// POST /timeslots/add
export function useAddTimeSlots(): {
  mutate: (variables: { body: { courseId: string; courseVersionId: string; timeSlots: Array<{ from: string; to: string; studentIds: string[] }> } }) => void,
  mutateAsync: (variables: { body: { courseId: string; courseVersionId: string; timeSlots: Array<{ from: string; to: string; studentIds: string[] }> } }) => Promise<{ success: boolean; message?: string }>,
  data: { success: boolean; message?: string } | undefined,
  error: string | null,
  isPending: boolean,
  isSuccess: boolean,
  isError: boolean,
  isIdle: boolean,
  reset: () => void,
  status: 'idle' | 'pending' | 'success' | 'error'
} {
  const result = api.useMutation("post", "/timeslots/add");
  return {
    ...result,
    error: result.error ? (result.error.message || 'Failed to add time slots') : null
  };
}

// POST /timeslots/remove
export function useRemoveTimeSlots(): {
  mutate: (variables: { body: { courseId: string; courseVersionId: string; timeSlotsToRemove: Array<{ from: string; to: string }> } }) => void,
  mutateAsync: (variables: { body: { courseId: string; courseVersionId: string; timeSlotsToRemove: Array<{ from: string; to: string }> } }) => Promise<{ success: boolean; message?: string }>,
  data: { success: boolean; message?: string } | undefined,
  error: string | null,
  isPending: boolean,
  isSuccess: boolean,
  isError: boolean,
  isIdle: boolean,
  reset: () => void,
  status: 'idle' | 'pending' | 'success' | 'error'
} {
  const result = api.useMutation("post", "/timeslots/remove");
  return {
    ...result,
    error: result.error ? (result.error.message || 'Failed to remove time slots') : null
  };
}

// PUT /timeslots/update
export function useUpdateTimeSlot(): {
  mutate: (variables: { body: { courseId: string; courseVersionId: string; oldTimeSlot: { from: string; to: string }; newTimeSlot: { from: string; to: string } } }) => void,
  mutateAsync: (variables: { body: { courseId: string; courseVersionId: string; oldTimeSlot: { from: string; to: string }; newTimeSlot: { from: string; to: string } } }) => Promise<{ success: boolean; message?: string }>,
  data: { success: boolean; message?: string } | undefined,
  error: string | null,
  isPending: boolean,
  isSuccess: boolean,
  isError: boolean,
  isIdle: boolean,
  reset: () => void,
  status: 'idle' | 'pending' | 'success' | 'error'
} {
  const result = api.useMutation("put", "/timeslots/update");
  return {
    ...result,
    error: result.error ? (result.error.message || 'Failed to update time slot') : null
  };
}

// PUT /timeslots/toggle
export function useToggleTimeSlots(): {
  mutate: (variables: { body: { courseId: string; courseVersionId: string; isActive: boolean } }) => void,
  mutateAsync: (variables: { body: { courseId: string; courseVersionId: string; isActive: boolean } }) => Promise<{ success: boolean; message?: string }>,
  data: { success: boolean; message?: string } | undefined,
  error: string | null,
  isPending: boolean,
  isSuccess: boolean,
  isError: boolean,
  isIdle: boolean,
  reset: () => void,
  status: 'idle' | 'pending' | 'success' | 'error'
} {
  const result = api.useMutation("put", "/timeslots/toggle");
  return {
    ...result,
    error: result.error ? (result.error.message || 'Failed to toggle time slots') : null
  };
}

// POST /timeslots/student/choose
export function useChooseTimeSlot(): {
  mutate: (variables: { body: { courseId: string; courseVersionId: string; timeSlot: { from: string; to: string } } }) => void,
  mutateAsync: (variables: { body: { courseId: string; courseVersionId: string; timeSlot: { from: string; to: string } } }) => Promise<{ success: boolean; message?: string }>,
  data: { success: boolean; message?: string } | undefined,
  error: string | null,
  isPending: boolean,
  reset: () => void,
  status: 'idle' | 'pending' | 'success' | 'error'
} {
  const result = api.useMutation("post", "/timeslots/student/choose");
  return {
    ...result,
    error: result.error ? (result.error.message || 'Failed to choose time slot') : null
  };
}

// POST /timeslots/teacher/remove-student
export function useRemoveStudentFromTimeSlot(): {
  mutateAsync: (variables: {
    body: {
      courseId: string;
      courseVersionId: string;
      studentId: string;
      timeSlot: { from: string; to: string };
    }
  }) => Promise<any>;
  isPending: boolean;
  error: string | null;
} {
  const result = api.useMutation("post", "/timeslots/teacher/remove-student");

  return {
    ...result,
    error: result.error ? (result.error.message || 'Failed to remove student from time slot') : null
  };
}

// GET /timeslots/course/{courseId}/version/{courseVersionId}
export function useGetTimeSlots(
  courseId: string | undefined,
  courseVersionId: string | undefined,
  enabled: boolean = true
): {
  data: { isActive: boolean; slots: Array<{ from: string; to: string; studentIds: string[] }> } | null | undefined,
  isLoading: boolean,
  error: string | null,
  refetch: () => void
} {
  const result = api.useQuery(
    "get",
    "/timeslots/course/{courseId}/version/{courseVersionId}",
    {
      params: {
        path: { courseId: courseId!, courseVersionId: courseVersionId! }
      }
    },
    {
      enabled: !!courseId && !!courseVersionId && enabled,
      retry: 1,
      refetchOnWindowFocus: false
    }
  );

  return {
    data: result.data?.data,
    isLoading: result.isLoading,
    error: result.error ? (result.error.message || 'Failed to fetch time slots') : null,
    refetch: result.refetch
  };
}

// PUT /timeslots/budget — configure the per-course hours budget from the
// instructor's per-category time estimates (raw fetch: not in the typed schema).
// Instructor's TOTAL estimated hours for all items of each category together
// (all videos, all quizzes, all readings, all projects) — not a per-item rate.
export type CategoryHours = {
  VIDEO?: number;
  QUIZ?: number;
  BLOG?: number;
  PROJECT?: number;
  FEEDBACK?: number;
};

export function useSetHoursBudget() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setHoursBudget = async (
    courseId: string,
    courseVersionId: string,
    categoryHours: CategoryHours,
    hoursFactor?: number,
  ): Promise<{
    totalBudgetHours: number;
    totalCategoryHours: number;
  }> => {
    setLoading(true);
    setError(null);
    try {
      const url = `${import.meta.env.VITE_BASE_URL}/timeslots/budget`;
      const res = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          authorization: `Bearer ${localStorage.getItem('firebase-auth-token')}`,
        },
        body: JSON.stringify({ courseId, courseVersionId, categoryHours, hoursFactor }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.message || `Failed to set hours budget: ${res.status}`);
      }
      return data?.data;
    } catch (err: any) {
      setError(err.message || 'Unknown error');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { setHoursBudget, loading, error };
}

// PUT /timeslots/fulfillment — configure the Phase 3 fulfillment threshold and
// whether fulfilling a window grants a same-day bonus booking (raw fetch).
export function useSetFulfillmentConfig() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setFulfillmentConfig = async (
    courseId: string,
    courseVersionId: string,
    fulfillmentThresholdPct: number,
    bonusOnFulfillment: boolean,
  ): Promise<{ fulfillmentThresholdPct: number; bonusOnFulfillment: boolean }> => {
    setLoading(true);
    setError(null);
    try {
      const url = `${import.meta.env.VITE_BASE_URL}/timeslots/fulfillment`;
      const res = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          authorization: `Bearer ${localStorage.getItem('firebase-auth-token')}`,
        },
        body: JSON.stringify({
          courseId,
          courseVersionId,
          fulfillmentThresholdPct,
          bonusOnFulfillment,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(
          data?.message || `Failed to set fulfillment settings: ${res.status}`,
        );
      }
      return data?.data;
    } catch (err: any) {
      setError(err.message || 'Unknown error');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { setFulfillmentConfig, loading, error };
}

// PUT /timeslots/capacity — derive each slot's maxStudents from a single
// capacity knob (total students the backend is provisioned for) so per-slot
// caps stay within the infra budget (raw fetch).
export function useSetCapacityConfig() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setCapacityConfig = async (
    courseId: string,
    courseVersionId: string,
    targetConcurrentStudents: number,
    headroomFactor: number,
  ): Promise<{
    targetConcurrentStudents: number;
    capacityHeadroomFactor: number;
    maxOverlappingWindows: number;
    derivedPerSlotCap: number;
    slots: { from: string; to: string; maxStudents: number }[];
  }> => {
    setLoading(true);
    setError(null);
    try {
      const url = `${import.meta.env.VITE_BASE_URL}/timeslots/capacity`;
      const res = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          authorization: `Bearer ${localStorage.getItem('firebase-auth-token')}`,
        },
        body: JSON.stringify({
          courseId,
          courseVersionId,
          targetConcurrentStudents,
          headroomFactor,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(
          data?.message || `Failed to set capacity settings: ${res.status}`,
        );
      }
      return data?.data;
    } catch (err: any) {
      setError(err.message || 'Unknown error');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { setCapacityConfig, loading, error };
}

// PUT /timeslots/extend — grant a student extra committed hours (raw fetch).
export function useGrantExtraHours() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const grantExtraHours = async (
    courseId: string,
    courseVersionId: string,
    studentId: string,
    extraHours: number,
  ): Promise<{ commitmentExtraHours: number }> => {
    setLoading(true);
    setError(null);
    try {
      const url = `${import.meta.env.VITE_BASE_URL}/timeslots/extend`;
      const res = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          authorization: `Bearer ${localStorage.getItem('firebase-auth-token')}`,
        },
        body: JSON.stringify({ courseId, courseVersionId, studentId, extraHours }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.message || `Failed to grant extra hours: ${res.status}`);
      }
      return data?.data;
    } catch (err: any) {
      setError(err.message || 'Unknown error');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { grantExtraHours, loading, error };
}

// PUT /timeslots/grant-bookings — award a student extra bookings (a consumable
// pool that lets them book beyond their daily allowance).
export function useGrantExtraBookings() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const grantExtraBookings = async (
    courseId: string,
    courseVersionId: string,
    studentId: string,
    extraBookings: number,
  ): Promise<{ commitmentExtraBookings: number }> => {
    setLoading(true);
    setError(null);
    try {
      const url = `${import.meta.env.VITE_BASE_URL}/timeslots/grant-bookings`;
      const res = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          authorization: `Bearer ${localStorage.getItem('firebase-auth-token')}`,
        },
        body: JSON.stringify({ courseId, courseVersionId, studentId, extraBookings }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.message || `Failed to award extra bookings: ${res.status}`);
      }
      return data?.data;
    } catch (err: any) {
      setError(err.message || 'Unknown error');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { grantExtraBookings, loading, error };
}

export interface SlotDemandData {
  date: string;
  isActive: boolean;
  slots: Array<{
    from: string;
    to: string;
    maxStudents: number | null;
    booked: number;
    remaining: number | null;
  }>;
}

// GET /slot-bookings/availability/course/{courseId}/version/{courseVersionId}
// Seats remaining + booked count per window for an IST day (default today), so
// an enrolled student can see capacity while picking a slot. Counts only.
export function useSlotAvailability(
  courseId: string | undefined,
  courseVersionId: string | undefined,
  date?: string,
  enabled: boolean = true,
): {
  data: SlotDemandData | undefined;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
} {
  const day = date ?? istToday();
  const valid =
    !!courseId &&
    courseId.length === 24 &&
    !!courseVersionId &&
    courseVersionId.length === 24;
  const result = useQuery({
    queryKey: ['slot-availability', courseId, courseVersionId, day],
    enabled: enabled && valid,
    queryFn: async (): Promise<SlotDemandData> => {
      const url = `${import.meta.env.VITE_BASE_URL}/slot-bookings/availability/course/${courseId}/version/${courseVersionId}?date=${encodeURIComponent(day)}`;
      const res = await fetch(url, {
        headers: {
          authorization: `Bearer ${localStorage.getItem('firebase-auth-token')}`,
        },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.message || `Failed to load availability: ${res.status}`);
      }
      return data?.data as SlotDemandData;
    },
  });
  return {
    data: result.data,
    isLoading: result.isLoading,
    error: result.error ? (result.error as Error).message : null,
    refetch: () => {
      void result.refetch();
    },
  };
}

// GET /slot-bookings/demand/course/{courseId}/version/{courseVersionId}
// Booked load per window for an IST day (the "demand schedule" for capacity
// planning). Instructors/managers only. Raw fetch via react-query.
export function useSlotDemand(
  courseId: string | undefined,
  courseVersionId: string | undefined,
  date?: string,
  enabled: boolean = true,
): {
  data: SlotDemandData | undefined;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
} {
  const valid =
    !!courseId &&
    courseId.length === 24 &&
    !!courseVersionId &&
    courseVersionId.length === 24;
  const result = useQuery({
    queryKey: ['slot-demand', courseId, courseVersionId, date ?? 'today'],
    enabled: enabled && valid,
    queryFn: async (): Promise<SlotDemandData> => {
      const q = date ? `?date=${encodeURIComponent(date)}` : '';
      const url = `${import.meta.env.VITE_BASE_URL}/slot-bookings/demand/course/${courseId}/version/${courseVersionId}${q}`;
      const res = await fetch(url, {
        headers: {
          authorization: `Bearer ${localStorage.getItem('firebase-auth-token')}`,
        },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.message || `Failed to load slot demand: ${res.status}`);
      }
      return data?.data as SlotDemandData;
    },
  });
  return {
    data: result.data,
    isLoading: result.isLoading,
    error: result.error ? (result.error as Error).message : null,
    refetch: () => {
      void result.refetch();
    },
  };
}

export interface MyBooking {
  _id: string;
  date: string;
  bookedOnDate?: string;
  from: string;
  to: string;
  kind?: string;
  status?: string;
  hoursReserved?: number;
  fulfilledAt?: string;
}

// IST "today" as YYYY-MM-DD — matches the backend's getCurrentISTDate so the
// daily-allowance math lines up regardless of the viewer's local timezone.
export function istToday(): string {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
}

// Current hour (0-23) in IST, used to gate which study days are bookable now.
export function istHour(): number {
  return parseInt(
    new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Asia/Kolkata',
      hour: '2-digit',
      hour12: false,
    }).format(new Date()),
    10,
  ) % 24;
}

// Add `days` calendar days to a YYYY-MM-DD date string (UTC-safe arithmetic).
export function addDays(date: string, days: number): string {
  const [y, m, d] = date.split('-').map(Number);
  const ms = Date.UTC(y, m - 1, d) + days * 24 * 60 * 60 * 1000;
  const dt = new Date(ms);
  const yy = dt.getUTCFullYear();
  const mm = String(dt.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(dt.getUTCDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

// The study days a student may book *right now*. Booking for day D opens 9 AM
// IST on D-2, and each individual slot stays bookable until its own start time
// on D (see slotBookingClosed):
//   • today        — all day (per-slot: only slots that haven't started yet)
//   • tomorrow     — all day
//   • day-after    — only from 9 AM IST (its D-2 open time)
export function bookableStudyDates(): string[] {
  const today = istToday();
  const hour = istHour();
  const dates: string[] = [today]; // today — slots bookable until they start
  dates.push(addDays(today, 1)); // tomorrow, always open
  if (hour >= 9) dates.push(addDays(today, 2)); // day-after, opens at 9 AM
  return dates;
}

// Minutes since midnight in IST (0–1439). Used to tell whether a slot on the
// current IST day has already started (and so is no longer bookable).
export function istNowMinutes(): number {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Kolkata',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(new Date());
  const h = Number(parts.find(p => p.type === 'hour')?.value ?? '0') % 24;
  const m = Number(parts.find(p => p.type === 'minute')?.value ?? '0');
  return h * 60 + m;
}

// A slot closes for booking at its own start time on the study day. Only the
// current IST day can have already-started slots; future days are always open.
export function slotBookingClosed(date: string, from: string): boolean {
  if (date !== istToday()) return false;
  const [h, m] = from.split(':').map(Number);
  return h * 60 + m <= istNowMinutes();
}

// How many minutes before a slot's start a booking can no longer be cancelled.
// Mirrors the backend CANCEL_CUTOFF_HOURS (1h).
export const CANCEL_CUTOFF_MINUTES = 60;

// A booking can be cancelled only up to CANCEL_CUTOFF_MINUTES before its slot
// starts; after that (or once the slot has started/passed) it locks and counts
// as an unused slot. Future study days are always cancellable; past days locked.
export function slotCancelClosed(date: string, from: string): boolean {
  const today = istToday();
  if (date < today) return true; // a past study day — already locked
  if (date > today) return false; // a future study day — well outside the cutoff
  const [h, m] = from.split(':').map(Number);
  return h * 60 + m - CANCEL_CUTOFF_MINUTES <= istNowMinutes();
}

// GET /slot-bookings/my/course/{courseId}/version/{courseVersionId}?date=
// The calling student's active bookings. Pass a YYYY-MM-DD `date` to restrict to
// one IST study day; pass `null` to fetch ALL active bookings across every study
// day (needed for the per-calendar-day allowance meter, which counts bookings by
// the day they were MADE, not the day they're for). Omitting `date` defaults to
// today for backward compatibility.
export function useMyBookings(
  courseId: string | undefined,
  courseVersionId: string | undefined,
  date?: string | null,
  enabled: boolean = true,
): {
  data: MyBooking[] | undefined;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
} {
  const allDates = date === null;
  const day = allDates ? undefined : date ?? istToday();
  const valid =
    !!courseId &&
    courseId.length === 24 &&
    !!courseVersionId &&
    courseVersionId.length === 24;
  const result = useQuery({
    queryKey: ['my-bookings', courseId, courseVersionId, allDates ? 'all' : day],
    enabled: enabled && valid,
    queryFn: async (): Promise<MyBooking[]> => {
      const base = `${import.meta.env.VITE_BASE_URL}/slot-bookings/my/course/${courseId}/version/${courseVersionId}`;
      const url = allDates ? base : `${base}?date=${encodeURIComponent(day as string)}`;
      const res = await fetch(url, {
        headers: {
          authorization: `Bearer ${localStorage.getItem('firebase-auth-token')}`,
        },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.message || `Failed to load bookings: ${res.status}`);
      }
      return (data?.data ?? []) as MyBooking[];
    },
  });
  return {
    data: result.data,
    isLoading: result.isLoading,
    error: result.error ? (result.error as Error).message : null,
    refetch: () => {
      void result.refetch();
    },
  };
}

// GET /slot-bookings/my/extra-bookings/course/{courseId}/version/{courseVersionId}
// How many instructor-awarded extra bookings the student may still make beyond
// their daily allowance (a consumable pool). 0 when none granted.
export function useMyExtraBookings(
  courseId: string | undefined,
  courseVersionId: string | undefined,
  enabled: boolean = true,
): { data: number; isLoading: boolean; refetch: () => void } {
  const valid =
    !!courseId &&
    courseId.length === 24 &&
    !!courseVersionId &&
    courseVersionId.length === 24;
  const result = useQuery({
    queryKey: ['my-extra-bookings', courseId, courseVersionId],
    enabled: enabled && valid,
    queryFn: async (): Promise<number> => {
      const url = `${import.meta.env.VITE_BASE_URL}/slot-bookings/my/extra-bookings/course/${courseId}/version/${courseVersionId}`;
      const res = await fetch(url, {
        headers: {
          authorization: `Bearer ${localStorage.getItem('firebase-auth-token')}`,
        },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.message || `Failed to load extra bookings: ${res.status}`);
      }
      return Number(data?.data?.extraBookings ?? 0);
    },
  });
  return {
    data: result.data ?? 0,
    isLoading: result.isLoading,
    refetch: () => {
      void result.refetch();
    },
  };
}

export interface MyHoursSummary {
  hasBudget: boolean;
  budgetHours: number | null;
  reservedHours: number;
  lostHours: number;
  remainingHours: number | null;
}

// GET /slot-bookings/my/hours/course/{courseId}/version/{courseVersionId}
// The student's committed-hours summary: budget, reserved, and hours LOST to
// unused (unfulfilled) slots — used to warn them about wasted budget.
export function useMyHoursSummary(
  courseId: string | undefined,
  courseVersionId: string | undefined,
  enabled: boolean = true,
): { data: MyHoursSummary | undefined; isLoading: boolean; refetch: () => void } {
  const valid =
    !!courseId &&
    courseId.length === 24 &&
    !!courseVersionId &&
    courseVersionId.length === 24;
  const result = useQuery({
    queryKey: ['my-hours-summary', courseId, courseVersionId],
    enabled: enabled && valid,
    queryFn: async (): Promise<MyHoursSummary> => {
      const url = `${import.meta.env.VITE_BASE_URL}/slot-bookings/my/hours/course/${courseId}/version/${courseVersionId}`;
      const res = await fetch(url, {
        headers: {
          authorization: `Bearer ${localStorage.getItem('firebase-auth-token')}`,
        },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.message || `Failed to load hours summary: ${res.status}`);
      }
      return data?.data as MyHoursSummary;
    },
  });
  return {
    data: result.data,
    isLoading: result.isLoading,
    refetch: () => {
      void result.refetch();
    },
  };
}

// POST /slot-bookings/book — book a time slot for today (raw fetch). The backend
// returns { success:false, message } for capacity/allowance/budget rejections
// (HTTP 200), so callers must check `success`, not just the HTTP status.
export function useBookSlot() {
  const [loading, setLoading] = useState(false);
  const book = async (
    courseId: string,
    courseVersionId: string,
    timeSlot: { from: string; to: string },
    cohortId?: string,
    date?: string,
  ): Promise<{ success: boolean; message?: string }> => {
    setLoading(true);
    try {
      const url = `${import.meta.env.VITE_BASE_URL}/slot-bookings/book`;
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          authorization: `Bearer ${localStorage.getItem('firebase-auth-token')}`,
        },
        body: JSON.stringify({ courseId, courseVersionId, timeSlot, cohortId, date }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        return { success: false, message: data?.message || `Failed to book: ${res.status}` };
      }
      return { success: data?.success !== false, message: data?.message };
    } catch (err: any) {
      return { success: false, message: err?.message || 'Network error' };
    } finally {
      setLoading(false);
    }
  };
  return { book, loading };
}

// POST /slot-bookings/cancel — cancel one of the student's own bookings.
export function useCancelBooking() {
  const [loading, setLoading] = useState(false);
  const cancel = async (
    bookingId: string,
  ): Promise<{ success: boolean; message?: string }> => {
    setLoading(true);
    try {
      const url = `${import.meta.env.VITE_BASE_URL}/slot-bookings/cancel`;
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          authorization: `Bearer ${localStorage.getItem('firebase-auth-token')}`,
        },
        body: JSON.stringify({ bookingId }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        return { success: false, message: data?.message || `Failed to cancel: ${res.status}` };
      }
      return { success: data?.success !== false, message: data?.message };
    } catch (err: any) {
      return { success: false, message: err?.message || 'Network error' };
    } finally {
      setLoading(false);
    }
  };
  return { cancel, loading };
}

// Imperative check used to gate entry on the "Continue Learning" click.
// Returns { canAccess, message }. Fails OPEN (allows) on any network/error so a
// transient failure never locks a student out — the backend gate is the backstop.
export function useCheckTimeSlotAccessOnDemand() {
  const check = async (
    courseId: string,
    courseVersionId: string,
  ): Promise<{ canAccess: boolean; message?: string }> => {
    try {
      const url = `${import.meta.env.VITE_BASE_URL}/timeslots/check-access/${courseId}/${courseVersionId}`;
      const res = await fetch(url, {
        headers: {
          authorization: `Bearer ${localStorage.getItem('firebase-auth-token')}`,
        },
      });
      if (!res.ok) return { canAccess: true };
      const data = await res.json().catch(() => ({ canAccess: true }));
      return { canAccess: data?.canAccess !== false, message: data?.message };
    } catch {
      return { canAccess: true };
    }
  };
  return { check };
}

// GET /timeslots/check-access/{courseId}/{courseVersionId}
// Pass pollMs to poll for a live cut-off when a booked window ends.
export function useCheckTimeSlotAccess(
  courseId: string | undefined,
  courseVersionId: string | undefined,
  enabled: boolean = true,
  pollMs: number = 0
): {
  data: { canAccess: boolean; message?: string } | undefined,
  isLoading: boolean,
  error: string | null,
  refetch: () => void
} {
  const result = api.useQuery(
    "get",
    "/timeslots/check-access/{courseId}/{courseVersionId}",
    {
      params: {
        path: { courseId: courseId!, courseVersionId: courseVersionId! }
      }
    },
    {
      enabled: !!courseId && !!courseVersionId && enabled,
      retry: 1,
      refetchOnWindowFocus: false,
      refetchInterval: pollMs > 0 ? pollMs : false
    }
  );

  return {
    data: result.data,
    isLoading: result.isLoading,
    error: result.error ? (result.error.message || 'Failed to check time slot access') : null,
    refetch: result.refetch
  };
}

// GET /users/enrollments
export function useUserEnrollmentsDetails(enabled: boolean = true, search?: string, role = "STUDENT", courseVersionId?: string, cohortId?: string): {
  data: components['schemas']['EnrollmentResponse'] | undefined,
  isLoading: boolean,
  error: string | null,
  refetch: () => void
} {
  const result = api.useQuery("get", "/users/enrollments/details", {
    params: {
      query: { search, role, courseVersionId, cohortId }
    },
    enabled: enabled
  });

  return {
    data: result.data,
    isLoading: result.isLoading,
    error: result.error ? (result.error.message || 'Failed to fetch user enrollments') : null,
    refetch: result.refetch
  };
}
export function useStoreWatchTimeTrack(): {
  mutate: (variables: { body: WatchTimeTrackData }) => void,
  mutateAsync: (variables: { body: WatchTimeTrackData }) => Promise<{ success: boolean; watchTimeTrack?: any }>,
  data: { success: boolean; watchTimeTrack?: any } | undefined,
  error: string | null,
  isPending: boolean,
  isSuccess: boolean,
  isError: boolean,
  isIdle: boolean,
  reset: () => void,
  status: 'idle' | 'pending' | 'success' | 'error'
} {
  const result = api.useMutation("post", "/users/user-activity-events/");
  return {
    ...result,
    error: result.error ? (result.error.message || 'Failed to store watch time track') : null
  };
}

// ─── HP System Hooks ─────────────────────────────────────────

import {
  hpApi,
  CourseWithVersions,
  CohortStats,
  HpActivity,
  HpRuleConfig,
  CreateHpActivityPayload,
  HpStudent,
  HpLedgerTransformerEntry,
  LedgerListResponse,
  LedgerStudentDetails,
  HpCohortOverviewStats,
  HpStudentSubmission,
  HpStudentSubmissionStats,
  HpCohortsResponse,
  ResetHpPayload,
  ResetStudentHpPayload,
} from '../lib/api/hp-system';

export function useHpCourseVersions() {
  const query = useQuery({
    queryKey: ['hp-course-versions'],
    queryFn: async () => {
      const res = await hpApi.getCourseVersions();
      if (!res.success) throw new Error(res.message || 'Failed to load course versions');
      return res.data;
    },
    refetchOnWindowFocus: false,
  });

  return {
    data: query.data || [],
    isLoading: query.isLoading,
    error: query.error ? (query.error as Error).message : null,
    refetch: query.refetch,
  };
}

/**
 * The HP System is opt-in per course version, so the instructor nav entry only
 * earns its place once at least one of their courses uses it.
 */
export function useInstructorHasHpCourses() {
  const { data, isLoading } = useHpCourseVersions();

  return {
    hasHpCourses: data.some(course => course.versions.length > 0),
    isLoading,
  };
}

/**
 * Whether the instructor may still write to a version's HP data. A version whose
 * HP System was switched off stays listed — and readable — but turns read-only.
 */
export function useHpVersionAccess(courseVersionId?: string) {
  const { data, isLoading } = useHpCourseVersions();

  const version = courseVersionId
    ? data.flatMap(course => course.versions).find(v => v.courseVersionId === courseVersionId)
    : undefined;

  return {
    isLoading,
    // Unknown versions stay writable here; the backend is the authority and
    // rejects the write if HP is in fact off.
    readOnly: !isLoading && version?.hpEnabled === false,
  };
}

/**
 * Learner-side counterpart: HP surfaces exist for a student only while the
 * course version they are enrolled in has the HP System switched on.
 */
export function useStudentHpEnabled(courseVersionId?: string) {
  const { user, token } = useAuthStore();
  const { data, isLoading } = useUserEnrollments(1, 100, !!token && !!user?.uid);

  const enrollments = data?.enrollments ?? [];
  const hpEnrollments = enrollments.filter(
    e => e.hpSystem === true && e.status === 'ACTIVE',
  );

  return {
    isLoading,
    // Access to HP pages, including for a course the student has finished —
    // their HP history stays theirs to read.
    hpEnabled: courseVersionId
      ? hpEnrollments.some(e => String(e.courseVersionId) === courseVersionId)
      : hpEnrollments.length > 0,
    // Narrower: worth a nav entry only while a course is still in progress.
    hasCourseInProgress: hpEnrollments.some(e => e.percentCompleted !== 100),
  };
}

export function useHpCohorts(courseVersionId: string) {
  return useQuery<HpCohortsResponse>({
    queryKey: ['hp-cohorts', courseVersionId],
    queryFn: async () => {
      const res = await hpApi.getCohorts(courseVersionId);
      if (!res.success) throw new Error(res.message || 'Failed to load cohorts');
      return res;
    },
    enabled: !!courseVersionId,
    refetchOnWindowFocus: false,
  });

  return {
    data: query.data || [],
    isLoading: query.isLoading,
    error: query.error ? (query.error as Error).message : null,
    refetch: query.refetch,
  };
}

export function useCourseDetails(versionId?: string) {
  const query = useQuery({
    queryKey: ['course-details', versionId],
    queryFn: async () => {
      if (!versionId) return null;

      const res = await hpApi.getCourseDetails(versionId);

      if (!res.success) {
        throw new Error(res.message || "Failed to fetch course details");
      }

      return res.data;
    },
    enabled: !!versionId, 
    staleTime: 5 * 60 * 1000, 
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    error: query.error ? (query.error as Error).message : null,
  };
}

export function useHpStudentCohorts() {
  const query = useQuery({
    queryKey: ['hp-student-cohorts'],
    queryFn: async () => {
      const res = await hpApi.getStudentCohorts();
      if (!res.success) throw new Error(res.message || 'Failed to load your cohorts');
      return res;
    },
    refetchOnWindowFocus: false,
  });

  
  return {
    data: query.data?.data || [],
    totalHp: query.data?.totalHp ?? 0,
    isLoading: query.isLoading,
    error: query.error ? (query.error as Error).message : null,
    refetch: query.refetch,
    isRefetching: query.isRefetching,
  };
}

export function useHpStudentActivities(courseVersionId: string, cohortId: string) {
  const query = useQuery({
    queryKey: ['hp-student-activities', courseVersionId, cohortId],
    queryFn: async () => {
      const res = await hpApi.getStudentActivities(courseVersionId, cohortId);
      if (!res.success) throw new Error(res.message || 'Failed to load activities');
      return res.data;
    },
    enabled: !!courseVersionId && !!cohortId,
    refetchOnWindowFocus: false,
  });

  return {
    data: query.data || [],
    isLoading: query.isLoading,
    error: query.error ? (query.error as Error).message : null,
    refetch: query.refetch,
    isRefetching: query.isRefetching,
  };
}

export function useSubmitActivity() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (payload: {
      courseId: string;
      courseVersionId: string;
      cohortId: string;
      activityId: string;
      payload: {
        textResponse?: string;
        links?: { url: string; label: string }[];
      };
      submissionSource?: string;
    }) => {
      const res = await hpApi.submitActivity(payload);
      if (!res.success) throw new Error(res.message || 'Failed to submit activity');
      return res.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['hp-student-activities', variables.courseVersionId, variables.cohortId] });
      queryClient.invalidateQueries({ queryKey: ['hp-student-ledger'] });
      queryClient.invalidateQueries({ queryKey: ['hp-students'] });
      queryClient.invalidateQueries({ queryKey: ['hp-cohort-overview'] });
      toast.success("Activity submitted successfully");
    },
  });

  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
  };
}

export function useUpdateActivitySubmission() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (payload: {
      submissionId: string;
      courseId: string;
      courseVersionId: string;
      cohortId: string;
      activityId: string;
        payload: {
          textResponse?: string;
          links?: { url: string; label: string }[];
          files?: any[];
          images?: any[];
        };
      submissionSource?: string;
      files?: File[];
      images?: File[];
    }) => {
      const { submissionId, ...rest } = payload;
      const res = await hpApi.updateActivitySubmission(submissionId, rest);
      if (!res.success) throw new Error(res.message || 'Failed to update submission');
      return res.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['studentMySubmissions', variables.courseVersionId, variables.cohortId] });
      toast.success("Submission updated successfully");
    },
  });

  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
  };
}

export function useHpActivities(
  courseVersionId: string,
  cohortId: string,
  status?: string,
  search?: string,
  activity?: string
) {
  const query = useQuery({
    queryKey: ['hp-activities', courseVersionId, cohortId, status, search, activity],
    queryFn: async () => {
      const res = await hpApi.getActivities(courseVersionId, cohortId, status, search, activity);
      if (!res.success) throw new Error(res.message || 'Failed to load activities');
      console.log("Fetched activities:", res.data);
      return res.data;
    },
    enabled: !!courseVersionId && !!cohortId,
    refetchOnWindowFocus: false,
  });

  return {
    data: query.data || [],
    isLoading: query.isLoading,
    error: query.error ? (query.error as Error).message : null,
    refetch: query.refetch,
    isRefetching: query.isRefetching,
  };
}

export function useCreateHpActivity() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (payload: CreateHpActivityPayload) => {
      const res = await hpApi.createActivity(payload);
      if (!res.success) throw new Error(res.message || 'Failed to create activity');
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hp-activities'] });
      toast.success("Activity created successfully");
    },
  });

  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
  };
}

export function useUpdateHpActivity() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async ({ activityId, updates }: { activityId: string, updates: Partial<HpActivity> }) => {
      const res = await hpApi.updateActivity(activityId, updates);
      if (!res.success) throw new Error(res.message || 'Failed to update activity');
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hp-activities'] });
      toast.success("Activity updated successfully");
    },
  });

  return {
    mutateAsync: (activityId: string, updates: Partial<HpActivity>) => mutation.mutateAsync({ activityId, updates }),
    isPending: mutation.isPending,
  };
}

export function usePublishHpActivity() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (activityId: string) => {
      const res = await hpApi.publishActivity(activityId);
      if (!res.success) throw new Error(res.message || 'Failed to publish activity');
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hp-activities'] });
      toast.success("Activity published successfully");
    },
  });

  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
  };
}

export function useArchiveHpActivity() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (activityId: string) => {
      const res = await hpApi.archiveActivity(activityId);
      if (!res.success) throw new Error(res.message || 'Failed to archive activity');
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hp-activities'] });
      toast.success("Activity archived successfully");
    },
  });

  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
  };
}

export function useDeleteHpActivity() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (activityId: string) => {
      const res = await hpApi.deleteActivity(activityId);
      if (!res.success) throw new Error(res.message || 'Failed to delete activity');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hp-activities'] });
      toast.success("Activity deleted successfully");
    },
  })
  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
  }
}

export function useHpRuleConfig(activityId: string | undefined) {
  const query = useQuery({
    queryKey: ['hp-rule-config', activityId],
    queryFn: async () => {
      try {
        console.log("Fetching rule config for activity ID:", activityId);
        const res = await hpApi.getRuleConfigByActivityId(activityId!);
        if (!res.success) return null;
        return res.data;
      } catch (err) {
        return null;
      }
    },
    enabled: !!activityId,
    refetchOnWindowFocus: false,
  });

  return {
    data: query.data || null,
    isLoading: query.isLoading,
    error: query.error ? (query.error as Error).message : null,
    refetch: query.refetch,
  };
}

export function useCreateHpRuleConfig() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (payload: Partial<HpRuleConfig>) => {
      const res = await hpApi.createRuleConfig(payload);
      if (!res.success) throw new Error(res.message || 'Failed to create rule config');
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hp-rule-config'] });
      toast.success("Rule configuration created");
    },
  });

  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
  };
}

export function useCreateActivityWithRule() {
  return useMutation({
    mutationFn: async (payload: {
      activity: CreateHpActivityPayload;
      ruleConfig: Partial<HpRuleConfig>;
    }) => {
      const res = await hpApi.createActivityWithRule(payload);
      if (!res.success) throw res;
      return res.data;
    },
  });
}

export function useUpdateHpRuleConfig() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async ({ ruleConfigId, updates }: { ruleConfigId: string, updates: Partial<HpRuleConfig> }) => {
      console.log("Updating rule config with ID:", ruleConfigId, "and updates:", updates);
      const res = await hpApi.updateRuleConfig(ruleConfigId, updates);
      if (!res.success) throw res;
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hp-rule-config'] });
      toast.success("Rule configuration updated");
    },
  });

  return {
    mutateAsync: (ruleConfigId: string, updates: Partial<HpRuleConfig>) => mutation.mutateAsync({ ruleConfigId, updates }),
    isPending: mutation.isPending,
  };
}

export function useHpStudents(courseVersionId: string, cohortId: string, params?: {
    page: number;
    limit: number;
    search: string;
    status: "ALL" | "SAFE" | "UNSAFE";
  }) {
  const query = useQuery({
    queryKey: ['hp-students', courseVersionId, cohortId, params],
    queryFn: async () => {
      const res = await hpApi.getStudents(courseVersionId, cohortId, params);
      if (!res.success) throw new Error(res.message || 'Failed to load students');
      return res.data;
    },
    enabled: !!courseVersionId && !!cohortId,
    refetchOnWindowFocus: false,
  });

  return {
    data: query.data || [],
    isLoading: query.isLoading,
    error: query.error ? (query.error as Error).message : null,
    refetch: query.refetch,
    isRefetching: query.isRefetching,
  };
}

export function useResetHp() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: ResetHpPayload) => {
      const res = await hpApi.resetHp(payload);

      if (!res.success) {
        throw res;
      }
      return res.documentsUpdated;
    },

    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [
          "hp-students",
          variables.courseVersionId,
          variables.cohortName,
        ],
      });
    },
  });
}

export function useResetStudentHp() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: ResetStudentHpPayload) => {
      const res = await hpApi.resetStudentHp(payload);

      if (!res.success) {
        throw res;
      }
      return res;
    },

    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [
          "hp-students",
          variables.courseVersionId,
          variables.cohortName,
        ],
      });
    },
  });
}

export function useHpStudentLedger(
  studentId: string,
  cohortId: string,
  courseId: string,
  courseVersionId: string
) {
  const query = useQuery({
    queryKey: ['hp-student-ledger', studentId, cohortId, courseId, courseVersionId],
    queryFn: async () => {
      const res = await hpApi.getStudentLedger(studentId, cohortId, courseId, courseVersionId);
      return res;
    },
    enabled: !!studentId && !!courseId && !!courseVersionId && !!cohortId,
    refetchOnWindowFocus: false,
  });

  return {
    data: query.data?.data || [],
    studentDetails: query.data?.studentDetails || null,
    total: query.data?.total || 0,
    isLoading: query.isLoading,
    error: query.error ? (query.error as Error).message : null,
    refetch: query.refetch,
    isRefetching: query.isRefetching,
  };
}

export function useMyHpLedger(
  courseId: string,
  courseVersionId: string,
  cohortId: string
) {
  const query = useQuery({
    queryKey: ['hp-my-ledger', courseId, courseVersionId, cohortId],
    queryFn: async () => {
      const res = await hpApi.getMyLedger(courseId, courseVersionId, cohortId);
      return res;
    },
    enabled: !!courseId && !!courseVersionId && !!cohortId,
    refetchOnWindowFocus: false,
  });

  return {
    data: query.data?.data || [],
    studentDetails: query.data?.studentDetails || null,
    total: query.data?.total || 0,
    isLoading: query.isLoading,
    error: query.error ? (query.error as Error).message : null,
    refetch: query.refetch,
    isRefetching: query.isRefetching,
  };
}

export function useRevertHpEntry() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (submissionId: string) => {
      const res = await hpApi.reviewSubmission(submissionId, 'REVERTED');
      if (!res.success) throw new Error(res.message || 'Failed to revert submission');
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hpStudentSubmissions'] });
      queryClient.invalidateQueries({ queryKey: ['hp-student-ledger'] });
      queryClient.invalidateQueries({ queryKey: ['hp-students'] });
      queryClient.invalidateQueries({ queryKey: ['hp-cohort-overview'] });
      toast.success('Submission reverted successfully');
    },
  });

  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
  };
}

export function useRestoreHpEntry() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async ({ entryId, note }: { entryId: string; note?: string }) => {
        const res = await hpApi.restoreLedgerEntry(entryId, note);
        if (!res.success) throw new Error(res.message || 'Failed to restore entry');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hp-student-ledger'] });
      queryClient.invalidateQueries({ queryKey: ['hp-students'] });
      queryClient.invalidateQueries({ queryKey: ['hp-cohort-overview'] });
      toast.success('Entry restored successfully');
    },
  });

  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
  };
}

// export function useAddFeedback() {
//   const queryClient = useQueryClient();
//   const mutation = useMutation({
//     mutationFn: async ({ submissionId, feedback }: { submissionId: string; feedback: string }) => {
//       const res = await hpApi.addFeedback(submissionId, feedback);
//       if (!res.success) throw new Error(res.message || 'Failed to add feedback');
//       return res.data;
//     },
//     onSuccess: () => {
//       queryClient.invalidateQueries({ queryKey: ['hpStudentSubmissions'] });
//       toast.success('Feedback added successfully');
//     },
//   });

//   return {
//     mutateAsync: mutation.mutateAsync,
//     isPending: mutation.isPending,
//   };
// }

export function useReviewSubmission() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async ({ submissionId, decision, note, pointsToDeduct }: { submissionId: string; decision: "APPROVED" | "REJECTED" | "REVERTED"; note?: string; pointsToDeduct?: number }) => {
      const res = await hpApi.reviewSubmission(submissionId, decision, note, pointsToDeduct);
      if (!res.success) throw new Error(res.message || 'Failed to review submission');
      return res.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['hpStudentSubmissions'] });
      queryClient.invalidateQueries({ queryKey: ['hp-student-ledger'] });
      queryClient.invalidateQueries({ queryKey: ['hp-students'] });
      queryClient.invalidateQueries({ queryKey: ['hp-cohort-overview'] });

      const decisionMessages = {
        APPROVED: 'Submission approved successfully',
        REJECTED: 'Submission rejected successfully',
        REVERTED: 'Submission reverted successfully'
      };
      toast.success(decisionMessages[variables.decision]);
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to review submission');
    },
  });

  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
  };
}

// Made by Rishabh Shukla....
export function useHpCohortActivitySubmissionsStats(cohortId: string, activityId: string) {
  const query = useQuery({
    queryKey: ['hp-cohort-activity-submissions-stats', cohortId, activityId],
    queryFn: async () => {
      const res = await hpApi.getCohortActivityStats(cohortId, activityId);
      if (!res.success) throw new Error(res.message || 'Failed to load submission stats');
      return res.data;
    }
  })
  return {
    data: query.data || null,
    isLoading: query.isLoading,
    error: query.error ? (query.error as Error).message : null,
    refetch: query.refetch,
  };
}

// Made by Rishabh Shukla....
export function useHpActivitiesStatsMap(cohortId: string, courseVersionId: string) {
  return useQuery({
    queryKey: ['hp-activities-stats-map', cohortId, courseVersionId],
    queryFn: async () => {
      const res = await hpApi.getCohortActivityStatsMap(cohortId, courseVersionId);
      if (!res.success) throw new Error(res.message || 'Failed to load stats');
      return res.data;
    }
  });
}

// Dashboard aggregation hook
export function useDashboardStats() {
  const { data: enrollments } = useCourseVersionCohorts("", "", 1, 100);
  
  
  // Get stats for each cohort
  const cohortQueries = enrollments?.data?.map((enrollment: any) => {
    return useHpActivitiesStatsMap(enrollment.cohortId, enrollment.courseVersionId);
  }) || [];

  const isLoading = cohortQueries.some((query: any) => query.isLoading);
  const error = cohortQueries.find((query: any) => query.error)?.error;

  const aggregatedData = useMemo(() => {
    if (!cohortQueries.length || isLoading) return null;

    const allStats = cohortQueries.map((query: any) => {
      return query.data;
    }).filter(Boolean);

    
    if (allStats.length === 0) return null;

    const totalHp = allStats.reduce((sum, stat) => sum + (stat.currentHp || 0), 0);
    const totalStudents = allStats.reduce((sum, stat) => sum + (stat.totalStudents || 0), 0);
    
    const data = {
      overview: {
        totalCohorts: allStats.length,
        totalStudents,
        totalHpDistributed: totalHp,
        bestPerformingCohort: allStats.reduce((best, stat) => 
          (stat.currentHp || 0) > (best.currentHp || 0) ? stat : best
        ).bestPerformingCohort || 'N/A'
      },
      courses: allStats.flatMap(stat => stat.coursePerformance || []),
      hpDistribution: allStats.map(stat => ({
        cohortId: stat.bestPerformingCohort || 'Unknown',
        totalHp: stat.currentHp || 0,
        percentage: totalHp > 0 ? ((stat.currentHp || 0) / totalHp) * 100 : 0
      })),
      weeklyActivity: aggregateWeeklyData(allStats),
      engagement: {
        activeStudents: Math.floor(totalStudents * 0.7), // This can be improved with real activity data
        inactiveStudents: Math.floor(totalStudents * 0.3), // This can be improved with real activity data
        completionRate: allStats.length > 0 ? 
          (allStats.reduce((sum, stat) => {
            const completionRate = stat.totalActivities > 0 ? (stat.totalSubmissions / stat.totalActivities) * 100 : 0;
            return sum + completionRate;
          }, 0) / allStats.length) : 0
      }
    };
    
    return data;
  }, [cohortQueries, isLoading]);

  return {
    data: aggregatedData,
    isLoading,
    error: error?.message || null,
    refetch: () => cohortQueries.forEach((query: any) => query.refetch())
  };
}

// Helper function to aggregate weekly data
function aggregateWeeklyData(allStats: any[]): any[] {
  const weeklyData = [];
  const today = new Date();
  
  for (let i = 6; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);
    
    // Sum activity counts from all cohorts for this day
    const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
    const dayActivityCount = allStats.reduce((sum, stat) => {
      const dayData = stat.weeklyActivity?.find((activity: any) => 
        new Date(activity.date).toDateString() === date.toDateString()
      );
      return sum + (dayData?.studentCount || 0);
    }, 0);
    
    weeklyData.push({
      name: dayName,
      students: dayActivityCount
    });
  }
  
  return weeklyData;
}

export function useAddFeedback() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async ({ submissionId, feedback }: { submissionId: string; feedback: string }) => {
      const res = await hpApi.addFeedback(submissionId, feedback);
      if (!res.success) throw new Error(res.message || 'Failed to add feedback');
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hpStudentSubmissions'] });
      queryClient.invalidateQueries({ queryKey: ['hp-student-ledger'] });
      queryClient.invalidateQueries({ queryKey: ['hp-students'] });
      queryClient.invalidateQueries({ queryKey: ['hp-cohort-overview'] });
      toast.success('Feedback added successfully');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to add feedback');
    },
  });

  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
  };
}

export function useHpCohortOverviewStats(courseVersionId: string, cohortId: string) {
  const query = useQuery({
    queryKey: ['hp-cohort-overview', courseVersionId, cohortId],
    queryFn: async () => {
      const res = await hpApi.getCohortOverviewStats(courseVersionId, cohortId);
      if (!res.success) throw new Error(res.message || 'Failed to load cohort overview stats');
      return res.data;
    },
    enabled: !!courseVersionId && !!cohortId,
    refetchOnWindowFocus: false,
  });

  return {
    data: query.data || null,
    isLoading: query.isLoading,
    error: query.error ? (query.error as Error).message : null,
    refetch: query.refetch,
  };
}

export function useHpStudentSubmissions(studentId: string | undefined, courseVersionId: string, cohortId: string) {
  return useQuery({
    queryKey: ['hpStudentSubmissions', studentId, courseVersionId, cohortId],
    queryFn: async () => {
      if (!studentId) return [];
      const res = await hpApi.getStudentSubmissions(studentId, courseVersionId, cohortId);
      if (!res.success) throw new Error("Failed to fetch student submissions");
      return res.data;
    },
    enabled: !!studentId,
  });
}

export function useHpStudentSubmissionStats(
  studentId: string | undefined,
  cohortId: string
) {
  return useQuery({
    queryKey: ['hpStudentSubmissionStats', studentId, cohortId],
    queryFn: async () => {
      if (!studentId) return null;
      const res = await hpApi.getStudentSubmissionStats(studentId, cohortId);
      if (!res.success) throw new Error("Failed to fetch submission stats");
      return res.data;
    },
    enabled: !!studentId && !!cohortId,
  });
}

export function useStudentMySubmissions(courseVersionId: string, cohortId: string) {
  return useQuery({
    queryKey: ['studentMySubmissions', courseVersionId, cohortId],
    queryFn: async () => {
      const res = await hpApi.getStudentMySubmissions(courseVersionId, cohortId);
      if (!res.success) throw new Error("Failed to fetch current student submissions");
      return res.data;
    },
    enabled: !!courseVersionId && !!cohortId,
  });


}

export interface UserEnrollmentStats {
  totalCourses: number;
  completedCourses: number;
  totalItems: number;
  completedItems: number;
  overallProgress: number;
}

export function useUserEnrollmentStats(enabled: boolean = true): {
  data: UserEnrollmentStats | undefined;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
} {
  const result = api.useQuery(
    "get",
    "/users/enrollment-stats" as any,
    {},
    { enabled }
  );

  return {
    data: result.data as UserEnrollmentStats | undefined,
    isLoading: result.isLoading,
    error: result.error
      ? result.error.message || "Failed to fetch enrollment stats"
      : null,
    refetch: result.refetch,
  };
}

export function useResetFace(): {
  mutate: (variables: { params: { path: { userId: string, courseId: string, versionId: string } } }) => void,
  mutateAsync: (variables: { params: { path: { userId: string, courseId: string, versionId: string } } }) => Promise<any>,
  isPending: boolean,
  error: string | null,
  isSuccess: boolean,
  isError: boolean,
  status: 'idle' | 'pending' | 'success' | 'error'
} {
  const [status, setStatus] = useState<'idle' | 'pending' | 'success' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);

  const mutateAsync = useCallback(async (variables: { params: { path: { userId: string, courseId: string, versionId: string } } }) => {
    setStatus('pending');
    setError(null);
    try {
      const authToken = localStorage.getItem('firebase-auth-token');
      const { userId, courseId, versionId } = variables.params.path;
      const response = await fetch(
        `${import.meta.env.VITE_BASE_URL}/users/${userId}/enrollments/courses/${courseId}/versions/${versionId}/reset-face`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to reset face reference: ${response.status}`);
      }

      const data = await response.json();
      setStatus('success');
      return data;
    } catch (err: any) {
      setStatus('error');
      setError(err.message || 'Failed to reset face reference');
      throw err;
    }
  }, []);

  const mutate = useCallback((variables: { params: { path: { userId: string, courseId: string, versionId: string } } }) => {
    mutateAsync(variables).catch(() => {});
  }, [mutateAsync]);

  return {
    mutate,
    mutateAsync,
    isPending: status === 'pending',
    error,
    isSuccess: status === 'success',
    isError: status === 'error',
    status,
  };
}
